<template>
    <div id="contentcontainter" class="standard-mode aiproject">
        <!-- Skip link -->
        <a href="#mainContent" class="sr-only sr-only-focusable">Skip to main content</a>
        <v-snackbar v-model="notification.show" :timeout="notification.timeout" :color="notification.color" elevation="2" top multi-line>
            <v-icon left small v-if="notification.icon">{{ notification.icon }}</v-icon>
            <span v-html="notification.message"></span>
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="notification.show = false">
                    {{ notification.actionLabel || "OK" }}
                </v-btn>
            </template>
        </v-snackbar>
        <!-- Prompt-Detail als Overlay (rechtes Panel) statt Routenwechsel.
             v-dialog statt v-navigation-drawer: wird an die App-Root gehängt und liegt
             damit garantiert über Top-Nav und Scrim (kein Stacking-Context-Problem in v-main). -->
        <v-dialog v-model="promptOverlayOpen" content-class="prompt-overlay-panel" :transition="false">
            <div class="prompt-overlay-panel__body">
                <PromptOverview v-if="promptOverlayParams" :key="promptOverlayKey" embedded :overlay-params="promptOverlayParams" @close="promptOverlayOpen = false" />
            </div>
        </v-dialog>
        <!-- HEAD -->
        <ProjectSettingsDialog
            v-if="project"
            v-model="showProjectSettings"
            :project="project"
            :saving="savingProjectSettings"
            :platforms-all="platformSelectItems.filter((p) => p && p.key && p.key !== 'all')"
            :platforms-enabled="project.platforms && project.platforms.length ? project.platforms : platformSelectItems.filter((p) => p && p.key !== 'all' && !p.disabled).map((p) => p.key)"
            @save="onProjectSettingsSave"
            @platform-removed="onPlatformRemoved"
            @notify="createNotification" />
        <PauseProjectDialog v-if="project" v-model="showPauseDialog" :project="project" @changed="(paused) => $set(project, 'paused', paused)" @notify="createNotification" />
        <v-dialog v-model="showAddPromptsDialog" max-width="1100px" persistent scrollable role="dialog" aria-modal="true" aria-label="Add prompts dialog">
            <v-card>
                <v-toolbar flat color="primary" dark class="add-prompts-toolbar">
                    <v-spacer />
                    <v-btn icon @click="closeAddPrompts" aria-label="Close dialog">
                        <v-icon aria-hidden="true">mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text class="pa-0">
                    <AddPrompts :project="project" mountedOn="llm_project" @promptsAdded="onPromptsCollected" @promptsAddedDetailed="promptsAddedDetailed" @createNotification="createNotification" />
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <span v-if="project && project.paused" class="text-caption ml-2">
                        <v-icon x-small class="mr-1" aria-hidden="true">mdi-pause-circle-outline</v-icon>
                        This project is paused. New prompts are saved now, but only fetched and charged once you resume the project.
                    </span>
                    <v-spacer />
                    <v-btn text @click="closeAddPrompts">Cancel</v-btn>
                    <v-btn rounded color="primary" :disabled="!mergedNewPrompts.length || savingPrompts" :loading="savingPrompts" @click="saveNewPrompts">
                        <v-icon left small>mdi-content-save</v-icon> Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="showImportPromptTagsDialog" max-width="760px" persistent>
            <v-card class="import-tags-card">
                <v-card-title class="py-3 white--text primarycolor import-tags-header">
                    <v-avatar size="30" class="mr-3 import-tags-icon">
                        <v-icon color="white" small>mdi-file-delimited</v-icon>
                    </v-avatar>
                    <div>
                        <div class="text-subtitle-1 font-weight-medium">Import Tags from CSV</div>
                        <div class="caption">Map prompts to one or multiple tags</div>
                    </div>
                </v-card-title>
                <v-card-text class="pt-4 pb-1 px-6">
                    <v-alert dense text type="info" class="mb-1 import-tags-hint">
                        Your CSV must include the columns <strong>prompt</strong> and <strong>tag</strong> (or <strong>tags</strong>).<br />
                        You can separate multiple tags in one cell using <strong>,</strong>, <strong>;</strong> or <strong>|</strong>.
                    </v-alert>
                    <div class="d-flex align-center mb-3">
                        <v-btn small text color="primary" class="px-1" @click="downloadImportPromptTagsTemplate">
                            <v-icon left small>mdi-download</v-icon>
                            Download example CSV
                        </v-btn>
                        <span class="caption grey--text text--darken-1 ml-2">Use this template format for quick import.</span>
                    </div>

                    <v-file-input
                        v-model="importPromptTagsFile"
                        accept=".csv,text/csv"
                        outlined
                        dense
                        clearable
                        show-size
                        prepend-inner-icon="mdi-upload"
                        class="import-tags-file-input"
                        label="Choose CSV file"
                        placeholder="Drop a file here or click to browse"
                        @change="onImportPromptTagsFileChange" />

                    <div v-if="importPromptTagsRows.length && !importPromptTagsError" class="mt-2 mb-1 d-flex flex-wrap">
                        <v-chip small class="mr-2 mb-1" color="primary" text-color="white">{{ importPromptTagsRows.length }} prompts</v-chip>
                        <v-chip small class="mb-1" outlined color="primary">{{ importPromptTagsUniqueCount }} unique tags</v-chip>
                    </div>

                    <v-alert v-if="importPromptTagsError" dense outlined type="error" class="mt-3 mb-0">{{ importPromptTagsError }}</v-alert>
                    <v-alert v-else-if="importPromptTagsSummary" dense outlined type="success" class="mt-3 mb-0">{{ importPromptTagsSummary }}</v-alert>
                </v-card-text>
                <v-divider />
                <v-card-actions class="px-6 pb-4 pt-3">
                    <v-spacer />
                    <v-btn text rounded :disabled="importPromptTagsLoading" @click="closeImportPromptTagsDialog">Close</v-btn>
                    <v-btn rounded color="primary" :loading="importPromptTagsLoading" :disabled="!importPromptTagsRows.length || !!importPromptTagsError" @click="submitImportPromptTags">
                        <v-icon left small>mdi-content-save</v-icon>
                        Import tags
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <ManageBrandsDialog
            v-model="showManageBrandsDialog"
            :brands="brands"
            :project-id="project && project._id"
            :initial-edited="managedBrandsEdited"
            :initial-edited-loaded="managedBrandsEditedLoaded"
            :initial-favorites="favoriteBrands"
            :initial-colors="brandColors"
            :reset-loading="managedBrandsResetLoading"
            @reset-brands="onManagedBrandsReset"
            @update-favorites="onBrandFavoritesUpdated"
            @save="onManagedBrandsSaved" />
        <v-slide-x-transition hide-on-leave>
            <div v-if="project" id="mainContent" role="main" aria-label="AI Project content" :aria-busy="loading || savingPrompts">
                <!-- <span class="mt-10 ml-1" style="font-size: 12px; display: block">
                    <div class="pb-1">
                        <v-chip x-small color="grey lighten-2" class="mr-2 px-2" style="text-transform: capitalize">
                            <v-icon x-small class="mr-1">{{ project.device == "mobile" ? "mdi-cellphone" : "mdi-monitor" }}</v-icon>
                            {{ project.device }}
                        </v-chip>
                        <v-chip x-small color="grey lighten-2" class="mr-2 px-2" style="text-transform: uppercase">
                            <v-icon x-small class="mr-1">mdi-map-marker</v-icon>
                            {{ project.langloc.location_text }}
                        </v-chip>
                    </div>
                </span> -->
                <div class="aiproject-hero quaroshadow">
                    <div class="aiproject-hero__top">
                        <span class="aiproject-hero__brandav">
                            <img v-if="brandFaviconUrl" :src="brandFaviconUrl" :alt="brandDomain || 'brand favicon'" />
                            <v-icon v-else color="#1cedb3" size="22" aria-hidden="true">mdi-domain</v-icon>
                        </span>
                        <div class="aiproject-hero__title-wrap">
                            <div class="aiproject-hero__row1">
                                <h1 class="aiproject-hero__title">{{ project.title }}</h1>
                                <span class="aiproject-hero__badge">
                                    <span class="aiproject-hero__badge-dot" aria-hidden="true"></span>
                                    AI Visibility
                                </span>
                                <span v-if="project.paused" class="aiproject-hero__badge aiproject-hero__badge--paused">
                                    <v-icon x-small class="mr-1" aria-hidden="true">mdi-pause</v-icon>
                                    Paused
                                </span>
                            </div>
                            <div class="aiproject-hero__meta">
                                <span>{{ brands.length }} {{ brands.length === 1 ? "brand" : "brands" }} tracked</span>
                                <span class="aiproject-hero__meta-dot" aria-hidden="true"></span>
                                <span>{{ (project.platforms ? project.platforms.length : 0) + (project.google ? 1 : 0) }} AI platforms</span>
                                <span class="aiproject-hero__meta-dot" aria-hidden="true"></span>
                                <span class="aiproject-hero__meta-chip" style="text-transform: capitalize">
                                    <v-icon x-small color="#9cc7bf" class="mr-1" aria-hidden="true">mdi-update</v-icon>{{ projectIntervalText }}
                                </span>
                                <template v-if="project.langloc && project.langloc.location_text">
                                    <span class="aiproject-hero__meta-dot" aria-hidden="true"></span>
                                    <span class="aiproject-hero__meta-chip">
                                        <v-icon x-small color="#9cc7bf" class="mr-1" aria-hidden="true">mdi-map-marker</v-icon>{{ project.langloc.location_text }}
                                    </span>
                                </template>
                                <template v-if="project.device">
                                    <span class="aiproject-hero__meta-dot" aria-hidden="true"></span>
                                    <span class="aiproject-hero__meta-chip" style="text-transform: capitalize">
                                        <v-icon x-small color="#9cc7bf" class="mr-1" aria-hidden="true">{{ project.device == "mobile" ? "mdi-cellphone" : "mdi-monitor" }}</v-icon
                                        >{{ project.device }}
                                    </span>
                                </template>
                            </div>
                        </div>
                        <div class="aiproject-hero__actions">
                            <v-btn class="aiproject-hero__btn aiproject-hero__btn--ghost" rounded small depressed @click="openImportPromptTagsDialog" aria-label="Import tags">
                                <v-icon left small aria-hidden="true">mdi-tag-plus</v-icon>
                                Import Tags
                            </v-btn>
                            <v-btn class="aiproject-hero__btn aiproject-hero__btn--ghost" rounded small depressed :to="`/ai-suite/project/${$route.params.id}/reports`" aria-label="Report Builder">
                                <v-icon left small aria-hidden="true">mdi-file-chart-outline</v-icon>
                                Report Builder
                            </v-btn>
                            <v-btn class="aiproject-hero__btn aiproject-hero__btn--ghost" rounded small depressed @click="openManageBrandsDialog" aria-label="Manage brands">
                                <v-icon left small aria-hidden="true">mdi-shape-outline</v-icon>
                                Manage Brands
                            </v-btn>
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        v-bind="attrs"
                                        v-on="on"
                                        class="aiproject-hero__btn aiproject-hero__btn--icon"
                                        icon
                                        :disabled="test_set"
                                        @click="showProjectSettings = true"
                                        aria-label="Project settings">
                                        <v-icon small aria-hidden="true">mdi-cog</v-icon>
                                    </v-btn>
                                </template>
                                <span>Project Settings</span>
                            </v-tooltip>
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        v-bind="attrs"
                                        v-on="on"
                                        class="aiproject-hero__btn aiproject-hero__btn--icon"
                                        icon
                                        :disabled="test_set || !!project.demo_project || initialImportRunning"
                                        @click="showPauseDialog = true"
                                        :aria-label="project.paused ? 'Resume project' : 'Pause project'">
                                        <v-icon small aria-hidden="true">{{ project.paused ? "mdi-play" : "mdi-pause" }}</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ initialImportRunning ? "Available once the import has finished" : project.paused ? "Resume Project" : "Pause Project" }}</span>
                            </v-tooltip>
                            <v-btn class="aiproject-hero__btn aiproject-hero__btn--primary" rounded small depressed @click="openAddPrompts" aria-label="Add prompts">
                                <v-icon left small aria-hidden="true">mdi-plus</v-icon>
                                Add Prompts
                            </v-btn>
                        </div>
                    </div>
                    <div class="aiproject-hero__bar">
                        <div class="aiproject-hero__ctrl">
                            <span class="aiproject-hero__ctrl-label">Platform</span>
                            <v-select
                                rounded
                                id="plattformselect"
                                hide-details
                                high-contrast
                                v-model="selectedPlatform"
                                :items="platformSelectItems"
                                item-text="name"
                                item-disabled="disabled"
                                label="Platform"
                                dense
                                base-color="red"
                                color="primary"
                                return-object
                                outlined
                                class="project-filter-control"
                                style="max-width: 230px"
                                aria-label="Select platform">
                                <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-on="on" v-bind="attrs" :disabled="item.disabled" style="cursor: pointer">
                                        <!-- NEU: Logo-Gruppe für 'all' -->
                                        <span v-if="item.key === 'all' && platformLogoPreview.length" class="all-platforms-logos">
                                            <span v-for="(logo, idx) in platformLogoPreview" :key="idx" v-html="logo" class="apl"> </span>
                                        </span>
                                        <!-- Einzel-Logo für normale Items -->
                                        <span
                                            v-else-if="getPlatformInfo(item.key).logo"
                                            v-html="getPlatformInfo(item.key).logo"
                                            style="height: 22px; width: 22px; margin-right: 10px; display: inline-block; vertical-align: middle" />
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                {{ item.name }}
                                                <v-chip
                                                    v-if="item.key !== 'all' && (item.status || getPlatformInfo(item.key).status) && (item.status || getPlatformInfo(item.key).status) != 'included'"
                                                    x-small
                                                    class="ml-2"
                                                    :color="item.unavailable ? 'warning' : 'grey lighten-2'"
                                                    :outlined="!!item.unavailable">
                                                    {{ item.status || getPlatformInfo(item.key).status }}
                                                </v-chip>
                                            </v-list-item-title>
                                            <v-list-item-subtitle v-if="item.unavailable">It will be re-enabled automatically once the issue is resolved.</v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                </template>
                                <template v-slot:selection="{ item }">
                                    <v-chip v-if="item" small>
                                        <span
                                            v-if="item.key !== 'all' && getPlatformInfo(item.key).logo"
                                            v-html="getPlatformInfo(item.key).logo"
                                            style="height: 18px; width: 18px; border-radius: 3px; margin-right: 6px; display: inline-block; vertical-align: middle" />
                                        {{ item.name }}
                                    </v-chip>
                                </template>
                            </v-select>
                            <v-tooltip v-if="unavailablePlatformNames.length" bottom max-width="320">
                                <template v-slot:activator="{ on, attrs }">
                                    <span class="aiproject-hero__ctrl-hint warning--text" v-on="on" v-bind="attrs">
                                        <v-icon x-small color="warning">mdi-alert-circle-outline</v-icon>
                                        {{ unavailableHint }}
                                    </span>
                                </template>
                                <span>{{ unavailableTooltip }}</span>
                            </v-tooltip>
                        </div>
                        <div class="aiproject-hero__ctrl">
                            <span class="aiproject-hero__ctrl-label">Date Range</span>
                            <v-menu
                                outlined
                                dense
                                hide-details
                                style="border-radius: 25px !important; height: 50px; min-width: 320px; background: transparent !important"
                                v-model="datepickerMenu"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="320px"
                                aria-label="Select date range">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        dense
                                        outlined
                                        hide-details
                                        class="project-filter-control"
                                        label="Date Range"
                                        style="max-width: 320px; width: 320px; background-color: transparent !important"
                                        v-model="selectedDatesText"
                                        rounded
                                        filled
                                        prepend-inner-icon="mdi-calendar"
                                        readonly
                                        aria-haspopup="dialog"
                                        aria-controls="date-picker"
                                        aria-label="Select date range"
                                        v-bind="attrs"
                                        v-on="on" />
                                </template>
                                <div class="date-range-picker-wrapper">
                                    <div class="date-range-presets" role="group" aria-label="Quick date ranges">
                                        <div class="date-range-presets__title">Quick ranges</div>
                                        <button
                                            v-for="preset in dateRangePresets"
                                            :key="preset.key"
                                            type="button"
                                            class="date-range-preset-btn"
                                            :class="{ 'is-active': activeDateRangePresetKey === preset.key }"
                                            @click="applyDateRangePreset(preset)">
                                            <span class="date-range-preset-btn__label">{{ preset.label }}</span>
                                            <v-icon v-if="activeDateRangePresetKey === preset.key" small class="date-range-preset-btn__icon">mdi-check</v-icon>
                                        </button>
                                    </div>
                                    <v-date-picker
                                        id="date-picker"
                                        @input="
                                            selectedDates.sort()
                                            dateRangeTouched = true
                                            fetchBrandMetrics(project.brand)
                                            buildMetricsMatrix()
                                        "
                                        color="primary"
                                        :allowed-dates="allowedDates"
                                        show-current="false"
                                        range
                                        v-model="selectedDates"
                                        no-title
                                        aria-label="Pick start and end date" />
                                </div>
                            </v-menu>
                        </div>
                        <div class="aiproject-hero__ctrl aiproject-hero__ctrl--grow">
                            <span class="aiproject-hero__ctrl-label">Tags</span>
                            <v-autocomplete
                                id="tagfilter"
                                clearable
                                v-model="selectedTags"
                                :items="tagFilterItems"
                                item-text="label"
                                item-value="id"
                                :menu-props="{ contentClass: 'tag-filter-menu-content' }"
                                :search-input.sync="tagSearch"
                                :filter="filterTags"
                                hide-selected
                                offset-y
                                multiple
                                clear-icon="mdi-close"
                                chips
                                small-chips
                                label="Tags"
                                hide-details
                                deletable-chips
                                outlined
                                class="project-filter-control"
                                rounded
                                dense
                                style="max-width: 400px; background-color: transparent !important"
                                aria-label="Filter by tags">
                                <template v-slot:selection="{ item, selected }">
                                    <v-chip
                                        small
                                        outlined
                                        class="mr-1 tag-chip"
                                        :input-value="selected"
                                        close
                                        close-icon="mdi-close"
                                        @mousedown.stop
                                        @click.stop
                                        @click:close="removeTag(item)"
                                        :aria-label="`Remove tag: ${item.label}`">
                                        <span class="tag-dot" :style="tagDotStyle(item.id)" aria-hidden="true"></span>
                                        {{ item.label }}
                                    </v-chip>
                                </template>
                                <template v-slot:prepend-inner>
                                    <v-menu v-model="tagSettingsMenu" offset-y left :close-on-content-click="false" content-class="tag-filter-menu-content">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-tooltip top>
                                                <template v-slot:activator="{ on: onTip, attrs: atTip }">
                                                    <v-btn
                                                        icon
                                                        small
                                                        class="mr-1 mb-1 tag-settings-btn"
                                                        v-bind="Object.assign({}, attrs, atTip)"
                                                        v-on="Object.assign({}, on, onTip)"
                                                        @mousedown.stop.prevent
                                                        @click.stop.prevent
                                                        aria-label="Configure tag filter logic">
                                                        <v-icon color="primary darken-1" small>mdi-cog</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Configure tag filter logic</span>
                                            </v-tooltip>
                                        </template>

                                        <v-card class="tag-settings-popover" outlined elevation="2">
                                            <v-card-title class="py-2 px-3">
                                                <v-icon small class="mr-2">mdi-filter-variant</v-icon>
                                                Tag filter
                                            </v-card-title>
                                            <v-card-subtitle class="py-0 px-3"> Choose how multiple tags are combined </v-card-subtitle>
                                            <v-divider class="my-2" />

                                            <div class="px-3 pb-2">
                                                <div class="mb-2 overline">Combine with</div>
                                                <v-btn-toggle v-model="tagOperator" dense mandatory class="tag-op-toggle">
                                                    <v-btn rounded small value="or" :class="{ 'is-active': tagOperator === 'or' }" text> <v-icon left small>mdi-gate-or</v-icon> OR </v-btn>
                                                    <v-btn rounded small value="and" class="ml-2" :class="{ 'is-active': tagOperator === 'and' }" text>
                                                        <v-icon left small>mdi-gate-and</v-icon> AND
                                                    </v-btn>
                                                </v-btn-toggle>

                                                <div class="caption mt-3 grey--text text--darken-1">OR: Matches items with at least one selected tag. AND: Items must contain all selected tags.</div>
                                            </div>
                                        </v-card>
                                    </v-menu>
                                </template>
                                <!-- Dropdown-Items mit Dot -->
                                <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-on="on" v-bind="attrs">
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                <span class="tag-color-dot" :style="tagDotStyle(item.id)"></span>
                                                {{ item.label }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </template>
                                <template v-slot:no-data>
                                    <v-list-item>
                                        <v-list-item-content>
                                            <v-list-item-title class="grey--text text--darken-1">
                                                <v-chip v-if="tagFilterRestricted">no further tags with data</v-chip>
                                                <v-chip v-else>no tags yet</v-chip>
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>
                        </div>
                        <div class="aiproject-hero__ctrl">
                            <span class="aiproject-hero__ctrl-label">Mode</span>
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <div v-bind="attrs" v-on="on" class="dashboard-mode-toggle ml-2" role="group" aria-label="Value mode">
                                        <v-btn-toggle v-model="dashboardMode" mandatory dense rounded class="dashboard-mode-toggle__group" :disabled="dashboardModeLoading">
                                            <v-btn value="absolute" small class="dashboard-mode-toggle__btn" :class="{ 'is-active': dashboardMode === 'absolute' }">
                                                <v-progress-circular v-if="dashboardModeLoading && dashboardMode === 'absolute'" indeterminate size="12" width="2" class="mr-1" />
                                                <v-icon v-else left x-small>mdi-calendar-today</v-icon>
                                                Absolute
                                            </v-btn>
                                            <v-btn value="average" small class="dashboard-mode-toggle__btn" :class="{ 'is-active': dashboardMode === 'average' }">
                                                <v-progress-circular v-if="dashboardModeLoading && dashboardMode === 'average'" indeterminate size="12" width="2" class="mr-1" />
                                                <v-icon v-else left x-small>mdi-chart-bell-curve</v-icon>
                                                Average
                                            </v-btn>
                                        </v-btn-toggle>
                                    </div>
                                </template>
                                <span>
                                    <strong>Absolute</strong>: Values from the last day with change compared to the first day of the period.<br />
                                    <strong>Average</strong>: Daily average over the period compared to the preceding period of equal length.
                                </span>
                            </v-tooltip>
                        </div>
                        <div class="aiproject-hero__ctrl" v-if="project && brands.length">
                            <span class="aiproject-hero__ctrl-label">Brands</span>
                            <v-tooltip top max-width="280">
                                <template v-slot:activator="{ on, attrs }">
                                    <div v-bind="attrs" v-on="on" class="dashboard-mode-toggle ml-2" role="group" aria-label="Brand display mode">
                                        <v-btn-toggle v-model="brandDisplayMode" mandatory dense rounded class="dashboard-mode-toggle__group">
                                            <v-btn value="all" small class="dashboard-mode-toggle__btn" :class="{ 'is-active': brandDisplayMode === 'all' }">
                                                <v-icon left x-small>mdi-view-grid-outline</v-icon>
                                                All
                                            </v-btn>
                                            <v-btn value="favorites" small class="dashboard-mode-toggle__btn" :class="{ 'is-active': brandDisplayMode === 'favorites' }">
                                                <v-icon left x-small>mdi-star</v-icon>
                                                Favorites
                                                <span v-if="favoriteBrands.length" class="ml-1 caption">({{ favoriteBrands.length }})</span>
                                            </v-btn>
                                        </v-btn-toggle>
                                    </div>
                                </template>
                                <span>
                                    <strong>All</strong>: Show all brands in selector and charts.<br />
                                    <strong>Favorites</strong>: Only your favorites. Manage favorites and chart colors in "Manage Brands".
                                </span>
                            </v-tooltip>
                        </div>
                    </div>
                    <div class="aiproject-hero__tabs-wrap" role="navigation" aria-label="Project sections">
                        <v-tabs class="project-section-tabs aiproject-hero__tabs" background-color="transparent" hide-slider show-arrows>
                            <v-tab
                                v-for="tab in projectSectionTabs"
                                :key="tab.key"
                                :disabled="Boolean(tab.disabled)"
                                :class="{ 'active-button2': currentProjectSection === tab.key, 'is-disabled-tab': Boolean(tab.disabled) }"
                                @click="goToProjectSection(tab.key)">
                                <span class="project-section-tabs__label">{{ tab.label }}</span>
                                <v-chip v-if="tab.comingSoon" x-small outlined class="ml-2">Coming soon</v-chip>
                            </v-tab>
                        </v-tabs>
                    </div>
                </div>
            </div>
        </v-slide-x-transition>
        <v-slide-x-transition hide-on-leave>
            <div v-if="isDashboardSection && project && !loading && !brands.length && selectedTags.length" class="tag-empty-state-wrap mt-4">
                <v-card class="tag-empty-state quaroshadow" outlined>
                    <div class="tag-empty-state__icon-wrap">
                        <v-icon class="tag-empty-state__icon" color="primary">mdi-tag-off-outline</v-icon>
                    </div>
                    <h3 class="tag-empty-state__title">No data for current tag filter</h3>
                    <p class="tag-empty-state__text">For the selected date range and platform, no results were found with the current tags.</p>

                    <div class="tag-empty-state__chips">
                        <v-chip v-for="tag in selectedTags" :key="`empty-tag-${tag}`" small outlined class="mr-1 mb-1">
                            {{ tag }}
                        </v-chip>
                    </div>

                    <div class="tag-empty-state__actions">
                        <v-btn rounded color="primary" @click="resetSelectedTagsNoData">
                            <v-icon left small>mdi-filter-remove-outline</v-icon>
                            Reset tags
                        </v-btn>
                    </div>
                </v-card>
            </div>
        </v-slide-x-transition>
        <v-slide-x-transition hide-on-leave>
            <div v-if="isDashboardSection && !loading" class="mt-5 brand-metrics-cards-wrapper" :class="{ 'is-mode-loading': dashboardModeLoading }">
                <div v-if="dashboardModeLoading" class="dashboard-mode-loading-overlay">
                    <v-progress-circular indeterminate color="primary" size="28" width="3" />
                    <span class="dashboard-mode-loading-overlay__text">Updating {{ dashboardMode }} values…</span>
                </div>
                <v-row dense>
                    <v-col v-for="card in brandMetricCards" :key="card.key" cols="12" sm="6" md="6" lg="3">
                        <v-card
                            class="platform-card brand-metric-card quaroshadow"
                            outlined
                            :class="{
                                'platform-card--up': card.delta > 0,
                                'platform-card--down': card.delta < 0
                            }">
                            <div class="pc-top-row">
                                <div class="pc-head-left">
                                    <div class="pc-favicon" aria-hidden="true">
                                        <img v-if="brandFaviconUrl" :src="brandFaviconUrl" :alt="(project && project.brand && project.brand.brand_name) || 'brand favicon'" />
                                        <span v-else>{{ (((project && project.brand && project.brand.brand_name) || card.label || "B") + "").substring(0, 1).toUpperCase() }}</span>
                                    </div>
                                    <h2 class="pc-title">{{ card.label }}</h2>
                                    <v-tooltip top max-width="380" color="primary" :disabled="!metricCardTooltip(card)">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon v-bind="attrs" v-on="on" x-small class="ml-1" color="grey" style="vertical-align: middle">mdi-help-circle-outline</v-icon>
                                        </template>
                                        <div class="metric-tooltip-content" v-html="metricCardTooltip(card)"></div>
                                    </v-tooltip>
                                </div>
                                <div class="pc-change" v-if="card.deltaPercent != null" :class="{ up: card.deltaPercent > 0, down: card.deltaPercent < 0 }">
                                    <v-icon x-small>
                                        {{ card.deltaPercent > 0 ? "mdi-arrow-up" : card.deltaPercent < 0 ? "mdi-arrow-down" : "mdi-minus" }}
                                    </v-icon>
                                    {{ formatDeltaPercent(card) }}
                                </div>
                                <div class="pc-change" v-else-if="card.delta != null" :class="{ up: card.delta > 0, down: card.delta < 0 }">
                                    <v-icon x-small>
                                        {{ card.delta > 0 ? "mdi-arrow-up" : card.delta < 0 ? "mdi-arrow-down" : "mdi-minus" }}
                                    </v-icon>
                                    {{ formatDeltaValue(card) }}
                                </div>
                                <div class="pc-change pc-change--na" v-else><v-icon x-small>mdi-minus</v-icon>—</div>
                            </div>

                            <div class="pc-main-row">
                                <div class="pc-value-wrap">
                                    <div class="pc-value"><span v-if="card.key === 'relevance_rank'">#</span>{{ formatMetricCardValue(card) }}</div>
                                    <div class="pc-subtitle">{{ metricCardSubtitle(card) }}</div>
                                </div>
                            </div>

                            <div class="pc-trend" :class="{ up: card.delta > 0, down: card.delta < 0 }">
                                <svg viewBox="0 0 160 34" preserveAspectRatio="none" aria-hidden="true">
                                    <path class="pc-trend-line" :d="metricTrendPath(card)"></path>
                                </svg>
                            </div>
                        </v-card>
                    </v-col>
                </v-row></div
        ></v-slide-x-transition>
        <!-- CHART -->
        <v-row v-if="isDashboardSection">
            <v-col cols="12" md="6" lg="8">
                <v-slide-x-transition hide-on-leave>
                    <div class="mt-5" v-if="project && brands.length">
                        <v-card class="rounded_card quaroshadow" style="border-top: 4px solid var(--v-primary-base); overflow: hidden">
                            <!-- Chart / Brand Line Toolbar -->
                            <v-toolbar id="projects-tool-bar" flat class="gradient-toolbar elevation-0">
                                <v-toolbar-items>
                                    <v-btn
                                        v-for="btn in chartBarButtons"
                                        :key="btn.name"
                                        :class="{ 'active-button2': activeChartButton === btn.name }"
                                        text
                                        :color="activeChartButton === btn.name ? 'primary darken-1' : 'grey darken-4'"
                                        @click="activeChartButton = btn.name">
                                        <h2 style="font-size: 0.9rem; font-weight: 100">{{ btn.label }}</h2>
                                        <v-tooltip top max-width="350" color="primary" :disabled="!btn.tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on" small class="ml-1" color="grey" style="vertical-align: middle">mdi-help-circle-outline</v-icon>
                                            </template>
                                            <span v-html="btn.tooltip"></span>
                                        </v-tooltip>
                                    </v-btn>
                                </v-toolbar-items>
                            </v-toolbar>
                            <v-card-text class="pt-0 pb-1">
                                <div class="brand-select-center mt-4 mb-1">
                                    <v-autocomplete
                                        id="ai_brand_selection"
                                        append-icon="mdi-chevron-down"
                                        class="brand-multi compact"
                                        v-model="selectedBrands"
                                        :items="brandSelectorOptions"
                                        rounded
                                        label="Select brands"
                                        :search-input.sync="brandSearchInput"
                                        outlined
                                        dense
                                        multiple
                                        persistent-hint
                                        @input="brandSearchInput = ''"
                                        hint="Type to search..."
                                        item-text="canonicalName"
                                        hide-details
                                        return-object>
                                        <template v-slot:append-inner>
                                            <!-- Beispiel: inner MDI Icon -->
                                            <v-icon small class="mr-2 brand-select-chevron">mdi-menu-down</v-icon>
                                            <!-- Alternativ eigenes SVG:
          <span class="brand-select-chevron">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
          </span>
          -->
                                        </template>
                                        <!-- Auswahl-Chips -->
                                        <template v-slot:selection="{ item, index }">
                                            <span class="">
                                                <span v-if="index < 10">
                                                    <v-chip small class="pl-2 pr-1" outlined>
                                                        <span class="brand-color-dot" :title="item.canonicalName" :style="brandColorDotStyle(item.canonicalName)"></span>
                                                        {{ item.canonicalName }}
                                                        <v-btn class="ml-2" style="margin-top: 2px" x-small icon @click="selectedBrands.splice(index, 1)">
                                                            <v-icon x-small>mdi-close</v-icon>
                                                        </v-btn>
                                                    </v-chip>
                                                </span>
                                            </span>
                                            <span v-if="index === 10" class="grey--text caption pt-2"> (+{{ selectedBrands.length - 10 }} weitere) </span>
                                        </template>
                                        <!-- Dropdown-Items -->
                                        <template v-slot:item="data">
                                            <v-row no-gutters style="border-bottom: 1px solid #eaeaea; border-radius: 10px; flex-wrap: nowrap !important">
                                                <v-col class="px-0 flex" style="max-width: 90px">
                                                    <v-icon class="mx-auto" v-if="data.attrs['aria-selected'] == 'true'" color="">mdi-check-circle</v-icon>
                                                </v-col>
                                                <v-col class="pl-0 flex">
                                                    <v-list-item two-line>
                                                        <v-list-item-content>
                                                            <v-list-item-title>
                                                                <span class="brand-color-dot" :style="brandColorDotStyle(data.item.canonicalName)"></span>
                                                                {{ data.item.canonicalName }}
                                                            </v-list-item-title>
                                                            <!-- <v-list-item-subtitle>
                                  <v-chip small>Mentions: {{ data.item.mentions ? data.item.mentions.join(", ") : "" }}</v-chip>
                              </v-list-item-subtitle> -->
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                </v-col>
                                            </v-row>
                                        </template>
                                    </v-autocomplete>
                                </div>
                                <BrandLineChart
                                    :selectedBrands="selectedBrands"
                                    :mode="activeChartButton"
                                    :tags="selectedTags"
                                    :brandColors="brandColors"
                                    :selectedDates="selectedDbDates"
                                    :availableDates="availableDates"
                                    :dateRangeTouched="dateRangeTouched"
                                    :tagsOperator="tagOperator"
                                    :platformsCount="this.project.platforms.length + (this.project.google ? 1 : 0)"
                                    :platform="selectedPlatform && selectedPlatform.key === 'all' ? null : selectedPlatform.key"
                                    :googleDate="project.latest_date" />
                            </v-card-text>
                        </v-card>
                    </div>
                </v-slide-x-transition>
            </v-col>
            <v-col cols="12" md="6" lg="4">
                <!-- CHART -->
                <v-slide-x-transition hide-on-leave>
                    <div class="mt-5" v-if="project && brands.length">
                        <v-card class="rounded_card quaroshadow" style="border-top: 4px solid var(--v-primary-base); overflow: hidden">
                            <!-- Top Brands / Sources Mini Toolbar -->
                            <v-toolbar id="projects-tool-bar" flat class="gradient-toolbar elevation-0">
                                <v-toolbar-items>
                                    <v-btn
                                        :class="{ 'active-button2': topMiniActive === 'brands' }"
                                        text
                                        :color="topMiniActive === 'brands' ? 'primary darken-2' : 'grey darken-4'"
                                        @click="topMiniActive = 'brands'">
                                        <h2 style="font-size: 0.9rem; font-weight: 100">Top Brands</h2>
                                        <v-tooltip top max-width="320" color="primary">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on" small class="ml-1" color="grey" style="vertical-align: middle"> mdi-help-circle-outline </v-icon>
                                            </template>
                                            <span>Shows the most mentioned brands for the selected dates and filters.</span>
                                        </v-tooltip>
                                    </v-btn>

                                    <v-btn
                                        :class="{ 'active-button2': topMiniActive === 'sources' }"
                                        text
                                        :color="topMiniActive === 'sources' ? 'primary darken-2' : 'grey darken-4'"
                                        @click="topMiniActive = 'sources'">
                                        <h2 style="font-size: 0.9rem; font-weight: 100">Top Sources</h2>
                                        <v-tooltip top max-width="320" color="primary">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on" small class="ml-1" color="grey" style="vertical-align: middle"> mdi-help-circle-outline </v-icon>
                                            </template>
                                            <span
                                                >Shows in how many responses the domain was used as a reference source. The domain is not part of the body text; it was extracted by the LLM to generate
                                                the text.</span
                                            >
                                        </v-tooltip>
                                    </v-btn>
                                </v-toolbar-items>
                                <div class="flex-grow-1"></div>
                                <span v-if="brandMetricsRangeLabel" class="data-basis-pill mr-4" :title="`Data basis: ${brandMetricsRangeLabel}`">
                                    <v-icon x-small class="data-basis-pill__icon" aria-hidden="true">mdi-calendar-range</v-icon>
                                    <span class="data-basis-pill__text">{{ brandMetricsRangeLabel }}</span>
                                </span>
                            </v-toolbar>
                            <v-card-text style="max-height: 439px" class="px-0">
                                <BrandsTable
                                    v-show="topMiniActive === 'brands'"
                                    :brands="dashboardTopBrands"
                                    :loading="loadingBrands"
                                    :totalPrompts="totalPromptsCount"
                                    :brandColors="brandColors"
                                    minimal
                                    :mode="activeChartButton"
                                    :project="project" />
                                <SourcesTable
                                    v-show="topMiniActive === 'sources'"
                                    :isActive="topMiniActive === 'sources'"
                                    :project_id="project._id"
                                    :selectedDates="selectedDbDates"
                                    :search="search"
                                    :project="project"
                                    :selectedPlatform="selectedPlatform"
                                    :tagsOperator="tagOperator"
                                    :tags="selectedTags"
                                    minimal
                                    :importDatesByPlatform="sourcesImportDatesByPlatform" />
                            </v-card-text>
                        </v-card>
                    </div>
                </v-slide-x-transition>
            </v-col>
        </v-row>
        <v-slide-x-transition hide-on-leave>
            <div v-if="isDashboardSection && project && brands.length && selectedPlatform && selectedPlatform.key == 'all'" class="mt-5 brand-metrics-cards-wrapper">
                <v-slide-y-transition hide-on-leave>
                    <div class="mt-4 mb-7 metrics-matrix-wrapper">
                        <v-card class="platform-card rounded_card metrics-matrix-card quaroshadow pa-0" outlined style="border-top: 4px solid var(--v-primary-base) !important">
                            <v-toolbar id="projects-tool-bar" flat class="gradient-toolbar elevation-0" aria-label="Brand performance matrix toolbar">
                                <v-toolbar-items>
                                    <v-btn
                                        text
                                        :class="{ 'active-button2': matrixViewMode === 'brand' }"
                                        :color="matrixViewMode === 'brand' ? 'primary' : 'grey darken-4'"
                                        @click="matrixViewMode = 'brand'">
                                        <h2 style="font-size: 0.9rem; font-weight: 100">Brand Performance</h2>
                                        <v-tooltip top max-width="350" color="primary">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on" small class="ml-1" color="grey" style="vertical-align: middle">mdi-help-circle-outline</v-icon>
                                            </template>
                                            <div style="font-size: 12px; line-height: 1.25">
                                                <b>Brand Performance</b>
                                                <ul style="margin: 6px 0 0 16px; padding: 0">
                                                    <li><b>Visibility (Vis)</b>: Percentage of AI responses that mention the brand on the selected day and platform. Higher = more visible.</li>
                                                    <li><b>Brand Relevance Rank (Rank)</b>: Position based on LLM salience among all brands for the same day/platform. 1 = highest relevance.</li>
                                                    <li><b>Sentiment (Sent)</b>: Average sentiment score of brand mentions (-1 to +1).</li>
                                                    <li>
                                                        <b>Sources</b>: Number of responses that include at least one source domain matching the brand (e.g., the official website). Domains are
                                                        normalized.
                                                    </li>
                                                </ul>
                                            </div>
                                        </v-tooltip>
                                    </v-btn>
                                    <v-btn
                                        text
                                        class="ml-2"
                                        :class="{ 'active-button2': matrixViewMode === 'tag' }"
                                        :color="matrixViewMode === 'tag' ? 'primary' : 'grey darken-4'"
                                        @click="onTagPerformanceTabClick">
                                        <h2 style="font-size: 0.9rem; font-weight: 100">Tag Performance</h2>
                                        <v-tooltip top max-width="360" color="primary">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on" small class="ml-1" color="grey" style="vertical-align: middle">mdi-help-circle-outline</v-icon>
                                            </template>
                                            <div style="font-size: 12px; line-height: 1.25">
                                                <b>Tag Performance</b>
                                                <ul style="margin: 6px 0 0 16px; padding: 0">
                                                    <li><b>Columns</b>: tags from the current filter context.</li>
                                                    <li><b>Rows</b>: active platforms.</li>
                                                    <li><b>Values</b>: visibility, relevance rank, source references, and sentiment for the selected brand.</li>
                                                </ul>
                                            </div>
                                        </v-tooltip>
                                    </v-btn>
                                </v-toolbar-items>
                                <div class="flex-grow-1"></div>
                                <span v-if="brandMetricsRangeLabel" class="data-basis-pill mr-4" :title="`Data basis: ${brandMetricsRangeLabel}`">
                                    <v-icon x-small class="data-basis-pill__icon" aria-hidden="true">mdi-calendar-range</v-icon>
                                    <span class="data-basis-pill__text">{{ brandMetricsRangeLabel }}</span>
                                </span>
                                <v-btn rounded color="primary" filled small class="mr-4" @click="exportPerformanceMatrix" aria-label="Export performance matrix">
                                    <v-icon left small aria-hidden="true">mdi-cloud-download</v-icon>
                                    Export
                                </v-btn>
                            </v-toolbar>
                            <!-- Ladeanzeige als Live-Status -->
                            <v-progress-linear
                                v-if="matrixViewMode === 'brand' ? !matrixLoaded : !tagMatrixLoaded"
                                indeterminate
                                role="status"
                                aria-label="Loading performance metrics"></v-progress-linear>

                            <div v-if="matrixViewMode === 'tag' && !matrixTagOrder.length" class="px-5 py-5">
                                <div class="tp-empty-state" role="status" aria-live="polite">
                                    <div class="tp-empty-state__icon-wrap">
                                        <v-icon class="tp-empty-state__icon" color="primary">mdi-tag-off-outline</v-icon>
                                    </div>
                                    <h3 class="tp-empty-state__title">No tags available yet</h3>
                                    <p class="tp-empty-state__text">There are currently no tags in this project. Add or import tags to view Tag Performance.</p>
                                    <div class="tp-empty-state__actions">
                                        <v-btn small rounded outlined color="primary" @click="openImportPromptTagsDialog">
                                            <v-icon left small>mdi-tag-plus</v-icon>
                                            Import Tags
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                            <v-row class="tp-controls-row mt-2" no-gutters v-if="matrixViewMode === 'tag' && matrixTagOrder.length">
                                <v-col cols="12" sm="6" md="6" lg="3" xl="2" class="pa-2">
                                    <v-select
                                        v-model="tagPerformanceBrandKeys"
                                        :items="tagPerformanceBrandOptions"
                                        item-text="name"
                                        item-value="key"
                                        multiple
                                        dense
                                        rounded
                                        outlined
                                        hide-details
                                        label="Brands"
                                        class="tp-control tp-brand-select"
                                        @change="onTagPerformanceControlsChange">
                                        <template v-slot:selection="{ item, index }">
                                            <v-chip v-if="index < 2" x-small outlined class="mr-1 tag-chip">{{ item.name }}</v-chip>
                                            <span v-if="index === 2" class="grey--text caption">+{{ tagPerformanceBrandKeys.length - 2 }}</span>
                                        </template>
                                    </v-select>
                                </v-col>

                                <v-col cols="12" sm="6" md="6" lg="2" xl="2" class="pa-2">
                                    <v-select
                                        v-model="tagPerformancePlatforms"
                                        :items="tagPerformancePlatformOptions"
                                        item-text="name"
                                        item-value="key"
                                        multiple
                                        dense
                                        rounded
                                        outlined
                                        hide-details
                                        label="Platforms"
                                        class="tp-control tp-platform-select"
                                        @change="onTagPerformanceControlsChange">
                                        <template v-slot:selection="{ item, index }">
                                            <span v-if="index < 6" class="mr-1 d-inline-flex align-center" :title="item.name">
                                                <span
                                                    class="mm-platform-logo mm-platform-logo--head"
                                                    v-if="getPlatformInfo(item.key).logo"
                                                    v-html="getPlatformInfo(item.key).logo"
                                                    style="width: 16px; height: 16px"></span>
                                                <v-icon v-else x-small color="grey">mdi-circle</v-icon>
                                            </span>
                                            <span v-if="index === 6" class="grey--text caption">+{{ tagPerformancePlatforms.length - 6 }}</span>
                                        </template>
                                        <template v-slot:item="{ item, on, attrs }">
                                            <v-list-item v-on="on" v-bind="attrs">
                                                <v-list-item-icon class="mr-2">
                                                    <span class="mm-platform-logo" v-if="getPlatformInfo(item.key).logo" v-html="getPlatformInfo(item.key).logo"></span>
                                                    <v-icon v-else x-small color="grey">mdi-circle</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-content>
                                                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </template>
                                    </v-select>
                                </v-col>

                                <v-col cols="12" md="8" lg="5" xl="6" class="pa-2">
                                    <v-autocomplete
                                        v-model="tagPerformanceSelectedTags"
                                        :items="availableTags"
                                        item-text="label"
                                        item-value="id"
                                        :menu-props="{ contentClass: 'tag-filter-menu-content' }"
                                        :filter="filterTags"
                                        hide-selected
                                        cache-items
                                        offset-y
                                        multiple
                                        clear-icon="mdi-close"
                                        chips
                                        rounded
                                        small-chips
                                        dense
                                        outlined
                                        clearable
                                        hide-details
                                        deletable-chips
                                        label="Tags"
                                        class="tp-control tp-tags-select"
                                        @click:clear="onTagPerformanceTagsCleared"
                                        @change="onTagPerformanceTagsChanged">
                                        <template v-slot:selection="{ item, selected }">
                                            <v-chip
                                                small
                                                outlined
                                                class="mr-1 tag-chip"
                                                :input-value="selected"
                                                close
                                                close-icon="mdi-close"
                                                @mousedown.stop
                                                @click.stop
                                                @click:close="removeTagPerformanceTag(item)">
                                                <span class="tag-dot" :style="tagDotStyle(item.id)" aria-hidden="true"></span>
                                                {{ item.label }}
                                            </v-chip>
                                        </template>
                                        <template v-slot:item="{ item, on, attrs }">
                                            <v-list-item v-on="on" v-bind="attrs">
                                                <v-list-item-content>
                                                    <v-list-item-title>
                                                        <span class="tag-color-dot" :style="tagDotStyle(item.id)"></span>
                                                        {{ item.label }}
                                                    </v-list-item-title>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </template>
                                    </v-autocomplete>
                                </v-col>

                                <v-col cols="12" md="4" lg="2" xl="2" class="pa-2">
                                    <v-autocomplete
                                        v-model="tagPerformanceCombineWithTags"
                                        :items="availableTags"
                                        item-text="label"
                                        item-value="id"
                                        :menu-props="{ contentClass: 'tag-filter-menu-content' }"
                                        :filter="filterTags"
                                        hide-selected
                                        cache-items
                                        offset-y
                                        multiple
                                        clear-icon="mdi-close"
                                        chips
                                        rounded
                                        small-chips
                                        dense
                                        outlined
                                        hide-details
                                        deletable-chips
                                        clearable
                                        label="Combine with (AND)"
                                        class="tp-control tp-combine-select"
                                        @change="onTagPerformanceControlsChange">
                                        <template v-slot:selection="{ item, selected }">
                                            <v-chip
                                                small
                                                outlined
                                                class="mr-1 tag-chip"
                                                :input-value="selected"
                                                close
                                                close-icon="mdi-close"
                                                @mousedown.stop
                                                @click.stop
                                                @click:close="removeTagPerformanceCombineTag(item)">
                                                <span class="tag-dot" :style="tagDotStyle(item.id)" aria-hidden="true"></span>
                                                {{ item.label }}
                                            </v-chip>
                                        </template>
                                        <template v-slot:item="{ item, on, attrs }">
                                            <v-list-item v-on="on" v-bind="attrs">
                                                <v-list-item-content>
                                                    <v-list-item-title>
                                                        <span class="tag-color-dot" :style="tagDotStyle(item.id)"></span>
                                                        {{ item.label }}
                                                    </v-list-item-title>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </template>
                                    </v-autocomplete>
                                </v-col>
                            </v-row>

                            <div v-if="matrixViewMode !== 'tag' || matrixTagOrder.length" class="mm-scroll px-5 pb-5" :class="{ 'mm-scroll--tag': matrixViewMode === 'tag' }">
                                <table class="mm-table" :key="'matrix-' + matrixViewMode + '-' + matrixVersion + '-' + tagMatrixVersion" aria-label="Brand performance metrics matrix">
                                    <caption class="sr-only">
                                        {{
                                            matrixViewMode === "brand"
                                                ? "Brand performance per platform: visibility, relevance rank, source references, and sentiment"
                                                : "Tag performance per platform for selected competitors"
                                        }}
                                    </caption>
                                    <thead>
                                        <tr>
                                            <th :class="matrixViewMode === 'brand' ? 'mm-col-platform' : 'mm-col-tag'">{{ matrixViewMode === "brand" ? "Platform" : "Tag" }}</th>
                                            <th
                                                v-for="col in matrixViewMode === 'brand' ? matrixBrandOrder : tagPerformanceMatrixColumns"
                                                :key="'mhead-' + (matrixViewMode === 'brand' ? col : col.key)"
                                                class="mm-col-brand">
                                                <div class="mm-brand-head" v-if="matrixViewMode === 'brand'">
                                                    <img
                                                        :alt="'favicon for ' + brandCanonical(col)"
                                                        v-if="brandFavicon(col)"
                                                        :src="'https://s2.googleusercontent.com/s2/favicons?domain=' + brandFavicon(col) + '&sz=16'"
                                                        class="mm-fav" />
                                                    <span>{{ brandCanonical(col) }}</span>
                                                </div>
                                                <div class="mm-brand-head" v-else>
                                                    <span
                                                        class="mm-platform-logo mm-platform-logo--head"
                                                        v-if="getPlatformInfo(col.platformKey).logo"
                                                        v-html="getPlatformInfo(col.platformKey).logo"></span>
                                                    <span>{{ (getPlatformInfo(col.platformKey).label || col.platformKey) + " • " + col.brandName }}</span>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="rowKey in matrixViewMode === 'brand' ? matrixPlatforms : matrixTagOrderVisible" :key="'row-' + rowKey">
                                            <td :class="matrixViewMode === 'brand' ? 'mm-platform-cell' : 'mm-tag-cell'">
                                                <div class="mm-platform-info" v-if="matrixViewMode === 'brand'">
                                                    <span class="mm-platform-logo" v-if="getPlatformInfo(rowKey).logo" v-html="getPlatformInfo(rowKey).logo"></span>
                                                    <span class="mm-platform-name">
                                                        {{ getPlatformInfo(rowKey).label || rowKey }}
                                                    </span>
                                                </div>
                                                <div v-else class="mm-brand-head">
                                                    <span class="tag-color-dot" :style="tagDotStyle(rowKey)"></span>
                                                    <span>{{ tagPerformanceRowLabel(rowKey) }}</span>
                                                </div>
                                            </td>
                                            <td
                                                v-for="col in matrixViewMode === 'brand' ? matrixBrandOrder : tagPerformanceMatrixColumns"
                                                :key="rowKey + '-' + (matrixViewMode === 'brand' ? col : col.key)"
                                                class="mm-metric-cell">
                                                <div
                                                    v-if="getMatrixCell(rowKey, col)"
                                                    :class="['mm-metric-box', { 'mm-metric-box--with-rank-pill': getMatrixCell(rowKey, col).relevanceRank != null }]">
                                                    <div v-if="getMatrixCell(rowKey, col).noData" class="mm-no-data-hint">No data for this combination</div>
                                                    <template v-else>
                                                        <div
                                                            v-if="getMatrixCell(rowKey, col).relevanceRank != null"
                                                            class="mm-rank-pill"
                                                            :class="matrixRankPillClass(getMatrixCell(rowKey, col).relevanceRank)">
                                                            <v-icon x-small v-if="showMatrixRankIcon(getMatrixCell(rowKey, col).relevanceRank)">mdi-trophy-outline</v-icon>
                                                            <span>{{ formatOrdinalRank(getMatrixCell(rowKey, col).relevanceRank) }}</span>
                                                        </div>
                                                        <div class="mm-line">
                                                            <span class="mm-label">VIS</span>
                                                            <div class="mm-value-wrap">
                                                                <span
                                                                    class="mm-value"
                                                                    :class="{
                                                                        'mm-na': getMatrixCell(rowKey, col).visibility == null
                                                                    }">
                                                                    {{ getMatrixCell(rowKey, col).visibility != null ? getMatrixCell(rowKey, col).visibility.toFixed(2) + "%" : "—" }}
                                                                </span>
                                                                <span
                                                                    v-if="formatMatrixMetricDelta('visibility', getMatrixCell(rowKey, col))"
                                                                    class="mm-change mm-change--compact"
                                                                    :title="matrixMetricDeltaTitle('visibility')"
                                                                    :class="matrixMetricDeltaClass('visibility', getMatrixCell(rowKey, col))">
                                                                    <v-icon x-small>{{ matrixMetricDeltaIcon("visibility", getMatrixCell(rowKey, col)) }}</v-icon>
                                                                    {{ formatMatrixMetricDelta("visibility", getMatrixCell(rowKey, col)) }}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div class="mm-bar">
                                                            <div
                                                                class="mm-bar-fill"
                                                                :style="{
                                                                    '--target-width': (getMatrixCell(rowKey, col).visibility != null ? Math.min(getMatrixCell(rowKey, col).visibility, 100) : 0) + '%'
                                                                }"></div>
                                                        </div>
                                                        <div class="mm-divider"></div>
                                                        <div class="mm-line mm-tertiary">
                                                            <span class="mm-label">Sources</span>
                                                            <div class="mm-value-wrap">
                                                                <span
                                                                    class="mm-value"
                                                                    :class="{
                                                                        'mm-na': getMatrixCell(rowKey, col).sourceReferences == null
                                                                    }">
                                                                    {{ getMatrixCell(rowKey, col).sourceReferences != null ? getMatrixCell(rowKey, col).sourceReferences : "—" }}
                                                                </span>
                                                                <span
                                                                    v-if="formatMatrixMetricDelta('sourceReferences', getMatrixCell(rowKey, col))"
                                                                    class="mm-change mm-change--compact"
                                                                    :title="matrixMetricDeltaTitle('sourceReferences')"
                                                                    :class="matrixMetricDeltaClass('sourceReferences', getMatrixCell(rowKey, col))">
                                                                    <v-icon x-small>{{ matrixMetricDeltaIcon("sourceReferences", getMatrixCell(rowKey, col)) }}</v-icon>
                                                                    {{ formatMatrixMetricDelta("sourceReferences", getMatrixCell(rowKey, col)) }}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div class="mm-divider"></div>
                                                        <div class="mm-line mm-tertiary">
                                                            <span class="mm-label">Sent</span>
                                                            <div class="mm-value-wrap">
                                                                <span
                                                                    class="mm-value"
                                                                    :class="{
                                                                        'mm-na': getMatrixCell(rowKey, col).sentiment == null
                                                                    }">
                                                                    {{ getMatrixCell(rowKey, col).sentiment != null ? formatSentimentValue(getMatrixCell(rowKey, col).sentiment) : "—" }}
                                                                </span>
                                                                <span
                                                                    v-if="formatMatrixMetricDelta('sentiment', getMatrixCell(rowKey, col))"
                                                                    class="mm-change mm-change--compact"
                                                                    :title="matrixMetricDeltaTitle('sentiment')"
                                                                    :class="matrixMetricDeltaClass('sentiment', getMatrixCell(rowKey, col))">
                                                                    <v-icon x-small>{{ matrixMetricDeltaIcon("sentiment", getMatrixCell(rowKey, col)) }}</v-icon>
                                                                    {{ formatMatrixMetricDelta("sentiment", getMatrixCell(rowKey, col)) }}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </template>
                                                </div>
                                                <div v-else class="mm-metric-box mm-empty">—</div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </v-card>
                    </div>
                </v-slide-y-transition>
            </div></v-slide-x-transition
        >
        <!-- SELECTION -->
        <v-slide-x-transition hide-on-leave>
            <div v-if="project && brands.length && !isDashboardSection && currentProjectSection === 'mentions'" class="brand-metrics-cards-wrapper mt-4">
                <v-row dense>
                    <v-col cols="12" md="4">
                        <v-card class="platform-card brand-metric-card quaroshadow" outlined>
                            <div class="pc-top-row">
                                <div class="pc-head-left">
                                    <div class="pc-favicon" aria-hidden="true">
                                        <img v-if="brandFaviconUrl" :src="brandFaviconUrl" :alt="(project && project.brand && project.brand.brand_name) || 'brand favicon'" />
                                        <span v-else>{{ (((project && project.brand && project.brand.brand_name) || "B") + "").substring(0, 1).toUpperCase() }}</span>
                                    </div>
                                    <h2 class="pc-title">Total Mentions</h2>
                                </div>
                            </div>
                            <div class="pc-main-row">
                                <div class="pc-value-wrap">
                                    <div class="pc-value">{{ mentionsDeltaMetrics.total || 0 }}</div>
                                    <div class="pc-subtitle">Current date snapshot</div>
                                </div>
                            </div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-card class="platform-card brand-metric-card quaroshadow" outlined>
                            <div class="pc-top-row">
                                <div class="pc-head-left">
                                    <div class="pc-favicon" aria-hidden="true">
                                        <img v-if="brandFaviconUrl" :src="brandFaviconUrl" :alt="(project && project.brand && project.brand.brand_name) || 'brand favicon'" />
                                        <span v-else>{{ (((project && project.brand && project.brand.brand_name) || "B") + "").substring(0, 1).toUpperCase() }}</span>
                                    </div>
                                    <h2 class="pc-title">New Mentions</h2>
                                </div>
                            </div>
                            <div class="pc-main-row">
                                <div class="pc-value-wrap">
                                    <div class="pc-value" style="color: #1f9d45">{{ mentionsDeltaMetrics.newCount || 0 }}</div>
                                    <div class="pc-subtitle">Found on end date only</div>
                                </div>
                            </div>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-card class="platform-card brand-metric-card quaroshadow" outlined>
                            <div class="pc-top-row">
                                <div class="pc-head-left">
                                    <div class="pc-favicon" aria-hidden="true">
                                        <img v-if="brandFaviconUrl" :src="brandFaviconUrl" :alt="(project && project.brand && project.brand.brand_name) || 'brand favicon'" />
                                        <span v-else>{{ (((project && project.brand && project.brand.brand_name) || "B") + "").substring(0, 1).toUpperCase() }}</span>
                                    </div>
                                    <h2 class="pc-title">Lost Mentions</h2>
                                </div>
                            </div>
                            <div class="pc-main-row">
                                <div class="pc-value-wrap">
                                    <div class="pc-value" style="color: #d64545">{{ mentionsDeltaMetrics.lostCount || 0 }}</div>
                                    <div class="pc-subtitle">Present on start date only</div>
                                </div>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>
            </div>
        </v-slide-x-transition>
        <v-slide-x-transition hide-on-leave>
            <div v-if="project && brands.length && !isDashboardSection && currentProjectSection === 'mentions' && mentionsPlatformOverviewRows.length" class="mentions-breakdown-wrap mt-4">
                <v-card class="rounded_card quaroshadow" outlined>
                    <v-card-title class="py-3 mb-2">
                        <div class="project-data-heading d-flex align-center">
                            <div class="pc-favicon mr-2" aria-hidden="true">
                                <img v-if="brandFaviconUrl" :src="brandFaviconUrl" :alt="(project && project.brand && project.brand.brand_name) || 'brand favicon'" />
                                <span v-else>{{ (((project && project.brand && project.brand.brand_name) || "B") + "").substring(0, 1).toUpperCase() }}</span>
                            </div>
                            <h2 style="font-size: 0.75rem !important; font-weight: 600 !important; color: rgba(15, 23, 42, 0.72); line-height: 1.2; text-transform: uppercase">
                                Platform mentions overview
                            </h2>
                            <v-tooltip top max-width="360" color="primary">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on" small class="ml-1" color="grey" style="vertical-align: middle">mdi-help-circle-outline</v-icon>
                                </template>
                                <span>Mentions, new and lost per platform for the selected timeframe.</span>
                            </v-tooltip>
                        </div>
                        <div class="flex-grow-1"></div>
                        <v-btn v-if="mentionsPlatformFilterKey" small rounded depressed color="primary" class="reset-platform-filter-btn" @click="clearMentionsPlatformFilter">
                            <v-icon left small>mdi-filter-off</v-icon>
                            Reset filter
                        </v-btn>
                    </v-card-title>
                    <v-card-text class="pt-1">
                        <div class="platform-delta-grid-head">
                            <div>Platform</div>
                            <div>Mentions</div>
                            <div>New</div>
                            <div>Lost</div>
                        </div>
                        <div class="platform-delta-rows">
                            <div v-for="row in mentionsPlatformOverviewRows" :key="row.key" class="platform-delta-row">
                                <div class="platform-delta-label">
                                    <span class="platform-icon-wrapper" v-if="row.logo" style="display: flex; align-items: center">
                                        <span class="platform-icon" v-html="row.logo" style="margin: auto"></span>
                                    </span>
                                    <span class="platform-delta-name">{{ row.label }}</span>
                                </div>
                                <div
                                    class="platform-delta-metric-cell"
                                    :class="{ 'platform-delta-metric-cell--active': isMentionsMetricActive(row.key, 'all') }"
                                    @click="onMentionsPlatformMetricClick(row.key, 'all')">
                                    <div class="platform-delta-metric-value">{{ row.total }}</div>
                                    <div class="platform-delta-bar">
                                        <div class="platform-delta-bar-fill platform-delta-bar-fill--total" :style="{ width: row.totalPct + '%' }"></div>
                                    </div>
                                </div>
                                <div
                                    class="platform-delta-metric-cell"
                                    :class="{ 'platform-delta-metric-cell--active': isMentionsMetricActive(row.key, 'new') }"
                                    @click="onMentionsPlatformMetricClick(row.key, 'new')">
                                    <div class="platform-delta-metric-value">{{ row.newCount }}</div>
                                    <div class="platform-delta-bar">
                                        <div class="platform-delta-bar-fill platform-delta-bar-fill--new" :style="{ width: row.newPct + '%' }"></div>
                                    </div>
                                </div>
                                <div
                                    class="platform-delta-metric-cell"
                                    :class="{ 'platform-delta-metric-cell--active': isMentionsMetricActive(row.key, 'lost') }"
                                    @click="onMentionsPlatformMetricClick(row.key, 'lost')">
                                    <div class="platform-delta-metric-value">{{ row.lostCount }}</div>
                                    <div class="platform-delta-bar">
                                        <div class="platform-delta-bar-fill platform-delta-bar-fill--lost" :style="{ width: row.lostPct + '%' }"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </div>
        </v-slide-x-transition>

        <v-slide-x-transition hide-on-leave>
            <div class="mt-5" v-if="project && brands.length && !isDashboardSection && currentProjectSection === 'brands'">
                <keep-alive>
                    <component
                        :is="activeTableViewComponent"
                        :key="`${currentProjectSection}-${project ? project._id : 'none'}`"
                        ref="activeTableView"
                        v-bind="activeTableViewProps"
                        @openAddPrompts="openAddPrompts"
                        @openProjectSettings="showProjectSettings = true"
                        @filtersUpdated="onActiveTableFiltersUpdated"
                        @tagsUpdated="fetchAvailableTags"
                        @searchUpdated="setBrandTableData"
                        @update:search="search = $event"
                        @topBrandsLoaded="onTopBrandsLoaded" />
                </keep-alive>
            </div>
        </v-slide-x-transition>

        <v-slide-x-transition hide-on-leave>
            <div class="mt-5" v-if="project && brands.length && !isDashboardSection && currentProjectSection === 'sources'">
                <keep-alive>
                    <component
                        :is="activeTableViewComponent"
                        :key="`${currentProjectSection}-${project ? project._id : 'none'}`"
                        ref="activeTableView"
                        v-bind="activeTableViewProps"
                        @filtersUpdated="onActiveTableFiltersUpdated"
                        @tagsUpdated="fetchAvailableTags"
                        @searchUpdated="setBrandTableData"
                        @update:search="search = $event"
                        @pageTypeLoaded="onSourcesPageTypeLoaded" />
                </keep-alive>
            </div>
        </v-slide-x-transition>
        <!-- Fanout: eigener Block, die View bringt eigene Cards und Toolbars mit -->
        <v-slide-x-transition hide-on-leave>
            <div class="mt-5" v-if="project && !isDashboardSection && currentProjectSection === 'fanout'">
                <keep-alive>
                    <component
                        :is="activeTableViewComponent"
                        :key="`${currentProjectSection}-${project ? project._id : 'none'}`"
                        ref="activeTableView"
                        v-bind="activeTableViewProps" />
                </keep-alive>
            </div>
        </v-slide-x-transition>
        <!-- Shopping: eigener Block ohne Card-/Toolbar-Wrapper, die View bringt ihr eigenes Layout mit -->
        <v-slide-x-transition hide-on-leave>
            <div class="mt-5" v-if="project && !isDashboardSection && currentProjectSection === 'shopping'">
                <keep-alive>
                    <component
                        :is="activeTableViewComponent"
                        :key="`${currentProjectSection}-${project ? project._id : 'none'}`"
                        ref="activeTableView"
                        v-bind="activeTableViewProps"
                        @switchPlatform="onShoppingSwitchPlatform"
                        @openPrompt="onShoppingOpenPrompt" />
                </keep-alive>
            </div>
        </v-slide-x-transition>
        <!-- TABLES -->
        <v-slide-x-transition hide-on-leave>
            <div class="mt-5" v-if="project && !isDashboardSection && currentProjectSection === 'prompts'">
                <keep-alive>
                    <component
                        :is="activeTableViewComponent"
                        :key="`${currentProjectSection}-${project ? project._id : 'none'}`"
                        ref="activeTableView"
                        v-bind="activeTableViewProps"
                        @openAddPrompts="openAddPrompts"
                        @openProjectSettings="showProjectSettings = true"
                        @filtersUpdated="onActiveTableFiltersUpdated"
                        @tagsUpdated="fetchAvailableTags"
                        @searchUpdated="setBrandTableData"
                        @update:search="search = $event"
                        @update:activeSubTab="mentionsSubTab = $event"
                        @platformOverviewUpdated="onMentionsPlatformOverviewUpdated"
                        @deltaMetrics="onMentionsDeltaMetrics" />
                </keep-alive>
            </div>
        </v-slide-x-transition>
        <v-slide-x-transition hide-on-leave>
            <div
                class="mt-5"
                v-if="
                    project &&
                    !isDashboardSection &&
                    currentProjectSection !== 'prompts' &&
                    currentProjectSection !== 'brands' &&
                    currentProjectSection !== 'sources' &&
                    currentProjectSection !== 'shopping' &&
                    currentProjectSection !== 'fanout' &&
                    currentProjectSection !== 'opportunities'
                "
                v-show="!isDashboardSection">
                <v-card class="rounded_card my-5 quaroshadow" style="border-top: 4px solid var(--v-primary-base); overflow: hidden">
                    <!-- Main Table Toolbar -->
                    <v-toolbar id="projects-tool-bar" flat class="gradient-toolbar elevation-0">
                        <!-- <div class="project-data-heading d-flex align-center">
                            <h2 class="project-data-heading__title">{{ activeTableMeta.label }}</h2>
                            <v-tooltip top max-width="350" color="primary" :disabled="!activeTableMeta.tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on" small class="ml-1" color="grey" style="vertical-align: middle">mdi-help-circle-outline</v-icon>
                                </template>
                                <span v-html="activeTableMeta.tooltip"></span>
                            </v-tooltip>
                        </div> -->
                        <v-toolbar-items v-if="currentProjectSection === 'mentions'">
                            <v-btn
                                v-for="btn in mentionsBarButtons"
                                :key="btn.key"
                                text
                                :class="{ 'active-button2': mentionsSubTab === btn.key }"
                                :color="mentionsSubTab === btn.key ? 'primary darken-1' : 'grey darken-4'"
                                @click="onMentionsSubTabClick(btn.key)">
                                <h2 style="font-size: 0.9rem; font-weight: 100">{{ btn.label }}</h2>
                                <v-tooltip top max-width="350" color="primary" :disabled="!btn.tooltip">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon v-bind="attrs" v-on="on" small class="ml-1" color="grey" style="vertical-align: middle">mdi-help-circle-outline</v-icon>
                                    </template>
                                    <span v-html="btn.tooltip"></span>
                                </v-tooltip>
                            </v-btn>
                        </v-toolbar-items>
                        <v-toolbar-items v-if="currentProjectSection === 'fanout'" class="mr-2">
                            <v-btn text class="active-button2" color="primary darken-1">
                                <v-icon x-small class="mr-1">mdi-call-split</v-icon>
                                <h2 style="font-size: 0.9rem; font-weight: 100">Fanout Queries</h2>
                            </v-btn>
                        </v-toolbar-items>
                        <v-text-field
                            class="pr-5"
                            v-model="search"
                            rounded
                            dense
                            filled
                            clearable
                            clear-icon="mdi-close"
                            prepend-inner-icon="mdi-magnify"
                            label="Search..."
                            single-line
                            hide-details
                            aria-label="Search table" />
                        <v-btn rounded color="primary" filled @click="exportActiveTable" aria-label="Export table">
                            <v-icon aria-hidden="true">mdi-cloud-download</v-icon>
                        </v-btn>
                    </v-toolbar>
                    <v-card-text class="px-0">
                        <keep-alive>
                            <component
                                :is="activeTableViewComponent"
                                :key="`${currentProjectSection}-${project ? project._id : 'none'}`"
                                ref="activeTableView"
                                v-bind="activeTableViewProps"
                                @openAddPrompts="openAddPrompts"
                                @openProjectSettings="showProjectSettings = true"
                                @filtersUpdated="onActiveTableFiltersUpdated"
                                @tagsUpdated="fetchAvailableTags"
                                @searchUpdated="setBrandTableData"
                                @update:activeSubTab="mentionsSubTab = $event"
                                @platformOverviewUpdated="onMentionsPlatformOverviewUpdated"
                                @deltaMetrics="onMentionsDeltaMetrics" />
                        </keep-alive>
                    </v-card-text>
                </v-card>
            </div>
        </v-slide-x-transition>
        <v-slide-x-transition hide-on-leave>
            <!-- Bewusst NICHT an brands.length gebunden: der Tab holt seine Daten selbst und muss nicht
                 auf die Marken-Ladekette der Seite (range-averages, ~30 s) warten. -->
            <div class="mt-5" v-if="project && currentProjectSection === 'opportunities'" v-show="!isDashboardSection">
                <keep-alive>
                    <component
                        :is="activeTableViewComponent"
                        :key="`${currentProjectSection}-${project ? project._id : 'none'}`"
                        ref="activeTableView"
                        v-bind="activeTableViewProps"
                        @filtersUpdated="onActiveTableFiltersUpdated"
                        @tagsUpdated="fetchAvailableTags"
                        @searchUpdated="setBrandTableData"
                        @update:activeSubTab="mentionsSubTab = $event"
                        @platformOverviewUpdated="onMentionsPlatformOverviewUpdated"
                        @deltaMetrics="onMentionsDeltaMetrics" />
                </keep-alive>
            </div>
        </v-slide-x-transition>
    </div>
</template>

<script>
    import BrandLineChart from "../../components/aisuite/BrandLineChart.vue"
    import BrandsTable from "../../components/aisuite/BrandsTable.vue"
    import SourcesTable from "../../components/aisuite/SourcesTable.vue"
    import AIProjectPromptsView from "./tabs/AIProjectPromptsView.vue"
    import AIProjectMentionsView from "./tabs/AIProjectMentionsView.vue"
    import AIProjectBrandsView from "./tabs/AIProjectBrandsView.vue"
    import AIProjectSourcesView from "./tabs/AIProjectSourcesView.vue"
    import AIProjectShoppingView from "./tabs/AIProjectShoppingView.vue"
    import AIProjectFanoutQueriesView from "./tabs/AIProjectFanoutQueriesView.vue"
    import AIProjectOpportunitiesView from "./tabs/AIProjectOpportunitiesView.vue"
    import AddPrompts from "../../components/aisuite/AddPrompts.vue"
    import ManageBrandsDialog from "../../components/aisuite/ManageBrandsDialog.vue"
    import { aiPerfEnabled } from "../../components/helper/aiRequestDedupe"
    import ProjectSettingsDialog from "../../components/aisuite/ProjectSettingsDialog.vue"
    import PromptOverview from "./PromptOverview.vue"
    import { resolveBrandColor } from "../../utils/brandColor"
    import { isPlatformTemporarilyUnavailable, unavailableHintText, unavailableTooltipText } from "../../utils/platformAvailability"
    import { saveAiProjectSettings, requestAddPlatformsUpdate, buildCreditsNotificationMessage } from "../../components/helper/aiSuiteProjectSettings"
    import PauseProjectDialog from "../../components/dialogs/PauseProjectDialog.vue"
    import { intervalLabel, updateProjectInterval, projectCreditUsage, buildNotEnoughCreditsMessage, isInitialImportRunning, importInProgressMessage } from "../../components/helper/projectStatus"

    export default {
        name: "AIProject",
        components: {
            BrandLineChart,
            BrandsTable,
            SourcesTable,
            AIProjectPromptsView,
            AIProjectMentionsView,
            AIProjectBrandsView,
            AIProjectSourcesView,
            AIProjectShoppingView,
            AIProjectFanoutQueriesView,
            AIProjectOpportunitiesView,
            AddPrompts,
            ManageBrandsDialog,
            ProjectSettingsDialog,
            PauseProjectDialog,
            // Bewusst synchron: als async-Komponente lädt der Chunk mitten in der Dialog-Transition
            // nach, die Transition bleibt dann bei opacity 0 stehen und das Panel bleibt leer.
            PromptOverview
        },
        provide() {
            // Tabellen/Tabs öffnen das Prompt-Detail darüber als Overlay statt per Routenwechsel
            return {
                aiSuiteOpenPromptDetail: this.openPromptOverlay
            }
        },
        data() {
            return {
                sentimentAvailable: false,
                notification: {
                    show: false,
                    color: "primary",
                    icon: "mdi-check-circle",
                    message: "",
                    timeout: -1, // -1 = persistent
                    actionLabel: "OK"
                },
                showProjectSettings: false,
                showPauseDialog: false,
                savingProjectSettings: false,
                promptOverlayOpen: false,
                promptOverlayParams: null,
                dashboardModeLoading: false,
                dateRangeTouched: false,
                tagSearch: "",
                tagOperator: "and",
                tagSettingsMenu: false,
                promptTableFilters: [],
                mentionTableFilters: [],
                filtersReady: false,
                _suppressUrlSync: false,
                _suppressPlatformWatcher: false,
                _brandMetricsCache: new Map(),
                _tagVisibilityCache: new Map(),
                _pendingQuery: null,
                _skipNextQueryApply: false,
                _suppressSectionRouteSync: false,
                loading: false,
                _mmRunId: 0,
                debouncedBuildMetricsMatrix: null,
                _mmBuilding: false,
                _mmPending: false,
                _tagMmRunId: 0,
                debouncedBuildTagMetricsMatrix: null,
                _tagMmBuilding: false,
                _tagMmPending: false,
                _suppressTagPerformanceFromGlobalFilters: false,
                _suppressSelectedTagsWatcher: false,
                _tagPerformanceTagsCleared: false,
                matrixVersion: 0,
                matrixLoaded: false,
                metricsMatrix: {},
                tagMatrixVersion: 0,
                tagMatrixLoaded: false,
                tagMetricsMatrix: {},
                matrixViewMode: "brand",
                tagPerformanceBrandKeys: [],
                tagPerformancePlatforms: [],
                tagPerformanceSelectedTags: [],
                tagPerformanceCombineWithTags: [],
                googlePromptCount: 0,
                selectedTags: [],
                availableTags: [],
                combinableTags: null,
                showAddPromptsDialog: false,
                showImportPromptTagsDialog: false,
                showManageBrandsDialog: false,
                importPromptTagsFile: null,
                importPromptTagsRows: [],
                importPromptTagsSummary: "",
                importPromptTagsError: "",
                importPromptTagsLoading: false,
                importPromptTagsCurrentPlatformOnly: false,
                projectSection: "dashboard",
                managedBrandsEdited: [],
                managedBrandsEditedLoaded: false,
                allKnownBrands: [],
                managedBrandsResetLoading: false,
                favoriteBrands: [],
                brandColors: {},
                brandDisplayMode: (() => {
                    try {
                        const stored = localStorage.getItem("aiSuiteBrandDisplayMode")
                        return stored === "favorites" ? "favorites" : "all"
                    } catch {
                        return "all"
                    }
                })(),
                _savingBrandFavorites: false,
                collectedPromptList: [],
                mergedNewPrompts: [],
                savingPrompts: false,
                project: null,
                search: "",

                brandMetrics: null,
                toolbarButtons: [
                    {
                        name: "Prompts",
                        label: "Prompts",
                        tooltip: `
                              <b>Prompts Table</b><br>
                              <span>
                                  Displays the original prompts (questions or tasks) that led to AI responses.<br>
                                  <br>
                                  <b>Tip:</b> Use this to understand the context in which brands are discussed and how questions influence the results.
                              </span>
                          `
                    },
                    {
                        name: "Mentions",
                        label: "Mentions",
                        tooltip: `
                              <b>Mentions Table</b><br>
                              <span>
                                  Shows all AI responses where your brand or competitors are mentioned.<br>
                                  <br>
                                  <b>Tip:</b> Use filters to analyze specific brands or time periods and discover trends in brand perception.
                              </span>
                          `
                    },
                    {
                        name: "Brands",
                        label: "Brands",
                        tooltip: `
                              <b>Brands Overview</b><br>
                              <span>
                                  Get an overview of all detected brands and their statistics for the selected time range.<br>
                                  <br>
                                  <b>Tip:</b> Compare visibility and relevance across brands to identify leaders and challengers.
                              </span>
                          `
                    },
                    {
                        name: "Sources",
                        label: "Sources",
                        tooltip: `
                              <b>Sources (Domains)</b><br>
                              <span>
                                   Shows aggregated domains from AI responses with the number of mentions (items) and how many responses they appear in.<br><br>
                                  <b>Tip:</b> Use this table to identify frequently referenced sources, shops, and publishers.
                              </span>
                          `
                    },
                    {
                        name: "Fanout Queries",
                        label: "Fanout Queries",
                        tooltip: `
                              <b>Fanout Queries</b><br>
                              <span>
                                  Shows background web queries used by LLMs during response generation (fields <b>web_search_query</b> and <b>fan_out_queries</b>).<br><br>
                                  <b>Tip:</b> Use this to understand retrieval intent and hidden query expansion behavior.
                              </span>
                          `
                    },
                    {
                        name: "Opportunities",
                        label: "Opportunities",
                        disabled: true,
                        comingSoon: true,
                        tooltip: `
                              <b>Opportunities</b><br>
                              <span>
                                  Responses where your brand is not present in sources and entities, including opportunity suggestions for content creation.<br><br>
                                  <b>Tip:</b> Prioritize recurring query patterns and source signals to build targeted content.
                              </span>
                          `
                    }
                ],
                activeChartButton: "Visibility",
                activeButton: "Prompts",
                promptResponses: [],
                loadingPrompts: false,
                brands: [],
                tableBrands: [],
                loadingBrands: false,
                selectedDates: [],
                sortDesc: true,
                start: 0,
                limit: 20,
                test_set: false,
                selectedBrands: [{}],
                brandSearchInput: "",
                availableDates: [],
                datepickerMenu: false,
                dashboardMode: (() => {
                    try {
                        const stored = localStorage.getItem("aiSuiteDashboardMode")
                        return stored === "average" || stored === "absolute" ? stored : "absolute"
                    } catch {
                        return "absolute"
                    }
                })(),
                defaultDateRangeDays: 30,
                dateRangePresets: [
                    { key: "last7", label: "Last 7 days", days: 7 },
                    { key: "last14", label: "Last 14 days", days: 14 },
                    { key: "last30", label: "Last 30 days", days: 30 },
                    { key: "last90", label: "Last 90 days", days: 90 },
                    { key: "last180", label: "Last 6 months", days: 180 },
                    { key: "last365", label: "Last 12 months", days: 365 },
                    { key: "all", label: "All time", days: null }
                ],
                brandColorCache: {},

                mentionBrand: {},
                mentionsSubTab: "all",
                mentionsDeltaMetrics: {
                    total: 0,
                    newCount: 0,
                    lostCount: 0,
                    loading: false
                },
                mentionsDeltaCacheSignature: "",
                topBrandsCacheSignature: "",
                topBrandsCacheByPlatform: {},
                sourcesPageTypeCacheSignature: "",
                sourcesPageTypeCacheRaw: [],
                mentionsPlatformOverviewRows: [],
                mentionsPlatformFilterKey: null,
                mentionsPlatformFilterMetric: null,
                selectedPlatform: null,
                topMiniActive: "brands", // NEU TogglebrandMetrics: null,
                brandMetricCards: [], // NEU,
                promptObjects: []
                // totalPromptsCount: 0
            }
        },
        watch: {
            async tagOperator(newVal, oldVal) {
                if (newVal === oldVal || !this.project || !this.selectedTags.length) return
                this.fetchCombinableTags()
                this._suppressTagPerformanceFromGlobalFilters = true
                try {
                    this.clearBrandMetricsCache()
                    await this.setBrandData(this.$route.params.id, this.category_id)
                    await this.refreshBrandMetrics()
                    if (this.selectedPlatform?.key === "all") {
                        this.debouncedBuildMetricsMatrix()
                        // Global tag operator should not force refetch of Tag Performance matrix.
                        if (this.matrixViewMode !== "tag") {
                            this.triggerTagPerformanceRefresh()
                        }
                    }
                } finally {
                    this._suppressTagPerformanceFromGlobalFilters = false
                }
                this.syncQueryFromState()
            },
            matrixViewMode(newVal) {
                if (newVal !== "tag") return
                this.triggerTagPerformanceRefresh(true)
            },
            brandDisplayMode(newVal) {
                try {
                    localStorage.setItem("aiSuiteBrandDisplayMode", newVal)
                } catch (e) {
                    // ignore storage errors
                }
                if (!this.project) return
                this.applyBrandDisplaySelection()
            },
            tagPerformanceSelectedTags() {
                this.triggerTagPerformanceRefresh()
            },
            tagPerformanceBrandKeys() {
                this.triggerTagPerformanceRefresh()
            },
            tagPerformancePlatforms() {
                this.triggerTagPerformanceRefresh()
            },
            tagPerformanceCombineWithTags() {
                this.triggerTagPerformanceRefresh()
            },
            activeButton(newVal) {
                if (newVal === "Prompts") {
                    // this.fetchPromptResponses()
                }
                this.syncQueryFromState()
            },
            selectedBrands() {
                this.ensureTagPerformanceBrandSelection()
                this.ensureTagPerformanceTagSelection()
                if (this.selectedPlatform?.key == "all") {
                    this.matrixLoaded = false
                    this.debouncedBuildMetricsMatrix()
                    if (!this._suppressTagPerformanceFromGlobalFilters) {
                        this.triggerTagPerformanceRefresh()
                    }
                }
            },
            selectedPlatform: {
                async handler(newVal, oldVal) {
                    if (this._suppressPlatformWatcher) {
                        this._suppressPlatformWatcher = false
                        return
                    }
                    if (!newVal || !oldVal) return
                    this.clearBrandMetricsCache()
                    this.updateAvailableDates()
                    const nextDates = this.getValidSelectedDates(this.availableDates, this.selectedDates)
                    if (!this.arraysEqual(this.selectedDates, nextDates)) this.selectedDates = nextDates
                    await this.setBrandData(this.$route.params.id, this.category_id)
                    await this.refreshBrandMetrics()
                    await this.fetchAvailableTags() // Tags neu laden bei Plattformwechsel
                    this.syncQueryFromState()
                },
                immediate: false
            },
            // Bei Datumsänderung Tags neu laden
            selectedDates() {
                this.clearBrandMetricsCache()
                if (this.project) this.fetchAvailableTags()
                this.setBrandData(this.$route.params.id, this.category_id)
                this.syncQueryFromState()
            },
            async dashboardMode(newVal) {
                try {
                    localStorage.setItem("aiSuiteDashboardMode", newVal)
                } catch (e) {
                    // ignore storage errors
                }
                if (!this.project) return
                this.dashboardModeLoading = true
                try {
                    this.clearBrandMetricsCache()
                    if (this._tagVisibilityCache && typeof this._tagVisibilityCache.clear === "function") {
                        this._tagVisibilityCache.clear()
                    }
                    await this.setBrandData(this.$route.params.id, this.category_id)
                    await this.refreshBrandMetrics()
                    if (this.selectedPlatform?.key === "all") {
                        this.debouncedBuildMetricsMatrix()
                        this.triggerTagPerformanceRefresh()
                    }
                } finally {
                    this.dashboardModeLoading = false
                }
            },
            // NEU: Bei Tag-Änderung alles neu laden (analog Plattformwechsel)
            async selectedTags() {
                if (this._suppressSelectedTagsWatcher) {
                    this._suppressSelectedTagsWatcher = false
                    return
                }
                if (!this.project) return
                this.fetchCombinableTags()
                this._suppressTagPerformanceFromGlobalFilters = true
                try {
                    this.clearBrandMetricsCache()
                    await this.setBrandData(this.$route.params.id, this.category_id)
                    await this.refreshBrandMetrics()
                    if (this.selectedPlatform?.key === "all") {
                        this.debouncedBuildMetricsMatrix()
                        // Global tag filter should not force refetch of Tag Performance matrix.
                        // Tag Performance updates only via its own controls (brand/tags/combine-with).
                        if (this.matrixViewMode !== "tag") {
                            this.triggerTagPerformanceRefresh()
                        }
                    }
                } finally {
                    this._suppressTagPerformanceFromGlobalFilters = false
                }
                this.syncQueryFromState()
            },
            promptTableFilters() {
                this.syncQueryFromState()
            },
            mentionTableFilters() {
                this.syncQueryFromState()
            },
            promptOverlayOpen(open) {
                // Seite hinter dem Overlay einfrieren, damit deren Scroll-Position unangetastet bleibt
                this.setPageScrollLock(open)
                if (open) return
                // Schließen über X/ESC/Backdrop: wenn das Overlay per push geöffnet wurde und
                // der pd-Param noch in der URL steht, einen History-Schritt zurückgehen —
                // so bleiben UI-Schließen und Browser-Zurück konsistent.
                if (this._promptOverlayPushed && this.$route?.query?.pd) {
                    this._promptOverlayPushed = false
                    this.$router.back()
                    return
                }
                this._promptOverlayPushed = false
                // Deep-Link-Fall (kein eigener History-Eintrag): pd per replace entfernen
                this.syncPromptOverlayQuery(null)
            },
            "$route.params.section": {
                immediate: true,
                handler() {
                    this.syncSectionFromRoute()
                }
            },
            "$route.query": {
                immediate: true,
                handler(newQuery) {
                    if (this._skipNextQueryApply) {
                        this._skipNextQueryApply = false
                        return
                    }
                    if (!this.project) {
                        this._pendingQuery = newQuery
                        return
                    }
                    // Browser-Zurück/-Vorwärts: Overlay-Zustand dem pd-Param folgen lassen
                    this.syncPromptOverlayFromRoute(newQuery)
                    this.applyQueryToState(newQuery)
                }
            }
        },
        created() {
            const makeDebounce = (fn, wait = 400) => {
                let t
                return (...args) => {
                    clearTimeout(t)
                    t = setTimeout(() => fn.apply(this, args), wait)
                }
            }
            this.debouncedBuildMetricsMatrix = makeDebounce(this.buildMetricsMatrix, 450)
            this.debouncedBuildTagMetricsMatrix = makeDebounce(this.buildTagMetricsMatrix, 450)
        },
        computed: {
            projectIntervalText() {
                return intervalLabel(this.project && this.project.interval)
            },
            // Pausieren erst nach dem Erstimport - sonst würde nur der bereits importierte Teil gutgeschrieben
            initialImportRunning() {
                return isInitialImportRunning(this.project)
            },
            promptOverlayKey() {
                // Erzwingt Remount des eingebetteten PromptOverview, wenn ein anderer Prompt geöffnet wird
                const p = this.promptOverlayParams || {}
                return [p.task_id || "", p.prompt || "", p.platform || "", p.dates || ""].join("|")
            },
            brandSelectorOptions() {
                const dateBrands = Array.isArray(this.brands) ? this.brands : []
                const known = Array.isArray(this.allKnownBrands) ? this.allKnownBrands : []
                const norm = (v) =>
                    (v || "")
                        .toString()
                        .trim()
                        .toLowerCase()
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "")

                let merged
                if (!known.length) {
                    merged = dateBrands
                } else {
                    const seen = new Set()
                    merged = []
                    dateBrands.forEach((b) => {
                        const key = norm(b?.canonicalName)
                        if (!key || seen.has(key)) return
                        seen.add(key)
                        merged.push(b)
                    })
                    known.forEach((b) => {
                        const key = norm(b?.canonicalName)
                        if (!key || seen.has(key)) return
                        seen.add(key)
                        merged.push({
                            canonicalName: b.canonicalName,
                            mentions: Array.isArray(b.mentions) ? b.mentions : [],
                            brand_domain: b.brand_domain || null,
                            metadata: b.metadata || {},
                            responsesWithMention: 0,
                            salienceAvg: 0,
                            sentimentAvg: 0,
                            __notInCurrentDateRange: true
                        })
                    })
                }

                // Im Favoriten-Modus nur Favoriten (plus aktuell ausgewählte Brands) anzeigen
                if (this.brandDisplayMode === "favorites" && Array.isArray(this.favoriteBrands) && this.favoriteBrands.length) {
                    const selectedKeys = new Set((this.selectedBrands || []).map((b) => norm(b?.canonicalName)).filter(Boolean))
                    return merged.filter((b) => this.isFavoriteBrand(b) || selectedKeys.has(norm(b?.canonicalName)))
                }

                return merged
            },
            importPromptTagsUniqueCount() {
                const unique = new Set()
                ;(this.importPromptTagsRows || []).forEach((row) => {
                    ;(row.tags || []).forEach((tag) => {
                        if (tag != null && String(tag).trim()) unique.add(String(tag).trim())
                    })
                })
                return unique.size
            },
            chartBarButtons() {
                let btns = [
                    {
                        name: "Visibility",
                        label: "Visibility",
                        tooltip: `
                              <b>Visibility</b><br>
                              <span>
                                  Shows how visible the brand is over time based on the number of mentions in AI responses.<br>
                                  <br>
                                  <b>Tip:</b> Use this to track brand awareness trends and compare different brands.
                              </span>
                          `
                    }
                    // {
                    //     name: "Sentiment",
                    //     label: "Sentiment",
                    //     tooltip: `
                    //           <b>Sentiment</b><br>
                    //           <span>
                    //               Visualizes the weighted sentiment score (-1 = very negative, +1 = very positive) for each selected brand over time.<br>
                    //               <br>
                    //               <b>Tip:</b> Compare how brand perception shifts relative to visibility and relevance.
                    //           </span>
                    //       `
                    // }
                ]
                if (this.sentimentAvailable) {
                    btns.push({
                        name: "Sentiment",
                        label: "Sentiment",
                        tooltip: `
                              <b>Sentiment</b><br>
                              <span>
                                  Visualizes the weighted sentiment score (-1 = very negative, +1 = very positive) for each selected brand over time.<br>
                                  <br>
                                  <b>Tip:</b> Compare how brand perception shifts relative to visibility and relevance.
                              </span>
                          `
                    })
                }
                return btns
            },
            mentionsBarButtons() {
                return [
                    { key: "all", label: "Mentions", tooltip: "All mentions for the selected timeframe." },
                    { key: "new", label: "New Mentions", tooltip: "Mentions that appear on the end date but not on the start date." },
                    { key: "lost", label: "Lost Mentions", tooltip: "Mentions that appear on the start date but not on the end date." }
                ]
            },
            matrixPlatforms() {
                const source = this.matrixViewMode === "tag" ? this.tagMetricsMatrix : this.metricsMatrix
                return Object.keys(source || {}).sort()
            },
            matrixBrandOrder() {
                return (this.selectedBrands || []).map((b) => this.brandKey(b)).filter(Boolean)
            },
            // Aktiv, wenn der Favoriten-Modus gewählt ist und Favoriten hinterlegt sind
            favoritesModeActive() {
                return this.brandDisplayMode === "favorites" && Array.isArray(this.favoriteBrands) && this.favoriteBrands.length > 0
            },
            // Top-Brands-Tabelle (Dashboard-Mini): im Favoriten-Modus nur Favoriten
            dashboardTopBrands() {
                if (this.favoritesModeActive) return (this.brands || []).filter((b) => this.isFavoriteBrand(b))
                return this.brands
            },
            // Brands-Tab / Top Brands per Platform: im Favoriten-Modus nur Favoriten
            brandsTabData() {
                if (this.favoritesModeActive) return (this.tableBrands || []).filter((b) => this.isFavoriteBrand(b))
                return this.tableBrands
            },
            // Items für den globalen Tag-Filter: bei AND nur Tags, die mit der Auswahl kombiniert noch Daten haben
            tagFilterItems() {
                if (this.tagOperator !== "and" || !Array.isArray(this.selectedTags) || !this.selectedTags.length) return this.availableTags
                if (!Array.isArray(this.combinableTags)) return this.availableTags
                const allowed = new Set(this.combinableTags)
                this.selectedTags.forEach((t) => allowed.add(t))
                return (this.availableTags || []).filter((t) => allowed.has(t.id))
            },
            tagFilterRestricted() {
                return this.tagOperator === "and" && Array.isArray(this.selectedTags) && this.selectedTags.length > 0 && Array.isArray(this.combinableTags)
            },
            matrixTagOrder() {
                return (this.availableTags || []).map((t) => t?.id).filter(Boolean)
            },
            matrixTagOrderVisible() {
                if (this._tagPerformanceTagsCleared && Array.isArray(this.tagPerformanceSelectedTags) && this.tagPerformanceSelectedTags.length === 0) {
                    return []
                }
                const selected = Array.isArray(this.tagPerformanceSelectedTags) ? this.tagPerformanceSelectedTags.filter(Boolean) : []
                return selected.length ? selected : this.matrixTagOrder.slice(0, 6)
            },
            tagPerformanceBrandOptions() {
                const seen = new Set()
                return (this.brands || [])
                    .map((b) => {
                        const key = this.brandKey(b)
                        const name = b?.canonicalName || b?.brand_name || (Array.isArray(b?.mentions) && b.mentions[0]) || key
                        return key ? { key, name } : null
                    })
                    .filter((x) => x && !seen.has(x.key) && seen.add(x.key))
            },
            tagPerformancePlatformOptions() {
                return (this.platformSelectItems || []).filter((p) => p && p.key && p.key !== "all" && !p.disabled)
            },
            tagPerformanceMatrixColumns() {
                const platformOptions = this.tagPerformancePlatformOptions || []
                const platformOptionKeys = new Set(platformOptions.map((p) => p.key))
                const selectedPlatformKeys = Array.isArray(this.tagPerformancePlatforms) ? this.tagPerformancePlatforms.filter((k) => platformOptionKeys.has(k)) : []
                const platformKeys = selectedPlatformKeys.length ? selectedPlatformKeys : platformOptions.map((p) => p.key)

                const brandOptions = this.tagPerformanceBrandOptions || []
                const brandOptionKeys = new Set(brandOptions.map((b) => b.key))
                const selectedBrandKeys = Array.isArray(this.tagPerformanceBrandKeys) ? this.tagPerformanceBrandKeys.filter((k) => brandOptionKeys.has(k)) : []
                const brandKeys = selectedBrandKeys.length ? selectedBrandKeys : brandOptions.map((b) => b.key)
                const selectedBrandOptions = brandOptions.filter((b) => brandKeys.includes(b.key))

                return platformKeys.flatMap((platformKey) =>
                    selectedBrandOptions.map((brand) => ({
                        key: `${platformKey}::${brand.key}`,
                        platformKey,
                        brandKey: brand.key,
                        brandName: brand.name
                    }))
                )
            },
            totalPromptsCount() {
                if (!this.project || !this.selectedPlatform) return 0
                const baseCount = this.project.prompt_count || this.project.keyword_count || 0
                const platformKey = this.selectedPlatform.key
                if (platformKey === "google") {
                    return this.googlePromptCount || 0
                }
                if (platformKey === "all") {
                    const basePlatforms = (this.project.platforms || []).map((p) => p.toLowerCase()).filter((p) => p !== "google")
                    let sum = basePlatforms.length * baseCount
                    if (this.project.google) sum += this.googlePromptCount || 0
                    return sum
                }
                return baseCount
            },
            sourcesImportDatesByPlatform() {
                const obj = this.buildImportDatesByPlatform(2) || {}
                return Object.entries(obj).map(([platform, importDates]) => ({ platform, importDates }))
            },
            processedDatesByPlatform() {
                if (!this.project || !this.selectedPlatform) return []
                const map = {
                    openai: "processed_dates_ai_tracking_openai",
                    chatgpt: "processed_dates_ai_tracking_chatgpt",
                    google: "processed_dates",
                    google_ai_mode: "processed_dates_ai_tracking_google_ai_mode",
                    gemini: "processed_dates_ai_tracking_gemini",
                    claude: "processed_dates_ai_tracking_claude",
                    perplexity: "processed_dates_ai_tracking_perplexity",
                    grok: "processed_dates_ai_tracking_grok",
                    microsoft_copilot: "processed_dates_ai_tracking_microsoft_copilot"
                }
                // Aggregation für "all"
                if (this.selectedPlatform.key === "all") {
                    const sets = []
                    Object.values(map).forEach((k) => {
                        const arr = this.project[k] || []
                        if (Array.isArray(arr)) sets.push(...arr)
                    })
                    // Unique + sort (ISO Strings)
                    return Array.from(new Set(sets)).sort()
                }
                const key = map[this.selectedPlatform.key || this.selectedPlatform] || "processed_dates_ai_tracking_openai"
                return this.project[key] || []
            },
            selectedDatesText() {
                return this.selectedDates.join(" ~ ")
            },
            activeDateRangePresetKey() {
                const dates = Array.isArray(this.availableDates) ? this.availableDates.filter(Boolean) : []
                const sel = Array.isArray(this.selectedDates) ? [...this.selectedDates].filter(Boolean).sort() : []
                if (!dates.length || sel.length < 1) return null
                const end = dates[dates.length - 1]
                const selStart = sel[0]
                const selEnd = sel.length > 1 ? sel[1] : sel[0]
                if (selEnd !== end) return null
                for (const preset of this.dateRangePresets || []) {
                    let expectedStart
                    if (!preset.days) {
                        expectedStart = dates[0]
                    } else {
                        const endDate = new Date(end)
                        if (isNaN(endDate)) continue
                        const candidate = new Date(endDate)
                        candidate.setDate(candidate.getDate() - (preset.days - 1))
                        const candidateIso = candidate.toISOString().slice(0, 10)
                        expectedStart = dates.find((d) => d >= candidateIso) || dates[0]
                    }
                    if (expectedStart === selStart) return preset.key
                }
                return null
            },
            brandMetricsRangeLabel() {
                const fmt = (iso) => {
                    if (!iso) return ""
                    const dt = new Date(`${iso}T00:00:00.000Z`)
                    if (isNaN(dt)) return iso
                    const day = String(dt.getUTCDate()).padStart(2, "0")
                    const month = String(dt.getUTCMonth() + 1).padStart(2, "0")
                    return `${day}.${month}`
                }
                if (this.dashboardMode === "absolute") {
                    const dates = Array.isArray(this.selectedDates) ? this.selectedDates.filter(Boolean) : []
                    if (!dates.length) return null
                    const sorted = [...dates].sort()
                    const start = sorted[0]
                    const end = sorted[sorted.length - 1] || sorted[0]
                    if (!start || !end || start === end) return fmt(start) || null
                    return `${fmt(start)} vs ${fmt(end)}`
                }
                const bm = this.brandMetrics
                if (!bm || !bm.currentRange || !bm.previousRange) return null
                const cur = `${fmt(bm.currentRange.from)}–${fmt(bm.currentRange.to)}`
                const prev = `${fmt(bm.previousRange.from)}–${fmt(bm.previousRange.to)}`
                return `${prev} vs ${cur}`
            },
            selectedDbDates() {
                if (!this.project) return ["", ""]
                // Hole die richtigen processed_dates für die ausgewählte Plattform
                const processedDates = this.processedDatesByPlatform
                let datehelper = processedDates.map((d) => new Date(d).toISOString().substring(0, 10))
                let firstindex = datehelper.lastIndexOf(this.selectedDates[0])
                let secondindex = datehelper.lastIndexOf(this.selectedDates[1])
                return [processedDates[firstindex] || "", processedDates[secondindex] || ""]
            },
            importingPlatformKeys() {
                const addedPlatforms = this.project?.setup?.added_platforms
                if (!addedPlatforms || typeof addedPlatforms !== "object") return []

                const keyMap = {
                    serp: "google"
                }

                return Array.from(
                    new Set(
                        Object.keys(addedPlatforms)
                            .map((key) => (key || "").toString().trim().toLowerCase())
                            .map((key) => keyMap[key] || key)
                            .filter(Boolean)
                    )
                )
            },
            platformSelectItems() {
                const platformMap = [
                    { key: "chatgpt", label: "ChatGPT" },
                    { key: "gemini", label: "Gemini" },
                    { key: "google", label: "Google AIO" },
                    { key: "google_ai_mode", label: "AI Mode" },
                    { key: "perplexity", label: "Perplexity" },
                    { key: "grok", label: "Grok" },
                    { key: "microsoft_copilot", label: "Microsoft Copilot" },
                    { key: "openai", label: "OpenAI" }
                    // { key: "claude", label: "Claude" }
                ]
                const allowed = this.project && this.project.platforms ? this.project.platforms.map((p) => p.toLowerCase()) : []
                if (this.project?.google) allowed.push("google")
                const importingSet = new Set(this.importingPlatformKeys)

                const items = platformMap
                    .map((p) => {
                        const isImporting = importingSet.has(p.key)
                        const isUnavailable = isPlatformTemporarilyUnavailable(p.key)
                        return {
                            key: p.key,
                            name: p.label,
                            status: isUnavailable ? "temporarily unavailable" : isImporting ? "importing..." : this.getPlatformInfo(p.key).status,
                            disabled: isUnavailable || isImporting || !allowed.includes(p.key),
                            unavailable: isUnavailable
                        }
                    })
                    // Aktiv (included) zuerst, danach alphabetisch
                    .sort((a, b) => {
                        if (a.disabled !== b.disabled) return a.disabled - b.disabled
                        return a.name.localeCompare(b.name)
                    })

                return [{ key: "all", name: "All Platforms", disabled: false }, ...items]
            },
            unavailablePlatformNames() {
                const projectPlatforms = Array.isArray(this.project?.platforms) ? this.project.platforms.map((p) => (p || "").toString().toLowerCase()) : []
                return (this.platformSelectItems || []).filter((p) => p && p.unavailable && projectPlatforms.includes(p.key)).map((p) => p.name)
            },
            unavailableHint() {
                return unavailableHintText(this.unavailablePlatformNames)
            },
            unavailableTooltip() {
                return unavailableTooltipText(this.unavailablePlatformNames)
            },
            brandDomain() {
                const raw = this.project?.brand?.brand_domain || ""
                if (!raw) return ""
                return raw
                    .replace(/^https?:\/\//, "")
                    .replace(/^www\./, "")
                    .split("/")[0]
            },
            brandFaviconUrl() {
                return this.brandDomain ? "https://s2.googleusercontent.com/s2/favicons?domain=" + this.brandDomain + "&sz=32" : null
            },
            platformLogoPreview() {
                const items = this.platformSelectItems.filter((i) => i.key !== "all" && !i.disabled).slice(0, 3)
                return items.map((i) => this.getPlatformInfo(i.key).logo).filter(Boolean)
            },
            projectSectionTabs() {
                return [
                    { key: "dashboard", label: "Dashboard" },
                    { key: "prompts", label: "Prompts" },
                    { key: "mentions", label: "Mentions" },
                    { key: "brands", label: "Brands" },
                    { key: "sources", label: "Sources" },
                    { key: "shopping", label: "Shopping" },
                    { key: "fanout", label: "Fanout Queries" }
                    // Opportunities vorerst ausgeblendet (Entscheidung Malte 2026-10-19) - Ansicht/Route bleiben im Code
                    // { key: "opportunities", label: "Opportunities" }
                ]
            },
            currentProjectSection() {
                return this.normalizeProjectSection(this.projectSection)
            },
            isDashboardSection() {
                return this.currentProjectSection === "dashboard"
            },
            activeTableMeta() {
                const effectiveButton = this.sectionToActiveButton(this.currentProjectSection) || this.activeButton
                const active = (this.toolbarButtons || []).find((b) => b.name === effectiveButton)
                return active || { label: effectiveButton || "Table", tooltip: "" }
            },
            activeTableViewComponent() {
                const effectiveButton = this.sectionToActiveButton(this.currentProjectSection) || this.activeButton
                if (effectiveButton === "Mentions") return "AIProjectMentionsView"
                if (effectiveButton === "Brands") return "AIProjectBrandsView"
                if (effectiveButton === "Sources") return "AIProjectSourcesView"
                if (effectiveButton === "Shopping") return "AIProjectShoppingView"
                if (effectiveButton === "Fanout Queries") return "AIProjectFanoutQueriesView"
                if (effectiveButton === "Opportunities") return "AIProjectOpportunitiesView"
                return "AIProjectPromptsView"
            },
            activeTableViewProps() {
                const effectiveButton = this.sectionToActiveButton(this.currentProjectSection) || this.activeButton
                const platforms = this.project?.platforms || []

                if (effectiveButton === "Mentions") {
                    return {
                        activeSubTab: this.mentionsSubTab,
                        platformFilterKey: this.mentionsPlatformFilterKey,
                        platformFilterMetric: this.mentionsPlatformFilterMetric,
                        brand: this.mentionBrand,
                        brands: this.brands,
                        sentimentAvailable: this.sentimentAvailable,
                        initialFilters: this.mentionTableFilters,
                        initialFiltersReady: this.filtersReady,
                        tags: this.selectedTags,
                        tagOperator: this.tagOperator,
                        search: this.search,
                        hasTags: this.availableTags.length > 0,
                        platforms,
                        project: this.project,
                        selectedPlatform: this.selectedPlatform,
                        selectedDates: this.selectedDates,
                        fromDate: this.selectedDbDates[0],
                        toDate: this.selectedDbDates[1] || this.selectedDbDates[0],
                        projectId: this.project?._id,
                        loading: this.loadingPrompts,
                        cachedDeltaSignature: this.mentionsDeltaCacheSignature
                    }
                }

                if (effectiveButton === "Brands") {
                    return {
                        tags: this.selectedTags,
                        tagOperator: this.tagOperator,
                        sentimentAvailable: this.sentimentAvailable,
                        brands: this.brandsTabData,
                        search: this.search,
                        loading: this.loadingBrands,
                        totalPrompts: this.totalPromptsCount,
                        project: this.project,
                        projectId: this.project?._id,
                        selectedDates: this.selectedDbDates,
                        selectedPlatform: this.selectedPlatform,
                        dashboardMode: this.dashboardMode,
                        rangeLabel: this.brandMetricsRangeLabel,
                        cachedTopBrandsSignature: this.topBrandsCacheSignature,
                        cachedTopBrandsByPlatform: this.topBrandsCacheByPlatform,
                        platformItems: this.platformSelectItems
                            .filter((p) => p && p.key && p.key !== "all" && !p.disabled)
                            .map((p) => {
                                const info = this.getPlatformInfo(p.key)
                                return {
                                    key: p.key,
                                    label: info?.label || p.name || p.key,
                                    logo: info?.logo || null
                                }
                            })
                    }
                }

                if (effectiveButton === "Sources") {
                    return {
                        projectId: this.project?._id,
                        selectedDates: this.selectedDbDates,
                        search: this.search,
                        tagsOperator: this.tagOperator,
                        tags: this.selectedTags,
                        brands: this.brands,
                        project: this.project,
                        googlePromptCount: this.googlePromptCount,
                        selectedPlatform: this.selectedPlatform,
                        importDatesByPlatform: this.sourcesImportDatesByPlatform,
                        cachedPageTypeSignature: this.sourcesPageTypeCacheSignature,
                        cachedPageTypeStatsRaw: this.sourcesPageTypeCacheRaw
                    }
                }

                if (effectiveButton === "Shopping") {
                    return {
                        projectId: this.project?._id,
                        project: this.project,
                        // Rohe Picker-Daten (nicht selectedDbDates): Shopping aggregiert über den
                        // echten Zeitraum, das Snapping auf exakte processed_dates würde die Range
                        // auf leere Strings kollabieren lassen, wenn ein Randtag kein Import-Tag ist.
                        selectedDates: this.selectedDates,
                        selectedPlatform: this.selectedPlatform,
                        tags: this.selectedTags,
                        tagsOperator: this.tagOperator,
                        dashboardMode: this.dashboardMode
                    }
                }

                if (effectiveButton === "Fanout Queries") {
                    return {
                        projectId: this.project?._id,
                        project: this.project,
                        brands: this.brands,
                        selectedDates: this.selectedDbDates,
                        // Rohe Picker-Daten zusätzlich: die Query-Aggregation braucht einen
                        // echten Zeitraum, selectedDbDates ist auf exakte Import-Tage gesnappt.
                        dateRangeRaw: this.selectedDates,
                        selectedPlatform: this.selectedPlatform,
                        tags: this.selectedTags,
                        tagsOperator: this.tagOperator,
                        search: this.search
                    }
                }

                if (effectiveButton === "Opportunities") {
                    return {
                        projectId: this.project?._id,
                        project: this.project,
                        selectedDates: this.selectedDbDates,
                        // Rohe Picker-Daten zusaetzlich: die Prompt-Gap-Auswertung laeuft ueber
                        // den echten Zeitraum, selectedDbDates ist auf exakte Import-Tage gesnappt.
                        dateRangeRaw: this.selectedDates,
                        selectedPlatform: this.selectedPlatform,
                        tags: this.selectedTags,
                        tagsOperator: this.tagOperator,
                        search: this.search
                    }
                }

                return {
                    brands: this.brands,
                    sentimentAvailable: this.sentimentAvailable,
                    hasTags: this.availableTags.length > 0,
                    initialFilters: this.promptTableFilters,
                    initialFiltersReady: this.filtersReady,
                    tags: this.selectedTags,
                    availableTags: this.availableTags,
                    tagsOperator: this.tagOperator,
                    selectedPlatform: this.selectedPlatform,
                    search: this.search,
                    platforms,
                    platformItems: this.platformSelectItems
                        .filter((p) => p && p.key && p.key !== "all" && !p.disabled)
                        .map((p) => {
                            const info = this.getPlatformInfo(p.key)
                            return {
                                key: p.key,
                                label: info?.label || p.name || p.key,
                                logo: info?.logo || null
                            }
                        }),
                    brand: this.mentionBrand,
                    project: this.project,
                    responses: this.promptResponses,
                    loading: this.loadingPrompts,
                    availableDates: this.availableDates,
                    selectedDates: this.selectedDbDates
                }
            }
        },
        methods: {
            normalizeProjectSection(section) {
                const val = (section || "").toString().trim().toLowerCase()
                if (["prompts", "mentions", "brands", "sources", "shopping", "fanout", "opportunities"].includes(val)) return val
                return "dashboard"
            },
            activeButtonToSection(buttonName) {
                const key = (buttonName || "").toString().trim().toLowerCase()
                if (["prompts", "mentions", "brands", "sources", "shopping", "fanout", "opportunities"].includes(key)) return key
                if (key === "fanout queries") return "fanout"
                if (key === "opportunities") return "opportunities"
                return null
            },
            sectionToActiveButton(section) {
                const key = this.normalizeProjectSection(section)
                if (key === "dashboard") return null
                if (key === "fanout") return "Fanout Queries"
                if (key === "opportunities") return "Opportunities"
                return key.charAt(0).toUpperCase() + key.slice(1)
            },
            syncSectionFromRoute() {
                const section = this.normalizeProjectSection(this.$route?.params?.section)
                if (this.projectSection !== section) this.projectSection = section
                const nextButton = this.sectionToActiveButton(section)
                if (!nextButton) return
                if (this.activeButton === nextButton) return
                this._suppressSectionRouteSync = true
                this.activeButton = nextButton
                this.$nextTick(() => {
                    this._suppressSectionRouteSync = false
                })
            },
            onShoppingSwitchPlatform(platformKey) {
                const item = (this.platformSelectItems || []).find((p) => p && p.key === platformKey && !p.disabled)
                if (item) this.selectedPlatform = item
            },
            // Klick auf einen Prompt im Shopping-Tab -> PromptOverview als Overlay
            // mit dem Prompt und dem letzten Shopping-Datum als ausgewähltem Tag.
            onShoppingOpenPrompt(promptRow) {
                const projectId = this.project?._id || this.$route?.params?.id
                const prompt = promptRow?.prompt
                if (!projectId || !prompt) return

                const rawDate = promptRow.lastShoppingAt || promptRow.lastImportDate
                const parsed = rawDate ? new Date(rawDate) : null
                const dateKey = parsed && !isNaN(parsed) ? parsed.toISOString().slice(0, 10) : null

                this.openPromptOverlay({
                    project_id: projectId,
                    prompt,
                    platform: "chatgpt",
                    dates: dateKey ? JSON.stringify([dateKey, dateKey]) : undefined
                })
            },
            goToProjectSection(section) {
                const normalized = this.normalizeProjectSection(section)
                if (this.projectSection !== normalized) this.projectSection = normalized
                const nextButton = this.sectionToActiveButton(normalized)
                if (nextButton && this.activeButton !== nextButton) {
                    this.activeButton = nextButton
                }
                this.syncQueryFromState()
            },
            arraysEqual(a, b) {
                if (!Array.isArray(a) || !Array.isArray(b)) return false
                if (a.length !== b.length) return false
                for (let i = 0; i < a.length; i += 1) {
                    if (JSON.stringify(a[i]) !== JSON.stringify(b[i])) return false
                }
                return true
            },
            getEnabledPlatformKeys() {
                const items = Array.isArray(this.platformSelectItems) ? this.platformSelectItems : []
                const keys = items.filter((i) => i && i.key && i.key !== "all" && !i.disabled).map((i) => i.key)
                return Array.from(new Set(keys))
            },
            ensureBrandMetricsCache() {
                if (!(this._brandMetricsCache instanceof Map)) {
                    this._brandMetricsCache = new Map()
                }
            },
            ensureTagVisibilityCache() {
                if (!(this._tagVisibilityCache instanceof Map)) {
                    this._tagVisibilityCache = new Map()
                }
            },
            clearBrandMetricsCache() {
                this.ensureBrandMetricsCache()
                this.ensureTagVisibilityCache()
                this._brandMetricsCache.clear()
                this._tagVisibilityCache.clear()
                this.clearSeriesMemo()
            },
            triggerTagPerformanceRefresh(immediate = false) {
                if (this.matrixViewMode !== "tag") return
                if (this.selectedPlatform?.key !== "all") return
                this.ensureTagPerformanceBrandSelection()
                this.ensureTagPerformancePlatformSelection()
                this.ensureTagPerformanceTagSelection()
                this.ensureTagPerformanceCombineTagSelection()
                this.tagMatrixLoaded = false
                if (immediate) {
                    this.buildTagMetricsMatrix()
                } else {
                    this.debouncedBuildTagMetricsMatrix()
                }
            },
            async onTagPerformanceTabClick() {
                this.matrixViewMode = "tag"
                if (!this.matrixTagOrder.length && this.project?._id) {
                    await this.fetchAvailableTags()
                }

                this.ensureTagPerformanceBrandSelection()
                this.ensureTagPerformancePlatformSelection()
                this.triggerTagPerformanceRefresh(true)
            },
            onTagPerformanceControlsChange() {
                // Änderungen werden über die entsprechenden Watcher verarbeitet.
            },
            onTagPerformanceTagsCleared() {
                this._tagPerformanceTagsCleared = true
                this.tagPerformanceSelectedTags = []
            },
            onTagPerformanceTagsChanged(value) {
                if (Array.isArray(value) && value.length) {
                    this._tagPerformanceTagsCleared = false
                }
            },
            ensureTagPerformanceTagSelection() {
                const allTags = this.matrixTagOrder
                const current = Array.isArray(this.tagPerformanceSelectedTags) ? this.tagPerformanceSelectedTags : []
                if (!allTags.length) {
                    if (current.length) this.tagPerformanceSelectedTags = []
                    if (Array.isArray(this.tagPerformanceCombineWithTags) ? this.tagPerformanceCombineWithTags.length : true) {
                        this.tagPerformanceCombineWithTags = []
                    }
                    return
                }
                // Wenn der Nutzer per clearable explizit geleert hat, nicht automatisch vorbelegen.
                if (this._tagPerformanceTagsCleared && Array.isArray(this.tagPerformanceSelectedTags) && this.tagPerformanceSelectedTags.length === 0) {
                    this.ensureTagPerformanceCombineTagSelection()
                    return
                }
                const valid = current.filter((t) => allTags.includes(t))
                if (valid.length) {
                    if (!this.arraysEqual(current, valid)) this.tagPerformanceSelectedTags = valid
                    this.ensureTagPerformanceCombineTagSelection()
                    return
                }
                const fallback = allTags.slice(0, 6)
                if (!this.arraysEqual(current, fallback)) this.tagPerformanceSelectedTags = fallback
                this.ensureTagPerformanceCombineTagSelection()
            },
            ensureTagPerformanceCombineTagSelection() {
                const allTags = this.matrixTagOrder
                if (!allTags.length) {
                    if (Array.isArray(this.tagPerformanceCombineWithTags) ? this.tagPerformanceCombineWithTags.length : true) {
                        this.tagPerformanceCombineWithTags = []
                    }
                    return
                }
                const current = Array.isArray(this.tagPerformanceCombineWithTags) ? this.tagPerformanceCombineWithTags : []
                const valid = current.filter((t) => allTags.includes(t))
                if (!this.arraysEqual(current, valid)) this.tagPerformanceCombineWithTags = valid
            },
            getTagPerformanceEffectiveTags(tag) {
                const base = tag ? [tag] : []
                const comboTags = Array.isArray(this.tagPerformanceCombineWithTags) ? this.tagPerformanceCombineWithTags : []
                comboTags.forEach((comboTag) => {
                    if (comboTag && comboTag !== tag && !base.includes(comboTag)) base.push(comboTag)
                })
                return base.filter(Boolean)
            },
            getTagPerformanceEffectiveTagsOperator(tag) {
                const tags = this.getTagPerformanceEffectiveTags(tag)
                return tags.length > 1 ? "and" : this.tagOperator
            },
            tagPerformanceRowLabel(tag) {
                const combos = Array.isArray(this.tagPerformanceCombineWithTags) ? this.tagPerformanceCombineWithTags.filter((t) => t && t !== tag) : []
                if (combos.length) return `${tag} + ${combos.join(" + ")}`
                return tag
            },
            getBrandMetricsCacheKey(brandKey, platformKey, importDates = []) {
                const dateKey = Array.isArray(importDates) ? importDates.join("|") : ""
                const tagsKey = Array.isArray(this.selectedTags) ? this.selectedTags.join("|") : ""
                const operatorKey = this.tagOperator || "or"
                const modeKey = this.dashboardMode || "absolute"
                return [brandKey || "?", platformKey || "?", dateKey, operatorKey, tagsKey, modeKey].join("::")
            },
            getDefaultPlatformKey() {
                const firstEnabled = this.platformSelectItems.find((p) => !p.disabled) || this.platformSelectItems[0]
                return firstEnabled?.key || null
            },
            getDefaultDates() {
                const dates = Array.isArray(this.availableDates) ? this.availableDates.filter(Boolean) : []
                if (!dates.length) return []
                const end = dates[dates.length - 1]
                const endDate = new Date(end)
                if (isNaN(endDate)) return dates.slice(Math.max(0, dates.length - 2))
                const candidate = new Date(endDate)
                candidate.setDate(candidate.getDate() - (this.defaultDateRangeDays - 1))
                const candidateIso = candidate.toISOString().slice(0, 10)
                const start = dates.find((d) => d >= candidateIso) || dates[0]
                return start === end ? [start] : [start, end]
            },
            applyDateRangePreset(preset) {
                if (!preset) return
                const dates = Array.isArray(this.availableDates) ? this.availableDates.filter(Boolean) : []
                if (!dates.length) return
                const end = dates[dates.length - 1]
                let start
                if (!preset.days) {
                    start = dates[0]
                } else {
                    const endDate = new Date(end)
                    if (isNaN(endDate)) return
                    const candidate = new Date(endDate)
                    candidate.setDate(candidate.getDate() - (preset.days - 1))
                    const candidateIso = candidate.toISOString().slice(0, 10)
                    start = dates.find((d) => d >= candidateIso) || dates[0]
                }
                const next = start === end ? [start] : [start, end]
                this.selectedDates = next
                this.dateRangeTouched = true
                this.datepickerMenu = false
                if (this.project && this.project.brand) this.fetchBrandMetrics(this.project.brand)
                this.buildMetricsMatrix()
            },
            getValidSelectedDates(availableDates, currentDates) {
                const avail = Array.isArray(availableDates) ? availableDates.filter(Boolean) : []
                const current = Array.isArray(currentDates) ? currentDates.filter(Boolean) : []

                if (!current.length) return this.getDefaultDates()
                if (!avail.length) return current

                const allValid = current.every((d) => avail.includes(d))
                if (allValid) return current

                return this.getDefaultDates()
            },
            updateAvailableDates() {
                this.availableDates = this.computeAvailableDatesForSelection()
            },
            computeAvailableDatesForSelection() {
                if (!this.project || !this.selectedPlatform) return []
                const normalizeDay = (value) => {
                    const dt = new Date(value)
                    return isNaN(dt) ? null : dt.toISOString().slice(0, 10)
                }
                const collectDaysForPlatform = (platformKey) => {
                    const field = this.getPlatformDateField(platformKey)
                    const arr = Array.isArray(this.project?.[field]) ? this.project[field] : []
                    const days = arr.map((d) => normalizeDay(d)).filter(Boolean)
                    return Array.from(new Set(days)).sort()
                }
                if (this.selectedPlatform.key === "all") {
                    const activePlatforms = this.platformSelectItems.filter((item) => item.key && item.key !== "all" && !item.disabled).map((item) => item.key)
                    if (!activePlatforms.length) return []
                    const platformDayArrays = activePlatforms.map((key) => collectDaysForPlatform(key)).filter((arr) => Array.isArray(arr) && arr.length)
                    if (!platformDayArrays.length) return []

                    const unionDays = Array.from(new Set(platformDayArrays.flat())).sort()
                    const latestPerPlatform = platformDayArrays
                        .map((arr) => arr[arr.length - 1])
                        .filter(Boolean)
                        .sort()
                    const slowestLatest = latestPerPlatform[0]
                    if (!slowestLatest) return unionDays

                    // const trimmed = unionDays.filter((day) => day <= slowestLatest)
                    const trimmed = unionDays.filter((day) => day <= slowestLatest)
                    return trimmed.length ? trimmed : unionDays
                }
                return collectDaysForPlatform(this.selectedPlatform.key)
            },
            isReturningFromPromptDetail(projectId) {
                if (!projectId) return false
                try {
                    return sessionStorage.getItem(`ai_suite_project_return_${projectId}`) === "1"
                } catch (e) {
                    return false
                }
            },
            isForceReloadNeeded(projectId) {
                if (!projectId) return false
                try {
                    return sessionStorage.getItem("ai_suite_project_force_reload_all") === "1" || sessionStorage.getItem(`ai_suite_project_force_reload_${projectId}`) === "1"
                } catch (e) {
                    return false
                }
            },
            setReturnFromPromptDetail(projectId) {
                if (!projectId) return
                try {
                    sessionStorage.setItem(`ai_suite_project_return_${projectId}`, "1")
                } catch (e) {
                    // ignore storage errors
                }
            },
            clearReturnFromPromptDetail(projectId) {
                if (!projectId) return
                try {
                    sessionStorage.removeItem(`ai_suite_project_return_${projectId}`)
                } catch (e) {
                    // ignore storage errors
                }
            },
            setForceReloadFlag(projectId) {
                if (!projectId) return
                try {
                    sessionStorage.setItem(`ai_suite_project_force_reload_${projectId}`, "1")
                } catch (e) {
                    // ignore storage errors
                }
            },
            clearForceReloadFlag(projectId) {
                if (!projectId) return
                try {
                    sessionStorage.removeItem("ai_suite_project_force_reload_all")
                    sessionStorage.removeItem(`ai_suite_project_force_reload_${projectId}`)
                } catch (e) {
                    // ignore storage errors
                }
            },
            startFreshProjectLoad(projectId) {
                this.clearBrandMetricsCache()
                this.filtersReady = false
                this.loading = true
                this.project = null
                this.brands = []
                this.tableBrands = []
                this.promptResponses = []
                this.mentionBrand = {}
                this.selectedBrands = [{}]
                this.loadingPrompts = false
                this.loadingBrands = false
                this.resetAiSuiteViewState()
                this.syncQueryFromState()
                this.fetchData(projectId)
            },
            resetAiSuiteViewState() {
                this.tagOperator = "and"
                this.selectedTags = []
                this.combinableTags = null
                this._tagPerformanceTagsCleared = false
                this.promptTableFilters = []
                this.mentionTableFilters = []
                this.activeButton = "Prompts"
                this.dateRangeTouched = false
                const defaultKey = this.getDefaultPlatformKey()
                if (!this.selectedPlatform || this.selectedPlatform.key !== defaultKey) {
                    const match = this.platformSelectItems.find((p) => p.key === defaultKey)
                    if (match) this.selectedPlatform = match
                }
                const defaultDates = this.getDefaultDates()
                if (!this.arraysEqual(this.selectedDates, defaultDates)) {
                    this.selectedDates = defaultDates
                }
            },
            encodeStateToQuery(state) {
                try {
                    const json = JSON.stringify(state || {})
                    const utf8 = encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode("0x" + p1))
                    return btoa(utf8).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
                } catch (e) {
                    return ""
                }
            },
            decodeStateFromQuery(encoded) {
                if (!encoded) return null
                try {
                    const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/")
                    const padded = base64 + "===".slice((base64.length + 3) % 4)
                    const bin = atob(padded)
                    const utf8 = Array.prototype.map.call(bin, (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join("")
                    return JSON.parse(decodeURIComponent(utf8))
                } catch (e) {
                    return null
                }
            },
            parseQueryArray(value) {
                if (!value) return []
                try {
                    const parsed = JSON.parse(value)
                    return Array.isArray(parsed) ? parsed : []
                } catch (e) {
                    return String(value)
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean)
                }
            },
            buildQueryFromState() {
                const payload = {}
                if (Array.isArray(this.selectedTags) && this.selectedTags.length) payload.t = this.selectedTags
                if (this.tagOperator && this.tagOperator !== "and") payload.o = this.tagOperator
                const defaultPlatformKey = this.getDefaultPlatformKey()
                if (this.selectedPlatform && this.selectedPlatform.key && this.selectedPlatform.key !== defaultPlatformKey) payload.p = this.selectedPlatform.key
                const defaultDates = this.getDefaultDates()
                if (Array.isArray(this.selectedDates) && this.selectedDates.length && !this.arraysEqual(this.selectedDates, defaultDates)) payload.d = this.selectedDates
                const sectionForQuery = this.currentProjectSection
                if (sectionForQuery && sectionForQuery !== "dashboard") {
                    const sectionButton = this.sectionToActiveButton(sectionForQuery)
                    if (sectionButton) payload.a = sectionButton
                }
                if (Array.isArray(this.promptTableFilters) && this.promptTableFilters.length) payload.f = this.promptTableFilters
                if (Array.isArray(this.mentionTableFilters) && this.mentionTableFilters.length) payload.m = this.mentionTableFilters

                if (!Object.keys(payload).length) return {}
                const encoded = this.encodeStateToQuery(payload)
                return encoded ? { s: encoded } : {}
            },
            setPageScrollLock(locked) {
                try {
                    const el = document.documentElement
                    if (locked) {
                        this._prevHtmlOverflow = el.style.overflow
                        el.style.overflow = "hidden"
                    } else {
                        el.style.overflow = this._prevHtmlOverflow || ""
                    }
                } catch (e) {
                    // ignore
                }
            },
            openPromptOverlay(params) {
                if (!params) return
                const payload = { ...params }
                if (!payload.project_id) payload.project_id = this.project?._id || this.$route?.params?.id
                this.promptOverlayParams = payload
                this.promptOverlayOpen = true
                // push statt replace: eigener History-Eintrag, damit Browser-Zurück erst das Overlay schließt
                this._promptOverlayPushed = true
                this.syncPromptOverlayQuery(payload, { push: true })
            },
            syncPromptOverlayQuery(payload, { push = false } = {}) {
                // pd-Query-Param für Deep-Links setzen bzw. entfernen
                try {
                    const currentQuery = { ...(this.$route?.query || {}) }
                    const encoded = payload ? this.encodeStateToQuery(payload) : ""
                    if (encoded) {
                        currentQuery.pd = encoded
                    } else {
                        delete currentQuery.pd
                    }
                    if ((this.$route?.query?.pd || "") === (currentQuery.pd || "")) return
                    this._skipNextQueryApply = true
                    const navigate = push ? this.$router.push : this.$router.replace
                    navigate.call(this.$router, { query: currentQuery }).catch(() => {})
                } catch (e) {
                    // ignore
                }
            },
            openPromptOverlayFromQuery() {
                // Deep-Link: ?pd=<encoded> öffnet das Prompt-Detail-Overlay direkt
                const encoded = this.$route?.query?.pd
                if (!encoded || this.promptOverlayOpen) return
                const params = this.decodeStateFromQuery(encoded)
                if (!params || typeof params !== "object") return
                if (!params.project_id) params.project_id = this.project?._id || this.$route?.params?.id
                this.promptOverlayParams = params
                this._promptOverlayPushed = false
                this.promptOverlayOpen = true
            },
            syncPromptOverlayFromRoute(query) {
                // Hält das Overlay synchron zum pd-Param, wenn die History navigiert wird
                const encoded = query && query.pd
                if (encoded && !this.promptOverlayOpen) {
                    const params = this.decodeStateFromQuery(encoded)
                    if (!params || typeof params !== "object") return
                    if (!params.project_id) params.project_id = this.project?._id || this.$route?.params?.id
                    this.promptOverlayParams = params
                    // Vorwärts-Navigation: der pd-Eintrag liegt bereits in der History
                    this._promptOverlayPushed = true
                    this.promptOverlayOpen = true
                } else if (!encoded && this.promptOverlayOpen) {
                    // Browser-Zurück hat den pd-Eintrag verlassen -> nur noch das Overlay schließen
                    this._promptOverlayPushed = false
                    this.promptOverlayOpen = false
                }
            },
            syncQueryFromState() {
                if (this._suppressUrlSync) return
                const path = this.$route?.path || ""
                if (!path.startsWith("/ai-suite/project/")) return
                const nextQuery = this.buildQueryFromState()
                if (this.$route?.query?.pd) nextQuery.pd = this.$route.query.pd
                const currentQuery = this.$route?.query || {}
                if (JSON.stringify(currentQuery) === JSON.stringify(nextQuery)) return
                this._suppressUrlSync = true
                this._skipNextQueryApply = true
                this.$router
                    .replace({ query: nextQuery })
                    .finally(() => {
                        this._suppressUrlSync = false
                    })
                    .catch(() => {
                        this._suppressUrlSync = false
                    })
            },
            applyQueryToState(query = {}) {
                if (this._suppressUrlSync) return
                this._suppressUrlSync = true

                const defaultPlatform = this.platformSelectItems.find((p) => !p.disabled) || this.platformSelectItems[0]

                const decoded = this.decodeStateFromQuery(query.s)
                const source =
                    decoded && typeof decoded === "object"
                        ? decoded
                        : {
                              t: this.parseQueryArray(query.tags),
                              d: this.parseQueryArray(query.dates),
                              f: this.parseQueryArray(query.pf),
                              m: this.parseQueryArray(query.mf),
                              o: query.tagOp,
                              p: query.platform,
                              a: query.tab
                          }

                const tags = Array.isArray(source.t) ? source.t : []
                const dates = Array.isArray(source.d) ? source.d : []
                const promptFilters = Array.isArray(source.f) ? source.f : []
                const mentionFilters = Array.isArray(source.m) ? source.m : []

                const nextTagOperator = source.o || "and"
                if (this.tagOperator !== nextTagOperator) this.tagOperator = nextTagOperator

                if (!this.arraysEqual(this.selectedTags, tags)) this.selectedTags = tags

                this._suppressPlatformWatcher = true
                const platformKey = source.p
                const match = platformKey ? this.platformSelectItems.find((p) => p.key === platformKey && !p.disabled) : null
                const nextPlatform = match || defaultPlatform
                if (!this.selectedPlatform || this.selectedPlatform.key !== nextPlatform.key) {
                    this.selectedPlatform = nextPlatform
                }
                this.updateAvailableDates()
                this._suppressPlatformWatcher = false

                const nextDates = this.getValidSelectedDates(this.availableDates, dates.length ? dates : this.getDefaultDates())
                if (!this.arraysEqual(this.selectedDates, nextDates)) this.selectedDates = nextDates
                this.dateRangeTouched = dates.length > 0

                const sectionTab = this.sectionToActiveButton(this.currentProjectSection)
                const nextTab = sectionTab || source.a || "Prompts"
                if (this.activeButton !== nextTab) this.activeButton = nextTab
                const routeSection = this.normalizeProjectSection(this.$route?.params?.section)
                const querySection = this.activeButtonToSection(source.a)
                if (routeSection === "dashboard" && querySection && this.projectSection !== querySection) {
                    this.projectSection = querySection
                }

                if (!this.arraysEqual(this.promptTableFilters, promptFilters)) this.promptTableFilters = promptFilters
                if (!this.arraysEqual(this.mentionTableFilters, mentionFilters)) this.mentionTableFilters = mentionFilters

                this.$nextTick(() => {
                    this._suppressUrlSync = false
                })
            },
            onPromptFiltersUpdated(filters) {
                this.promptTableFilters = Array.isArray(filters) ? filters : []
            },
            onMentionFiltersUpdated(filters) {
                this.mentionTableFilters = Array.isArray(filters) ? filters : []
            },
            onActiveTableFiltersUpdated(filters) {
                const effectiveButton = this.sectionToActiveButton(this.currentProjectSection) || this.activeButton
                if (effectiveButton === "Mentions") {
                    this.onMentionFiltersUpdated(filters)
                    return
                }
                this.onPromptFiltersUpdated(filters)
            },
            onMentionsDeltaMetrics(payload) {
                const safe = payload && typeof payload === "object" ? payload : {}
                this.mentionsDeltaMetrics = {
                    total: Number.isFinite(Number(safe.total)) ? Number(safe.total) : 0,
                    newCount: Number.isFinite(Number(safe.newCount)) ? Number(safe.newCount) : 0,
                    lostCount: Number.isFinite(Number(safe.lostCount)) ? Number(safe.lostCount) : 0,
                    loading: Boolean(safe.loading)
                }
                if (!safe.loading && typeof safe.signature === "string" && safe.signature) {
                    this.mentionsDeltaCacheSignature = safe.signature
                }
            },
            onTopBrandsLoaded(payload) {
                const safe = payload && typeof payload === "object" ? payload : {}
                if (typeof safe.signature === "string" && safe.signature) {
                    this.topBrandsCacheSignature = safe.signature
                }
                if (safe.byPlatform && typeof safe.byPlatform === "object") {
                    this.topBrandsCacheByPlatform = { ...safe.byPlatform }
                }
            },
            onSourcesPageTypeLoaded(payload) {
                const safe = payload && typeof payload === "object" ? payload : {}
                if (typeof safe.signature === "string" && safe.signature) {
                    this.sourcesPageTypeCacheSignature = safe.signature
                }
                if (Array.isArray(safe.statsRaw)) {
                    this.sourcesPageTypeCacheRaw = [...safe.statsRaw]
                }
            },
            onMentionsPlatformOverviewUpdated(rows) {
                this.mentionsPlatformOverviewRows = Array.isArray(rows) ? rows : []
                if (!this.mentionsPlatformFilterKey) return
                const exists = this.mentionsPlatformOverviewRows.some((r) => r && r.key === this.mentionsPlatformFilterKey)
                if (!exists) this.clearMentionsPlatformFilter()
            },
            isMentionsMetricActive(platformKey, metricKey) {
                return this.mentionsPlatformFilterKey === platformKey && this.mentionsPlatformFilterMetric === metricKey
            },
            clearMentionsPlatformFilter() {
                this.mentionsPlatformFilterKey = null
                this.mentionsPlatformFilterMetric = null
            },
            onMentionsSubTabClick(key) {
                if (this.mentionsSubTab === key) return
                this.mentionsSubTab = key
                this.clearMentionsPlatformFilter()
            },
            scrollToProjectTable() {
                this.$nextTick(() => {
                    const target = document.getElementById("projects-tool-bar")
                    if (!target) return
                    target.scrollIntoView({ behavior: "smooth", block: "start" })
                })
            },
            onMentionsPlatformMetricClick(platformKey, metricKey) {
                const sameSelection = this.mentionsPlatformFilterKey === platformKey && this.mentionsPlatformFilterMetric === metricKey
                if (sameSelection) {
                    this.clearMentionsPlatformFilter()
                    this.scrollToProjectTable()
                    return
                }
                this.mentionsPlatformFilterKey = platformKey
                this.mentionsPlatformFilterMetric = metricKey
                this.mentionsSubTab = metricKey
                this.scrollToProjectTable()
            },
            shouldClearQueryOnLeave(to) {
                const toPath = (to && typeof to.path === "string" ? to.path : "") || ""
                if (toPath.startsWith("/ai-suite/project/")) return false
                if (toPath.startsWith("/ai-suite/prompt-detail/")) return false
                return true
            },
            saveProjectScrollPosition(projectId) {
                if (!projectId) return
                try {
                    const key = `ai_suite_project_scroll_${projectId}`
                    const restoreKey = `ai_suite_project_scroll_restore_${projectId}`
                    const container = document.getElementById("content") || document.scrollingElement || document.documentElement
                    const y = container ? container.scrollTop : typeof window !== "undefined" ? window.scrollY || window.pageYOffset || 0 : 0
                    sessionStorage.setItem(key, String(y))
                    sessionStorage.setItem(restoreKey, "1")
                } catch (e) {
                    // ignore storage errors
                }
            },
            restoreProjectScrollPosition(projectId) {
                if (!projectId) return
                try {
                    const key = `ai_suite_project_scroll_${projectId}`
                    const restoreKey = `ai_suite_project_scroll_restore_${projectId}`
                    const shouldRestore = sessionStorage.getItem(restoreKey) === "1"
                    if (!shouldRestore) return
                    const raw = sessionStorage.getItem(key)
                    const y = raw != null ? Number(raw) : null
                    if (!Number.isFinite(y)) return
                    const restore = () => {
                        const container = document.getElementById("content") || document.scrollingElement || document.documentElement
                        if (container) {
                            container.scrollTop = y
                        } else if (typeof window !== "undefined") {
                            window.scrollTo({ top: y, behavior: "auto" })
                        }
                    }
                    this.$nextTick(() => {
                        setTimeout(() => {
                            restore()
                            requestAnimationFrame(restore)
                        }, 0)
                    })
                    sessionStorage.removeItem(restoreKey)
                } catch (e) {
                    // ignore storage errors
                }
            },
            // Plattform wurde im Settings-Dialog serverseitig gelöscht (inkl. ihrer Antworten) -> Projekt lokal anpassen.
            onPlatformRemoved(key) {
                if (!this.project) return
                if (key === "google") this.$set(this.project, "google", false)
                else this.$set(this.project, "platforms", (this.project.platforms || []).filter((p) => p !== key))
                // war die gelöschte Plattform gerade ausgewählt, auf "All Platforms" zurückfallen
                if (this.selectedPlatform && this.selectedPlatform.key === key) this.selectedPlatform = this.platformSelectItems.find((p) => p.key === "all") || this.platformSelectItems[0]
            },
            async onProjectSettingsSave({ title, brandName, brandDomain, platforms, interval }) {
                if (!this.project?._id) return

                try {
                    this.savingProjectSettings = true
                    // Intervall zuerst: reichen die Credits nicht, bleibt der Dialog offen und nichts ist gespeichert.
                    if (interval) {
                        const intervalResult = await updateProjectInterval({
                            projectId: this.project._id,
                            interval,
                            axiosPost: this.$helpers.axiosPost.bind(this.$helpers),
                            cryptoJS: this.CryptoJS
                        })
                        if (intervalResult.error) {
                            const notEnough = intervalResult.error === "not_enough_credits"
                            const importRunning = intervalResult.error === "import_in_progress"
                            const message = notEnough
                                ? buildNotEnoughCreditsMessage({
                                      requested: projectCreditUsage(this.project, interval).total - projectCreditUsage(this.project).total,
                                      unit: "prompt updates",
                                      responseData: intervalResult
                                  })
                                : importRunning
                                ? importInProgressMessage(intervalResult.import_type)
                                : "Error saving the update interval"
                            this.createNotification(message, "mdi-alert-circle-outline", notEnough || importRunning ? "#FCBA03" : "error")
                            return
                        }
                        this.$set(this.project, "interval", interval)
                    }
                    const { addedPlatforms, baseUrl } = await saveAiProjectSettings({
                        companyIdEncrypted: localStorage.company_id,
                        project: this.project,
                        title,
                        brandName,
                        brandDomain,
                        currentPlatforms: this.project.platforms || [],
                        selectedPlatforms: platforms || [],
                        axiosPost: this.$helpers.axiosPost.bind(this.$helpers),
                        cryptoJS: this.CryptoJS,
                        includePlatformsInUpdate: false,
                        caseInsensitive: true
                    })
                    // lokal aktualisieren
                    this.project.title = title
                    this.project.brand = this.project.brand || {}
                    this.project.brand.brand_name = brandName
                    this.project.brand.brand_domain = brandDomain
                    // mit neuen Plattformen meldet erst der Plattform-Block das Ergebnis (Erfolg oder Sperre)
                    if (!addedPlatforms.length) this.createNotification("Project settings saved", "mdi-check-circle", "primary")

                    console.log("ADDEDPLATFORM", addedPlatforms)
                    if (addedPlatforms.length) {
                        const response_data = await requestAddPlatformsUpdate({
                            baseUrl,
                            companyIdEncrypted: localStorage.company_id,
                            projectId: this.project._id,
                            socketId: this.$socket.id,
                            newPlatforms: addedPlatforms,
                            axiosPost: this.$helpers.axiosPost.bind(this.$helpers)
                        })
                        console.log("RES", response_data?.data)
                        if (response_data?.data?.error === "import_in_progress") {
                            this.createNotification(importInProgressMessage(response_data.data.import_type), "mdi-alert-circle-outline", "#FCBA03")
                            return
                        }
                        if (response_data?.data?.setup_unfinished) {
                            this.createNotification(response_data?.data?.message, "mdi-alert-circle-outline", "#FCBA03")
                            return
                        }
                        const message = buildCreditsNotificationMessage({ project: this.project, responseData: response_data, addedPlatformsCount: addedPlatforms.length })
                        if (message) {
                            this.createNotification(message, "mdi-alert-circle-outline", "#FCBA03")
                            return
                        }
                        const merged = Array.from(new Set([...(this.project.platforms || []), ...addedPlatforms]))
                        this.$set(this.project, "platforms", merged)
                        const addedNames = addedPlatforms.map((k) => this.getPlatformInfo(k)?.label || k).join(", ")
                        // pausiert: die API trägt die Plattform ein, importiert und bucht sie aber erst beim Fortsetzen
                        const followUp = response_data?.data?.paused ? "It will be imported and use credits once you resume the project." : "Updating will take a few minutes..."
                        this.createNotification(`Added platform${addedPlatforms.length > 1 ? "s" : ""}: ${addedNames}. ${followUp}`, "mdi-check-circle", "primary")
                    }
                    this.showProjectSettings = false
                } catch (e) {
                    console.error("saveProjectSettings error", e)
                    this.createNotification("Error saving project settings", "mdi-alert-circle-outline", "error")
                } finally {
                    this.savingProjectSettings = false
                }
            },

            filterTags(item, queryText, itemText) {
                // NEU: benutzerdefinierter Filter
                if (!queryText) return true
                const q = queryText.toLowerCase().trim()
                const text = (itemText || item.label || "").toLowerCase()
                const idStr = String(item.id ?? "").toLowerCase()
                // match: label enthält q ODER id enthält q
                return text.includes(q) || idStr.includes(q)
            },
            removeTag(item) {
                const val = item && (item.id != null ? item.id : item)
                if (val == null) return
                const idx = (this.selectedTags || []).indexOf(val)
                if (idx > -1) this.selectedTags.splice(idx, 1)
            },
            resetSelectedTagsNoData() {
                this.selectedTags = []
                this.tagSearch = ""
                this.tagSettingsMenu = false
                this.combinableTags = null
                if (this.tagOperator !== "and") this.tagOperator = "and"
            },
            removeTagPerformanceTag(item) {
                const val = item && (item.id != null ? item.id : item)
                if (val == null) return
                const idx = (this.tagPerformanceSelectedTags || []).indexOf(val)
                if (idx > -1) this.tagPerformanceSelectedTags.splice(idx, 1)
                this.onTagPerformanceControlsChange()
            },
            removeTagPerformanceCombineTag(item) {
                const val = item && (item.id != null ? item.id : item)
                if (val == null) return
                const idx = (this.tagPerformanceCombineWithTags || []).indexOf(val)
                if (idx > -1) this.tagPerformanceCombineWithTags.splice(idx, 1)
                this.onTagPerformanceControlsChange()
            },
            tagColor(id) {
                const s = 68,
                    l = 42
                const h = this._hashString(String(id)) % 360
                return `hsl(${h}, ${s}%, ${l}%)`
            },
            tagChipStyle(id) {
                const bg = this.tagColor(id)
                return {
                    background: bg,
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.25)"
                }
            },
            tagDotStyle(id) {
                const bg = this.tagColor(id)
                return { background: bg }
            },
            _hashString(str) {
                let h = 0
                for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0
                return Math.abs(h)
            },
            brandCanonical(key) {
                const obj = this.getBrandByKey(key)
                return obj?.canonicalName || key
            },
            brandFavicon(key) {
                const obj = this.getBrandByKey(key)
                if (!obj) return null
                return this.getBrandFaviconDomain(obj)
            },
            getBrandByKey(key) {
                if (!key) return null
                const fromAllBrands = (this.brands || []).find((b) => this.brandKey(b) === key)
                if (fromAllBrands) return fromAllBrands
                return (this.selectedBrands || []).find((b) => this.brandKey(b) === key) || null
            },
            async fetchGooglePromptCount(project_id) {
                const url =
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/get/google/promptcount"
                const params = {
                    company_id: localStorage.company_id,
                    project_id,
                    importDates: this.project[this.getPlatformDateField("google")] || []
                }
                const response = await this.$helpers.axiosPost(url, params)
                this.googlePromptCount = response.data.promptCount || 0
                // totalPromptsCount recalculates automatisch (computed)
            },
            brandKey(b) {
                if (!b || typeof b !== "object") return null
                return b._id || b.id || (b.canonicalName && b.canonicalName.toLowerCase()) || (Array.isArray(b.mentions) && b.mentions.length && b.mentions[0].toLowerCase()) || null
            },
            async buildMetricsMatrix() {
                // Koaleszierung: Wenn Build läuft, nur pending markieren
                if (this._mmBuilding) {
                    this._mmPending = true
                    return
                }
                this._mmBuilding = true
                this.matrixLoaded = false
                const runId = ++this._mmRunId
                if (!this.project) {
                    this._mmBuilding = false
                    return
                }
                const platforms = this.platformSelectItems.filter((p) => !p.disabled && p.key !== "all")
                const brands = (this.selectedBrands || []).map((b) => ({ b, key: this.brandKey(b) })).filter((x) => x.key)
                const rankingBrandsSource = Array.isArray(this.brands) && this.brands.length ? this.brands : this.selectedBrands || []
                const rankingBrands = rankingBrandsSource
                    .map((b) => ({ b, key: this.brandKey(b) }))
                    .filter((x) => x.key)
                    .reduce((acc, cur) => {
                        if (!acc.some((e) => e.key === cur.key)) acc.push(cur)
                        return acc
                    }, [])
                    .map((x) => x.b)

                if (!brands.length) {
                    this.metricsMatrix = {}
                    this.matrixVersion++
                    this.$nextTick(() => (this.matrixLoaded = true))
                    this._mmBuilding = false
                    if (this._mmPending) {
                        this._mmPending = false
                        this.debouncedBuildMetricsMatrix()
                    }
                    return
                }

                const newMatrix = {}
                try {
                    // Performance: alle Plattformen in einer Anfrage vorholen (abschaltbar)
                    await this.prefetchPlatformSeriesBatch(platforms, rankingBrands)
                    await Promise.all(
                        platforms.map(async (platform) => {
                            const pKey = platform.key
                            newMatrix[pKey] = {}
                            const importDates = this.getPlatformDateField(pKey, true)
                            const brandsByDate = await this.fetchPlatformBrandSeriesForMode(pKey, importDates, null, rankingBrands)
                            await Promise.all(
                                brands.map(async ({ b, key }) => {
                                    const metrics = await this.fetchBrandMetricsForMatrix(b, pKey, importDates, brandsByDate)
                                    newMatrix[pKey][key] = metrics
                                })
                            )
                        })
                    )
                    // if (runId !== this._mmRunId) return
                    this.metricsMatrix = newMatrix
                    this.matrixVersion++
                    this.$nextTick(() => (this.matrixLoaded = true))
                } finally {
                    this._mmBuilding = false
                    if (this._mmPending) {
                        this._mmPending = false
                        this.debouncedBuildMetricsMatrix()
                    }
                }
            },
            async buildTagMetricsMatrix() {
                if (this._tagMmBuilding) {
                    this._tagMmPending = true
                    return
                }
                this._tagMmBuilding = true
                this.tagMatrixLoaded = false
                ++this._tagMmRunId
                if (!this.project) {
                    this._tagMmBuilding = false
                    return
                }

                const platforms = this.platformSelectItems.filter((p) => !p.disabled && p.key !== "all")
                this.ensureTagPerformanceBrandSelection()
                this.ensureTagPerformancePlatformSelection()
                this.ensureTagPerformanceTagSelection()
                const hasExplicitTagClear = this._tagPerformanceTagsCleared && Array.isArray(this.tagPerformanceSelectedTags) && this.tagPerformanceSelectedTags.length === 0
                const tags = hasExplicitTagClear
                    ? []
                    : this.matrixTagOrderVisible.length > 0
                    ? this.matrixTagOrderVisible
                    : Array.isArray(this.selectedTags) && this.selectedTags.length
                    ? this.selectedTags.slice(0, 6)
                    : this.matrixTagOrder.slice(0, 6)
                const selectedPlatformSet = new Set((this.tagPerformancePlatforms || []).filter(Boolean))
                const effectivePlatforms = platforms.filter((p) => selectedPlatformSet.has(p.key))
                const allBrandsForTagPerformance = (this.brands || []).filter((b) => this.brandKey(b))

                const tagBrandKeysSet = new Set((this.tagPerformanceBrandKeys || []).filter(Boolean))
                let selectedTagBrands = (this.brands || []).map((b) => ({ key: this.brandKey(b), brand: b })).filter((x) => x.key && tagBrandKeysSet.has(x.key))
                if (!selectedTagBrands.length) {
                    const fallbackBrand = (this.brands || []).find((b) => this.brandKey(b)) || null
                    if (fallbackBrand) {
                        const key = this.brandKey(fallbackBrand)
                        this.tagPerformanceBrandKeys = key ? [key] : []
                        selectedTagBrands = key ? [{ key, brand: fallbackBrand }] : []
                    }
                }
                if (!selectedTagBrands.length && this.project?.brand?.brand_name) {
                    const fallbackBrand = {
                        canonicalName: this.project.brand.brand_name,
                        brand_name: this.project.brand.brand_name,
                        brand_domain: this.project?.brand?.brand_domain || null,
                        mentions: [this.project.brand.brand_name]
                    }
                    const fallbackKey = this.brandKey(fallbackBrand) || (fallbackBrand.canonicalName || "").toLowerCase()
                    selectedTagBrands = fallbackKey
                        ? [
                              {
                                  key: fallbackKey,
                                  brand: fallbackBrand
                              }
                          ]
                        : []
                }

                if (!selectedTagBrands.length || !tags.length || !effectivePlatforms.length) {
                    this.tagMetricsMatrix = {}
                    this.tagMatrixVersion++
                    this.$nextTick(() => (this.tagMatrixLoaded = true))
                    this._tagMmBuilding = false
                    if (this._tagMmPending) {
                        this._tagMmPending = false
                        this.debouncedBuildTagMetricsMatrix()
                    }
                    return
                }

                const tagMatrix = {}
                try {
                    this.ensureTagVisibilityCache()
                    await Promise.all(
                        effectivePlatforms.map(async (platform) => {
                            const pKey = platform.key
                            const importDates = this.getPlatformDateField(pKey, true)
                            tagMatrix[pKey] = {}

                            await Promise.all(
                                tags.map(async (tag) => {
                                    const effectiveTags = this.getTagPerformanceEffectiveTags(tag)
                                    const effectiveTagsOperator = this.getTagPerformanceEffectiveTagsOperator(tag)
                                    const availabilityKey = JSON.stringify({
                                        pKey,
                                        tag,
                                        combineWith: [...(this.tagPerformanceCombineWithTags || [])].sort(),
                                        tags: [...effectiveTags].sort(),
                                        tagsOperator: effectiveTagsOperator,
                                        importDates,
                                        availabilityOnly: true
                                    })
                                    let allSeries = this._tagVisibilityCache.get(availabilityKey)
                                    if (!allSeries) {
                                        allSeries = await this.fetchPlatformBrandSeriesForMode(pKey, importDates, effectiveTags, allBrandsForTagPerformance, effectiveTagsOperator)
                                        this._tagVisibilityCache.set(availabilityKey, allSeries)
                                    }
                                    const latest = allSeries[allSeries.length - 1] || []
                                    const hasAnyBrands = Array.isArray(allSeries) && allSeries.some((arr) => Array.isArray(arr) && arr.length > 0)

                                    if (!hasAnyBrands) {
                                        tagMatrix[pKey][tag] = {}
                                        selectedTagBrands.forEach(({ key }) => {
                                            tagMatrix[pKey][tag][key] = {
                                                visibility: null,
                                                relevanceRank: null,
                                                sourceReferences: null,
                                                sentiment: null,
                                                noData: true
                                            }
                                        })
                                    } else {
                                        tagMatrix[pKey][tag] = {}
                                        selectedTagBrands.forEach(({ key, brand }) => {
                                            const metrics = this.computeAggregateMetricsForSelectedBrands(latest, [brand])
                                            if (metrics.foundBrand) {
                                                const visibilityValue = typeof metrics.visibility === "number" ? metrics.visibility : null
                                                tagMatrix[pKey][tag][key] = {
                                                    visibility: visibilityValue,
                                                    relevanceRank: visibilityValue > 0 ? metrics.relevanceRank : null,
                                                    sourceReferences: metrics.sourceReferences,
                                                    sentiment: metrics.sentiment,
                                                    noData: false
                                                }
                                            } else {
                                                // Kombination hat Daten, aber die ausgewählte Brand kommt nicht vor -> 0 anzeigen
                                                tagMatrix[pKey][tag][key] = {
                                                    visibility: 0,
                                                    relevanceRank: null,
                                                    sourceReferences: 0,
                                                    sentiment: null,
                                                    noData: false
                                                }
                                            }
                                        })
                                    }
                                })
                            )
                        })
                    )

                    this.tagMetricsMatrix = tagMatrix
                    this.tagMatrixVersion++
                    this.matrixVersion++
                    this.$nextTick(() => (this.tagMatrixLoaded = true))
                } finally {
                    this._tagMmBuilding = false
                    if (this._tagMmPending) {
                        this._tagMmPending = false
                        this.debouncedBuildTagMetricsMatrix()
                    }
                }
            },
            matrixCellData(platformKey, colKey) {
                if (this.matrixViewMode === "tag") {
                    return this.tagMetricsMatrix?.[platformKey]?.[colKey] || null
                }
                return this.metricsMatrix?.[platformKey]?.[colKey] || null
            },
            getTagMatrixCell(tagKey, column) {
                if (!column || !column.platformKey || !column.brandKey) return null
                return this.tagMetricsMatrix?.[column.platformKey]?.[tagKey]?.[column.brandKey] || null
            },
            getMatrixCell(rowKey, col) {
                if (this.matrixViewMode === "brand") {
                    return this.matrixCellData(rowKey, col)
                }
                return this.getTagMatrixCell(rowKey, col)
            },
            ensureTagPerformanceBrandSelection() {
                const options = this.tagPerformanceBrandOptions || []
                if (!options.length) {
                    this.tagPerformanceBrandKeys = []
                    return
                }

                const optionKeys = new Set(options.map((o) => o.key))
                const current = Array.isArray(this.tagPerformanceBrandKeys) ? this.tagPerformanceBrandKeys.filter((k) => optionKeys.has(k)) : []
                if (current.length) {
                    if (!this.arraysEqual(current, this.tagPerformanceBrandKeys || [])) this.tagPerformanceBrandKeys = current
                    return
                }

                const projectBrandName = (this.project?.brand?.brand_name || "").toLowerCase().trim()
                const projectMatch = (this.brands || []).find((b) => {
                    const canonical = (b?.canonicalName || b?.brand_name || "").toLowerCase()
                    if (projectBrandName && canonical === projectBrandName) return true
                    const mentions = Array.isArray(b?.mentions) ? b.mentions.map((m) => (m || "").toLowerCase()) : []
                    return projectBrandName && mentions.includes(projectBrandName)
                })
                this.tagPerformanceBrandKeys = [this.brandKey(projectMatch) || options[0].key].filter(Boolean)
            },
            ensureTagPerformancePlatformSelection() {
                const options = this.tagPerformancePlatformOptions || []
                if (!options.length) {
                    this.tagPerformancePlatforms = []
                    return
                }
                const optionKeys = new Set(options.map((o) => o.key))
                const current = Array.isArray(this.tagPerformancePlatforms) ? this.tagPerformancePlatforms.filter((k) => optionKeys.has(k)) : []
                if (current.length) {
                    if (!this.arraysEqual(current, this.tagPerformancePlatforms || [])) this.tagPerformancePlatforms = current
                    return
                }
                this.tagPerformancePlatforms = options.map((o) => o.key)
            },
            computeAggregateMetricsForSelectedBrands(latest, brands) {
                const rows = Array.isArray(latest) ? latest : []
                if (!rows.length || !Array.isArray(brands) || !brands.length) {
                    return { visibility: null, relevanceRank: null, sourceReferences: null, sentiment: null, foundBrand: false }
                }

                const ranked = rows
                    .filter((b) => b && typeof b.visibilityPercent === "number" && Number.isFinite(b.visibilityPercent))
                    .sort((a, b) => (b.visibilityPercent || 0) - (a.visibilityPercent || 0))
                const metrics = []

                brands.forEach((brand) => {
                    const targetName = (brand?.canonicalName || brand?.brand_name || "").toLowerCase()
                    const extraMentions = Array.isArray(brand?.mentions) ? brand.mentions.map((m) => (m || "").toLowerCase()) : []

                    const found = rows.find((entry) => {
                        if (!entry) return false
                        const entryCanon = (entry.canonicalName || "").toLowerCase()
                        if (entryCanon && (entryCanon === targetName || extraMentions.includes(entryCanon))) return true
                        if (Array.isArray(entry.mentions)) {
                            return entry.mentions.map((m) => (m || "").toLowerCase()).some((m) => m && (m === targetName || extraMentions.includes(m)))
                        }
                        return false
                    })

                    if (!found) return
                    const rankIdx = ranked.indexOf(found)
                    metrics.push({
                        visibility: typeof found.visibilityPercent === "number" ? found.visibilityPercent : null,
                        relevanceRank: rankIdx > -1 ? rankIdx + 1 : null,
                        sourceReferences: typeof found?.sourcesCount === "number" ? found.sourcesCount : found?.sources?.current?.totalResponses ?? found?.sources?.totalResponses ?? null,
                        sentiment: typeof found?.sentimentAvg === "number" ? found.sentimentAvg : null
                    })
                })

                if (!metrics.length) {
                    return { visibility: null, relevanceRank: null, sourceReferences: null, sentiment: null, foundBrand: false }
                }

                const avg = (arr) => {
                    const nums = arr.filter((v) => typeof v === "number" && Number.isFinite(v))
                    if (!nums.length) return null
                    return nums.reduce((s, n) => s + n, 0) / nums.length
                }

                const visibility = avg(metrics.map((m) => m.visibility))
                const relevanceRank = avg(metrics.map((m) => m.relevanceRank))
                const sourceReferences = avg(metrics.map((m) => m.sourceReferences))
                const sentiment = avg(metrics.map((m) => m.sentiment))

                return {
                    visibility: visibility != null ? Number(visibility.toFixed(2)) : null,
                    relevanceRank: relevanceRank != null ? Number(relevanceRank.toFixed(2)) : null,
                    sourceReferences: sourceReferences != null ? Number(sourceReferences.toFixed(2)) : null,
                    sentiment: sentiment != null ? Number(sentiment.toFixed(2)) : null,
                    foundBrand: true
                }
            },
            extractBrandMetricsFromRows(rows, brand) {
                const safeRows = Array.isArray(rows) ? rows : []
                const targetName = (brand?.canonicalName || brand?.brand_name || "").toLowerCase()
                const extraMentions = Array.isArray(brand?.mentions) ? brand.mentions.map((m) => (m || "").toLowerCase()) : []
                const ranked = safeRows
                    .filter((b) => b && typeof b.visibilityPercent === "number" && Number.isFinite(b.visibilityPercent))
                    .sort((a, b) => (b.visibilityPercent || 0) - (a.visibilityPercent || 0))

                const matchBrand = (entry) => {
                    if (!entry) return false
                    const entryCanon = (entry.canonicalName || "").toLowerCase()
                    if (entryCanon && (entryCanon === targetName || extraMentions.includes(entryCanon))) return true
                    if (Array.isArray(entry.mentions)) {
                        return entry.mentions.map((m) => (m || "").toLowerCase()).some((m) => m && (m === targetName || extraMentions.includes(m)))
                    }
                    return false
                }

                const found = safeRows.find(matchBrand)
                let relevanceRank = null
                if (found) {
                    const idx = ranked.indexOf(found)
                    relevanceRank = idx > -1 ? idx + 1 : null
                }

                return {
                    visibility: typeof found?.visibilityPercent === "number" ? found.visibilityPercent : null,
                    relevanceRank,
                    sourceReferences: typeof found?.sourcesCount === "number" ? found.sourcesCount : found?.sources?.current?.totalResponses ?? found?.sources?.totalResponses ?? null,
                    sentiment: typeof found?.sentimentAvg === "number" ? found.sentimentAvg : null
                }
            },
            async fetchPlatformBrandRangeAverages(platformKey, tagsOverride = null, selectedBrandsOverride = null, tagsOperatorOverride = null) {
                const range = this.computeBrandMetricsRanges()
                if (!range || !this.project?._id) return []
                try {
                    const baseUrl =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/brandvisibility/range-averages"
                    const tags = Array.isArray(tagsOverride) ? tagsOverride : this.selectedTags && this.selectedTags.length ? this.selectedTags : null
                    const tagsOperator = tagsOperatorOverride || this.tagOperator
                    const sourceBrands = Array.isArray(selectedBrandsOverride) ? selectedBrandsOverride : this.selectedBrands
                    const brandsData = Array.isArray(sourceBrands)
                        ? sourceBrands
                              .map((b) => ({
                                  canonicalName: b?.canonicalName || b?.brand_name || null,
                                  mentions: Array.isArray(b?.mentions) ? b.mentions : []
                              }))
                              .filter((b) => b.canonicalName || (Array.isArray(b.mentions) && b.mentions.length))
                        : null
                    const post_data = {
                        company_id: localStorage.company_id,
                        project_id: this.project._id,
                        platform: platformKey,
                        tags,
                        tagsOperator,
                        currentRange: range.current,
                        previousRange: range.previous,
                        includeSources: true
                    }
                    if (brandsData && brandsData.length) post_data.brandsData = brandsData
                    const resp = await this.$helpers.axiosPost(baseUrl, post_data)
                    const brands = Array.isArray(resp.data?.brands) ? resp.data.brands : []
                    if (!brands.length) return []
                    const prevRows = brands.map((b) => ({
                        canonicalName: b.canonicalName,
                        mentions: Array.isArray(b.mentions) ? b.mentions : [],
                        visibilityPercent: typeof b.visibilityPercentOld === "number" ? b.visibilityPercentOld : null,
                        sentimentAvg: typeof b.sentimentAvgOld === "number" ? b.sentimentAvgOld : null,
                        sourcesCount: typeof b.sourcesCountOld === "number" ? b.sourcesCountOld : 0,
                        responsesWithMention: b.responsesWithMentionOld || 0
                    }))
                    const curRows = brands.map((b) => ({
                        canonicalName: b.canonicalName,
                        mentions: Array.isArray(b.mentions) ? b.mentions : [],
                        visibilityPercent: typeof b.visibilityPercent === "number" ? b.visibilityPercent : null,
                        sentimentAvg: typeof b.sentimentAvg === "number" ? b.sentimentAvg : null,
                        sourcesCount: typeof b.sourcesCount === "number" ? b.sourcesCount : 0,
                        responsesWithMention: b.responsesWithMention || 0
                    }))
                    return [prevRows, curRows]
                } catch (e) {
                    console.warn("fetchPlatformBrandRangeAverages error", platformKey, e)
                    return []
                }
            },
            fetchPlatformBrandSeriesForMode(platformKey, importDates, tagsOverride = null, selectedBrandsOverride = null, tagsOperatorOverride = null) {
                if (this.dashboardMode === "average") {
                    return this.fetchPlatformBrandRangeAverages(platformKey, tagsOverride, selectedBrandsOverride, tagsOperatorOverride)
                }
                return this.fetchPlatformBrandVisibility(platformKey, importDates, tagsOverride, selectedBrandsOverride, tagsOperatorOverride)
            },
            // ---- Performance (abschaltbar, siehe components/helper/aiRequestDedupe.js) ----
            // Die Plattform-Reihen der Matrix werden kurz gemerkt: buildMetricsMatrix laeuft beim
            // Laden zweimal hintereinander mit identischen Anfragen (ein zweiter Ausloeser trifft
            // waehrend des ersten Laufs ein und wird danach nachgeholt). Gemerkt wird hoechstens
            // 30 s und nur bis zur naechsten Aenderung an Filtern oder Marken.
            ensureSeriesMemo() {
                if (!(this._seriesMemo instanceof Map)) this._seriesMemo = new Map()
            },
            clearSeriesMemo() {
                this.ensureSeriesMemo()
                this._seriesMemo.clear()
            },
            seriesMemoKey(visPost) {
                const rest = { ...visPost }
                delete rest.company_id
                return JSON.stringify(rest)
            },
            freshSeriesMemoEntry(key) {
                this.ensureSeriesMemo()
                const entry = this._seriesMemo.get(key)
                if (!entry) return null
                if (Date.now() - entry.ts > 30000) {
                    this._seriesMemo.delete(key)
                    return null
                }
                return entry
            },
            aiSuiteApiRoot() {
                return JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)) == -1
                    ? process.env.VUE_APP_MONITORING_API_URL
                    : process.env.VUE_APP_APIURL + "/monitoring-api"
            },
            buildPlatformVisPost(platformKey, safeDates, tagsOverride = null, selectedBrandsOverride = null, tagsOperatorOverride = null) {
                const projectBrandName = (this.project?.brand?.brand_name || "").toString().trim()
                const projectBrandNameLower = projectBrandName.toLowerCase()
                const projectBrandDomain = this.project?.brand?.brand_domain || null
                const sourceBrands = Array.isArray(selectedBrandsOverride) ? selectedBrandsOverride : this.selectedBrands
                return {
                    company_id: localStorage.company_id,
                    project_id: this.project._id,
                    platform: platformKey,
                    importDates: safeDates,
                    selectedBrands: Array.isArray(sourceBrands)
                        ? sourceBrands.map((b) => {
                              const canonical = b?.canonicalName || b?.brand_name || null
                              const brandName = b?.brand_name || null
                              const mentions = Array.isArray(b?.mentions) ? b.mentions : []
                              const matchesProjectBrand = Boolean(
                                  (projectBrandNameLower && [canonical, brandName].filter(Boolean).some((name) => name.toString().toLowerCase() === projectBrandNameLower)) ||
                                      mentions.map((m) => (m || "").toString().toLowerCase()).includes(projectBrandNameLower)
                              )

                              return {
                                  canonicalName: canonical,
                                  brand_name: matchesProjectBrand ? projectBrandName || brandName : brandName,
                                  brand_domain: (matchesProjectBrand ? projectBrandDomain : null) || b?.brand_domain || b?.metadata?.officialWebsite || b?.metadata?.website || null,
                                  mentions
                              }
                          })
                        : null,
                    tagsOperator: tagsOperatorOverride || this.tagOperator,
                    tags: Array.isArray(tagsOverride) ? tagsOverride : this.selectedTags && this.selectedTags.length ? this.selectedTags : null
                }
            },
            // Holt die Reihen ALLER Plattformen in einer Anfrage (statt einer je Plattform, jede mit
            // der kompletten Markenliste im Body) und legt sie im Memo ab. Schlaegt das fehl, bleibt
            // das Memo leer und fetchPlatformBrandVisibility nimmt wie bisher die Einzelroute.
            async prefetchPlatformSeriesBatch(platforms, rankingBrands) {
                if (!aiPerfEnabled() || this.dashboardMode === "average" || !this.project) return
                const posts = (platforms || [])
                    .map((p) => {
                        const dates = (this.getPlatformDateField(p.key, true) || []).filter(Boolean)
                        return dates.length ? this.buildPlatformVisPost(p.key, dates, null, rankingBrands) : null
                    })
                    .filter(Boolean)
                    .filter((post) => !this.freshSeriesMemoEntry(this.seriesMemoKey(post)))
                if (posts.length < 2) return

                const shared = { ...posts[0] }
                delete shared.platform
                delete shared.importDates
                const batchPromise = this.$helpers.axiosPost(this.aiSuiteApiRoot() + "/ai-suite/brandvisibility/by/date/batch", {
                    ...shared,
                    requests: posts.map((post) => ({ platform: post.platform, importDates: post.importDates }))
                })
                posts.forEach((post) => {
                    const key = this.seriesMemoKey(post)
                    const promise = batchPromise.then((resp) => {
                        const hit = (Array.isArray(resp?.data?.results) ? resp.data.results : []).find((r) => r && r.platform === post.platform)
                        if (!hit || hit.status !== 200) throw new Error("batch: kein Ergebnis fuer " + post.platform)
                        return Array.isArray(hit.data?.brandsByDate) ? hit.data.brandsByDate : []
                    })
                    this._seriesMemo.set(key, { ts: Date.now(), promise })
                    promise.catch(() => {
                        const current = this._seriesMemo.get(key)
                        if (current && current.promise === promise) this._seriesMemo.delete(key)
                    })
                })
                try {
                    await batchPromise
                } catch (e) {
                    console.warn("prefetchPlatformSeriesBatch: Sammelroute fehlgeschlagen, Rueckfall auf Einzelrouten", e?.message || e)
                }
            },
            async fetchPlatformBrandVisibility(platformKey, importDates, tagsOverride = null, selectedBrandsOverride = null, tagsOperatorOverride = null) {
                const safeDates = Array.isArray(importDates) ? importDates.filter(Boolean) : []
                if (!safeDates.length) return []
                const visPost = this.buildPlatformVisPost(platformKey, safeDates, tagsOverride, selectedBrandsOverride, tagsOperatorOverride)
                const usePerf = aiPerfEnabled()
                const memoKey = usePerf ? this.seriesMemoKey(visPost) : null
                if (usePerf) {
                    const entry = this.freshSeriesMemoEntry(memoKey)
                    if (entry) {
                        try {
                            return await entry.promise
                        } catch (e) {
                            // gemerkte Anfrage ist gescheitert — unten normal neu holen
                        }
                    }
                }
                const promise = (async () => {
                    const visResp = await this.$helpers.axiosPost(this.aiSuiteApiRoot() + "/ai-suite/brandvisibility/by/date", visPost)
                    return Array.isArray(visResp.data?.brandsByDate) ? visResp.data.brandsByDate : []
                })()
                if (usePerf) {
                    this.ensureSeriesMemo()
                    this._seriesMemo.set(memoKey, { ts: Date.now(), promise })
                }
                try {
                    return await promise
                } catch (e) {
                    // Fehler nie merken
                    if (usePerf && this._seriesMemo.get(memoKey)?.promise === promise) this._seriesMemo.delete(memoKey)
                    console.warn("fetchPlatformBrandVisibility error", platformKey, e)
                    return []
                }
            },
            async fetchBrandMetricsForMatrix(brand, platformKey, importDates, brandsByDate) {
                const bKey = this.brandKey(brand)
                if (!bKey) return { visibility: null, relevanceRank: null, sourceReferences: null, sentiment: null, domain: null }
                let derivedDomain = this.getBrandFaviconDomain(brand) || null
                try {
                    const effectiveImportDates = Array.isArray(importDates) && importDates.length ? importDates : this.getPlatformDateField(platformKey, true)

                    if (!effectiveImportDates.length) {
                        return { visibility: null, relevanceRank: null, sourceReferences: null, sentiment: null, domain: derivedDomain }
                    }
                    this.ensureBrandMetricsCache()
                    const cacheKey = this.getBrandMetricsCacheKey(bKey, platformKey, effectiveImportDates)
                    if (this._brandMetricsCache.has(cacheKey)) {
                        return this._brandMetricsCache.get(cacheKey)
                    }
                    const visibilitySeries = Array.isArray(brandsByDate) && brandsByDate.length ? brandsByDate : await this.fetchPlatformBrandSeriesForMode(platformKey, effectiveImportDates)
                    const first = visibilitySeries[0] || []
                    const latest = visibilitySeries[visibilitySeries.length - 1] || []
                    const currentMetrics = this.extractBrandMetricsFromRows(latest, brand)
                    const previousMetrics = visibilitySeries.length > 1 ? this.extractBrandMetricsFromRows(first, brand) : null

                    const visibility = currentMetrics.visibility
                    const relevanceRank = currentMetrics.relevanceRank
                    const sourceReferences = currentMetrics.sourceReferences
                    const sentiment = currentMetrics.sentiment

                    const visibilityDelta =
                        previousMetrics && typeof visibility === "number" && typeof previousMetrics.visibility === "number" ? Number((visibility - previousMetrics.visibility).toFixed(2)) : null
                    const relevanceRankDelta =
                        previousMetrics && typeof relevanceRank === "number" && typeof previousMetrics.relevanceRank === "number"
                            ? Number((previousMetrics.relevanceRank - relevanceRank).toFixed(2))
                            : null
                    const sourceReferencesDelta =
                        previousMetrics && typeof sourceReferences === "number" && typeof previousMetrics.sourceReferences === "number"
                            ? Number((sourceReferences - previousMetrics.sourceReferences).toFixed(2))
                            : null
                    const sentimentDelta =
                        previousMetrics && typeof sentiment === "number" && typeof previousMetrics.sentiment === "number" ? Number((sentiment - previousMetrics.sentiment).toFixed(2)) : null

                    const domain = derivedDomain
                    const result = {
                        visibility,
                        relevanceRank,
                        sourceReferences,
                        sentiment,
                        visibilityDelta,
                        relevanceRankDelta,
                        sourceReferencesDelta,
                        sentimentDelta,
                        domain
                    }
                    this._brandMetricsCache.set(cacheKey, result)
                    return result
                } catch (e) {
                    console.warn("fetchBrandMetricsForMatrix error", platformKey, e)
                    return { visibility: null, relevanceRank: null, sourceReferences: null, sentiment: null, domain: derivedDomain }
                }
            },
            matrixMetricDeltaClass(metric, cell) {
                const value = this.getMatrixMetricDelta(metric, cell)
                if (value == null || value === 0) return "mm-change--na"
                return value > 0 ? "up" : "down"
            },
            matrixMetricDeltaIcon(metric, cell) {
                const value = this.getMatrixMetricDelta(metric, cell)
                if (value == null || value === 0) return "mdi-minus"
                return value > 0 ? "mdi-arrow-up" : "mdi-arrow-down"
            },
            getMatrixMetricDelta(metric, cell) {
                if (!cell || typeof cell !== "object") return null
                if (metric === "visibility") return typeof cell.visibilityDelta === "number" ? cell.visibilityDelta : null
                if (metric === "relevanceRank") return typeof cell.relevanceRankDelta === "number" ? cell.relevanceRankDelta : null
                if (metric === "sourceReferences") return typeof cell.sourceReferencesDelta === "number" ? cell.sourceReferencesDelta : null
                if (metric === "sentiment") return typeof cell.sentimentDelta === "number" ? cell.sentimentDelta : null
                return null
            },
            formatMatrixMetricDelta(metric, cell) {
                const delta = this.getMatrixMetricDelta(metric, cell)
                if (delta == null) return ""
                if (metric === "visibility") return `${delta > 0 ? "+" : ""}${delta.toFixed(2)}%`
                if (metric === "relevanceRank") {
                    const abs = Math.abs(delta)
                    const decimals = Number.isInteger(abs) ? 0 : 2
                    return abs.toFixed(decimals)
                }
                if (metric === "sourceReferences") return this.formatCompactSignedNumber(delta, 2)
                if (metric === "sentiment") return this.formatCompactSignedNumber(delta * 100, 0)
                return ""
            },
            formatCompactSignedNumber(value, maxDecimals = 2) {
                const num = Number(value)
                if (!Number.isFinite(num)) return ""
                const rounded = Number(num.toFixed(maxDecimals))
                const abs = Math.abs(rounded)
                const formatted = Number.isInteger(abs)
                    ? abs.toString()
                    : abs
                          .toFixed(maxDecimals)
                          .replace(/\.0+$/, "")
                          .replace(/(\.\d*?)0+$/, "$1")
                return `${rounded > 0 ? "+" : rounded < 0 ? "-" : ""}${formatted}`
            },
            matrixMetricDeltaTitle(metric) {
                if (metric === "visibility") return "Change vs start date"
                if (metric === "relevanceRank") return "Rank movement vs start date"
                if (metric === "sourceReferences") return "Source references change vs start date"
                if (metric === "sentiment") return "Sentiment change vs start date"
                return "Change vs start date"
            },
            formatOrdinalRank(rank) {
                const num = Number(rank)
                if (!Number.isFinite(num)) return ""
                const n = Math.max(1, Math.round(num))
                const mod100 = n % 100
                if (mod100 >= 11 && mod100 <= 13) return `${n}th`
                const mod10 = n % 10
                if (mod10 === 1) return `${n}st`
                if (mod10 === 2) return `${n}nd`
                if (mod10 === 3) return `${n}rd`
                return `${n}th`
            },
            matrixRankPillClass(rank) {
                const num = Number(rank)
                if (!Number.isFinite(num)) return ""
                const n = Math.max(1, Math.round(num))
                if (n === 1) return "mm-rank-pill--gold"
                if (n === 2) return "mm-rank-pill--silver"
                if (n === 3) return "mm-rank-pill--bronze"
                return "mm-rank-pill--neutral"
            },
            showMatrixRankIcon(rank) {
                const num = Number(rank)
                if (!Number.isFinite(num)) return false
                const n = Math.max(1, Math.round(num))
                return n <= 3
            },
            getPlatformDateField(platformKey, returnDates = false) {
                const map = {
                    openai: "processed_dates_ai_tracking_openai",
                    chatgpt: "processed_dates_ai_tracking_chatgpt",
                    google: "processed_dates",
                    google_ai_mode: "processed_dates_ai_tracking_google_ai_mode",
                    gemini: "processed_dates_ai_tracking_gemini",
                    gemini_api: "processed_dates_ai_tracking_gemini_api",
                    claude: "processed_dates_ai_tracking_claude",
                    perplexity: "processed_dates_ai_tracking_perplexity",
                    grok: "processed_dates_ai_tracking_grok",
                    microsoft_copilot: "processed_dates_ai_tracking_microsoft_copilot"
                }
                const field = map[platformKey] || "processed_dates_ai_tracking_openai"
                if (!returnDates) return field

                // NEU: zwei Dates anhand selectedDbDates (tagesbasiert)
                const arr = Array.isArray(this.project?.[field]) ? this.project[field] : []
                if (!arr.length) return []

                const toDay = (d) => {
                    const dt = new Date(d)
                    return isNaN(dt) ? null : dt.toISOString().slice(0, 10)
                }
                const db = Array.isArray(this.selectedDbDates) ? this.selectedDbDates.filter(Boolean) : []
                const firstDay = db[0] ? toDay(db[0]) : null
                const lastDay = db[1] ? toDay(db[1]) : firstDay

                let earliest = null
                let latest = null
                if (firstDay && lastDay) {
                    arr.forEach((d) => {
                        const dt = new Date(d)
                        if (isNaN(dt)) return
                        const day = dt.toISOString().slice(0, 10)
                        if (day === firstDay && (!earliest || dt < earliest)) earliest = dt
                        if (day === lastDay && (!latest || dt > latest)) latest = dt
                    })
                }

                const out = []
                if (earliest) out.push(earliest.toISOString())
                if (latest && (!earliest || latest.getTime() !== earliest.getTime())) out.push(latest.toISOString())
                return out.length ? out : arr.slice(-2)
            },
            buildImportDatesByPlatform(limitPerPlatform = 2) {
                if (!this.project) return {}
                const map = {
                    openai: "processed_dates_ai_tracking_openai",
                    chatgpt: "processed_dates_ai_tracking_chatgpt",
                    google: "processed_dates",
                    google_ai_mode: "processed_dates_ai_tracking_google_ai_mode",
                    gemini: "processed_dates_ai_tracking_gemini",
                    claude: "processed_dates_ai_tracking_claude",
                    perplexity: "processed_dates_ai_tracking_perplexity",
                    grok: "processed_dates_ai_tracking_grok",
                    microsoft_copilot: "processed_dates_ai_tracking_microsoft_copilot"
                }
                const allowedPlatforms = new Set(this.getEnabledPlatformKeys())

                const toUtcDay = (d) => {
                    const dt = new Date(d)
                    return isNaN(dt) ? null : dt.toISOString().slice(0, 10)
                }
                const dbDates = Array.isArray(this.selectedDbDates) ? this.selectedDbDates.filter(Boolean) : []
                const firstDay = dbDates[0] ? toUtcDay(dbDates[0]) : null
                const lastDay = dbDates[1] ? toUtcDay(dbDates[1]) : firstDay
                const dayFallbackIso = {}
                if (firstDay && dbDates[0]) {
                    const d0 = new Date(dbDates[0])
                    if (!isNaN(d0)) dayFallbackIso[firstDay] = d0.toISOString()
                }
                if (lastDay && dbDates[1]) {
                    const d1 = new Date(dbDates[1])
                    if (!isNaN(d1)) dayFallbackIso[lastDay] = d1.toISOString()
                } else if (lastDay && dbDates[0] && !dayFallbackIso[lastDay]) {
                    const d0 = new Date(dbDates[0])
                    if (!isNaN(d0)) dayFallbackIso[lastDay] = d0.toISOString()
                }

                const result = {}
                Object.entries(map).forEach(([platform, field]) => {
                    if (allowedPlatforms.size && !allowedPlatforms.has(platform)) return
                    const arr = Array.isArray(this.project[field]) ? this.project[field] : []
                    if (!arr.length) return

                    let importDates = []
                    const selectedDays = Array.from(new Set([firstDay, lastDay].filter(Boolean))).sort()
                    if (selectedDays.length) {
                        const buckets = {}
                        arr.forEach((d) => {
                            const dt = new Date(d)
                            if (isNaN(dt)) return
                            const day = dt.toISOString().slice(0, 10)
                            if (!selectedDays.includes(day)) return
                            if (!buckets[day]) {
                                buckets[day] = { earliest: dt, latest: dt }
                            } else {
                                if (dt < buckets[day].earliest) buckets[day].earliest = dt
                                if (dt > buckets[day].latest) buckets[day].latest = dt
                            }
                        })
                        selectedDays.forEach((day) => {
                            const bucket = buckets[day]
                            if (bucket) {
                                const pick = day === lastDay ? bucket.latest : bucket.earliest
                                importDates.push(pick.toISOString())
                                return
                            }
                            if (dayFallbackIso[day]) {
                                importDates.push(dayFallbackIso[day])
                            }
                        })

                        // Sicherstellen: bei 2 Vergleichstagen immer 2 Werte je Plattform
                        if (selectedDays.length === 2 && importDates.length === 1) {
                            importDates = [importDates[0], importDates[0]]
                        }
                    } else {
                        importDates = arr.slice(-limitPerPlatform)
                    }

                    if (importDates.length) result[platform] = importDates
                })
                return result
            },
            async refreshBrandMetrics() {
                this.brandMetrics = null
                // this.brandMetricCards = [] // UI leert sofort
                if (this.project?.brand && this.selectedPlatform) {
                    await this.fetchBrandMetrics(this.project.brand)
                }
            },
            async fetchAvailableTags() {
                try {
                    if (!this.project?._id) return
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/tags/list"
                    const payload = {
                        company_id: localStorage.company_id,
                        project_id: this.project._id,
                        platform: this.selectedPlatform && this.selectedPlatform.key !== "all" ? this.selectedPlatform.key : null,
                        selectedDates: this.selectedDates,
                        selectedTags: this.tagOperator === "and" && Array.isArray(this.selectedTags) && this.selectedTags.length ? this.selectedTags : null
                    }
                    const resp = await this.$helpers.axiosPost(url, payload)
                    const tags = Array.isArray(resp.data?.tags) ? resp.data.tags : []
                    this.availableTags = tags.map((t) => ({ id: t, label: t }))
                    this.combinableTags = payload.selectedTags && Array.isArray(resp.data?.combinableTags) ? resp.data.combinableTags : null
                    this.ensureTagPerformanceTagSelection()

                    // Auswahl nur setzen, wenn sich wirklich etwas ändert (Watcher dämpfen)
                    const current = Array.isArray(this.selectedTags) ? this.selectedTags : []
                    const valid = this.availableTags.map((t) => t.id).filter((t) => current.includes(t))
                    const changed = current.length !== valid.length || current.some((v, i) => v !== valid[i])
                    if (changed) {
                        this._suppressSelectedTagsWatcher = true
                        this.selectedTags = valid
                        this.fetchCombinableTags()
                    }
                    this.triggerTagPerformanceRefresh(true)
                } catch (e) {
                    console.warn("fetchAvailableTags error", e)
                    this.availableTags = []
                    this.combinableTags = null
                    if (this.selectedPlatform?.key === "all") {
                        this.tagMetricsMatrix = {}
                        this.tagMatrixVersion++
                        this.tagMatrixLoaded = true
                    }
                    // Nichts setzen, um Watcher-Kaskaden zu vermeiden
                }
            },
            // Lädt nur die mit der aktuellen Auswahl (AND) kombinierbaren Tags nach — ohne Seiteneffekte auf availableTags
            async fetchCombinableTags() {
                if (!this.project?._id) return
                if (this.tagOperator !== "and" || !Array.isArray(this.selectedTags) || !this.selectedTags.length) {
                    this.combinableTags = null
                    return
                }
                // Achtung: Vue proxiert "_"-Props aus data() nicht auf die Instanz -> this._... ist undefined.
                // Ohne den ||0-Fallback wird der Zaehler NaN und der Guard unten greift immer (NaN !== NaN).
                const reqId = (this._combinableTagsReqId = (this._combinableTagsReqId || 0) + 1)
                try {
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/tags/list"
                    const payload = {
                        company_id: localStorage.company_id,
                        project_id: this.project._id,
                        platform: this.selectedPlatform && this.selectedPlatform.key !== "all" ? this.selectedPlatform.key : null,
                        selectedDates: this.selectedDates,
                        selectedTags: this.selectedTags
                    }
                    const resp = await this.$helpers.axiosPost(url, payload)
                    if (reqId !== this._combinableTagsReqId) return
                    this.combinableTags = Array.isArray(resp.data?.combinableTags) ? resp.data.combinableTags : null
                } catch (e) {
                    console.warn("fetchCombinableTags error", e)
                    if (reqId === this._combinableTagsReqId) this.combinableTags = null
                }
            },
            getColorForBrand(name) {
                return resolveBrandColor(name, this.brandColors)
            },
            getColorFromString(str) {
                return resolveBrandColor(str, this.brandColors)
            },
            brandColorDotStyle(name) {
                const c = this.getColorForBrand(name || "")
                return {
                    background: c
                }
            },
            async fetchBrandMetrics(brand) {
                if (!this.project?._id || !brand || !this.selectedPlatform) return
                try {
                    const baseUrl =
                        JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api"

                    const range = this.computeBrandMetricsRanges()
                    if (this.dashboardMode === "average" && range) {
                        const post_data = {
                            company_id: localStorage.company_id,
                            project_id: this.project._id,
                            platform: this.selectedPlatform.key === "all" ? null : this.selectedPlatform.key,
                            brand,
                            tags: this.selectedTags && this.selectedTags.length ? this.selectedTags : null,
                            tagsOperator: this.tagOperator,
                            currentRange: range.current,
                            previousRange: range.previous
                        }
                        const resp = await this.$helpers.axiosPost(baseUrl + "/ai-suite/brand/metrics-range", post_data)
                        if (resp.data?.success) {
                            this.brandMetrics = resp.data
                            this.buildBrandMetricCards()
                            return
                        }
                    }

                    // Absolute mode (or fallback): legacy snapshot endpoint
                    const post_data = {
                        company_id: localStorage.company_id,
                        project_id: this.project._id,
                        platform: this.selectedPlatform.key === "all" ? null : this.selectedPlatform.key,
                        brand,
                        promptCount: this.project.prompt_count || this.project.keyword_count || null,
                        tags: this.selectedTags && this.selectedTags.length ? this.selectedTags : null,
                        tagsOperator: this.tagOperator
                    }
                    if (this.selectedPlatform.key === "all") {
                        post_data.importDatesByPlatform = this.buildImportDatesByPlatform(2)
                    } else {
                        post_data.importDates = this.selectedDbDates
                    }
                    const resp = await this.$helpers.axiosPost(baseUrl + "/ai-suite/brandmetrics", post_data)
                    if (resp.data?.success) {
                        this.brandMetrics = resp.data
                        this.buildBrandMetricCards()
                    }
                } catch (e) {
                    console.error("fetchBrandMetrics error", e)
                }
            },
            computeBrandMetricsRanges() {
                const dates = Array.isArray(this.selectedDates) ? this.selectedDates.filter(Boolean) : []
                if (!dates.length) return null
                const sorted = [...dates].sort()
                const from = sorted[0]
                const to = sorted[sorted.length - 1] || sorted[0]
                if (!from || !to) return null
                const fromDate = new Date(`${from}T00:00:00.000Z`)
                const toDate = new Date(`${to}T00:00:00.000Z`)
                if (isNaN(fromDate) || isNaN(toDate)) return null
                const spanDays = Math.max(1, Math.round((toDate - fromDate) / 86400000) + 1)
                const prevTo = new Date(fromDate)
                prevTo.setUTCDate(prevTo.getUTCDate() - 1)
                const prevFrom = new Date(prevTo)
                prevFrom.setUTCDate(prevFrom.getUTCDate() - (spanDays - 1))
                return {
                    current: { from, to },
                    previous: { from: prevFrom.toISOString().slice(0, 10), to: prevTo.toISOString().slice(0, 10) }
                }
            },
            buildBrandMetricCards() {
                this.brandMetricCards = []
                if (!this.project || !Array.isArray(this.brands) || !this.brands.length) return
                const platformLabel = this.selectedPlatform?.key === "all" ? "All Models" : this.getPlatformInfo(this.selectedPlatform.key).label
                const make = (key, { label, current, previous, unit = null, decimals = 2, suffix = "", lowerIsBetter = false, forceNoDeltaPercent = false }) => {
                    let delta = null
                    if (current != null && previous != null) {
                        delta = lowerIsBetter ? previous - current : current - previous
                    }
                    let deltaPercent = null
                    if (!forceNoDeltaPercent && current != null && previous != null && previous !== 0) {
                        deltaPercent = ((current - previous) / previous) * 100
                        if (lowerIsBetter) {
                            deltaPercent = ((previous - current) / previous) * 100
                        }
                    }
                    return { key, label, value: current, previous, unit, suffix, delta, deltaPercent, decimals, lowerIsBetter }
                }
                const mainNameLower = (this.project?.brand?.brand_name || "").toLowerCase()
                const isMain = (b) =>
                    (b.canonicalName && b.canonicalName.toLowerCase() === mainNameLower) || (Array.isArray(b.mentions) && b.mentions.some((m) => (m || "").toLowerCase() === mainNameLower))

                const currentBrand = this.brands.find(isMain) || null
                const bm = this.brandMetrics || {}
                const hasRangeMetrics = !!(bm && bm.currentRange && bm.previousRange)

                const visCurrent = hasRangeMetrics ? bm?.visibility?.current ?? null : currentBrand?.visibilityPercent ?? null
                const visPrevious = hasRangeMetrics ? bm?.visibility?.previous ?? null : currentBrand?.visibilityPercentOld ?? null

                this.brandMetricCards.push(
                    make("visibility", {
                        label: "Brand Visibility",
                        current: visCurrent,
                        previous: visPrevious,
                        unit: platformLabel,
                        suffix: visCurrent != null || visPrevious != null ? "%" : "",
                        decimals: 2
                    })
                )

                let currentRank = null
                let prevRank = null
                if (hasRangeMetrics && (bm?.rank?.current != null || bm?.rank?.previous != null)) {
                    currentRank = bm.rank.current ?? null
                    prevRank = bm.rank.previous ?? null
                } else {
                    const currentSorted = [...this.brands]
                        .filter((b) => b && typeof b.visibilityPercent === "number" && Number.isFinite(b.visibilityPercent))
                        .sort((a, b) => (b.visibilityPercent || 0) - (a.visibilityPercent || 0))
                    const prevSorted = [...this.brands]
                        .filter((b) => b && typeof b.visibilityPercentOld === "number" && Number.isFinite(b.visibilityPercentOld))
                        .sort((a, b) => (b.visibilityPercentOld || 0) - (a.visibilityPercentOld || 0))
                    const currentBrandRank = currentSorted.find(isMain)
                    const prevBrand = prevSorted.find(isMain)
                    currentRank = currentBrandRank ? currentSorted.findIndex((b) => b === currentBrandRank) + 1 : null
                    prevRank = prevBrand ? prevSorted.findIndex((b) => b === prevBrand) + 1 : null
                }

                const sentimentCurrentVal = hasRangeMetrics
                    ? typeof bm?.sentiment?.current === "number"
                        ? bm.sentiment.current
                        : null
                    : currentBrand && typeof currentBrand.sentimentAvg === "number"
                    ? currentBrand.sentimentAvg
                    : null
                const sentimentPreviousVal = hasRangeMetrics
                    ? typeof bm?.sentiment?.previous === "number"
                        ? bm.sentiment.previous
                        : null
                    : currentBrand && typeof currentBrand.sentimentAvgOld === "number"
                    ? currentBrand.sentimentAvgOld
                    : null
                if (sentimentCurrentVal != null || sentimentPreviousVal != null) {
                    this.brandMetricCards.push(
                        make("sentiment", {
                            label: "Brand Sentiment",
                            current: sentimentCurrentVal,
                            previous: sentimentPreviousVal,
                            unit: platformLabel,
                            decimals: 2
                        })
                    )
                }

                this.brandMetricCards.push(
                    make("relevance_rank", {
                        label: "Brand Relevance Rank",
                        current: currentRank,
                        previous: prevRank,
                        unit: platformLabel,
                        decimals: 0,
                        lowerIsBetter: true,
                        forceNoDeltaPercent: true
                    })
                )

                this.brandMetricCards.push(
                    make("sources_responses", {
                        label: "Source References",
                        current: this.brandMetrics?.sources?.current?.totalResponses ?? null,
                        previous: this.brandMetrics?.sources?.previous?.totalResponses ?? null,
                        unit: platformLabel,
                        decimals: 0
                    })
                )
            },
            formatMetricCardValue(card) {
                if (card.value == null) return "—"
                if (card.key === "sentiment") {
                    return this.formatSentimentValue(card.value)
                }
                return card.value.toFixed(card.decimals) + (card.suffix || "")
            },
            metricCardTooltip(card) {
                const key = card?.key
                if (key === "visibility") {
                    return `
                        <div><b>Brand Visibility</b></div>
                        <ul style="margin:6px 0 0 16px; padding:0; line-height:1.35;">
                            <li><b>Current</b>: avg share of responses mentioning the brand across the selected period.</li>
                            <li><b>Change</b>: vs an equal-length period immediately before.</li>
                            <li><b>Scope</b>: selected platform + active tag filters.</li>
                        </ul>
                    `
                }
                if (key === "relevance_rank") {
                    return `
                        <div><b>Brand Rank (by Visibility)</b></div>
                        <ul style="margin:6px 0 0 16px; padding:0; line-height:1.35;">
                            <li><b>Current</b>: rank based on visibility (#1 = highest visibility).</li>
                            <li><b>Change</b>: movement vs the previous selected period (positive = improved rank).</li>
                            <li><b>Scope</b>: selected platform + active tag filters.</li>
                        </ul>
                    `
                }
                if (key === "sources_responses") {
                    return `
                        <div><b>Source References</b></div>
                        <ul style="margin:6px 0 0 16px; padding:0; line-height:1.35;">
                            <li><b>Current</b>: number of responses containing at least one source linked to the brand.</li>
                            <li><b>Change</b>: relative difference vs the previous selected period.</li>
                            <li><b>Scope</b>: selected platform + active tag filters.</li>
                        </ul>
                    `
                }
                if (key === "sentiment") {
                    return `
                        <div><b>Brand Sentiment</b></div>
                        <ul style="margin:6px 0 0 16px; padding:0; line-height:1.35;">
                            <li><b>Current</b>: average sentiment from -1 (very negative) to +1 (very positive).</li>
                            <li><b>Change</b>: absolute difference vs the previous selected period.</li>
                            <li><b>Scope</b>: selected platform + active tag filters.</li>
                        </ul>
                    `
                }
                return ""
            },
            metricCardSubtitle(card) {
                const rangeLabel = this.brandMetricsRangeLabel
                return rangeLabel ? `${rangeLabel}` : ""
            },
            metricTrendPath(card) {
                if (card?.delta < 0) {
                    return "M2 8 C22 12, 38 16, 54 18 C72 21, 88 23, 104 25 C122 28, 140 30, 158 32"
                }
                if (card?.delta > 0) {
                    return "M2 30 C20 26, 38 22, 54 18 C72 14, 90 12, 106 10 C124 8, 142 6, 158 4"
                }
                return "M2 20 C24 20, 40 20, 58 20 C78 20, 96 20, 114 20 C132 20, 146 20, 158 20"
            },
            formatDeltaPercent(card) {
                if (card.deltaPercent == null) return "—"
                return (card.deltaPercent > 0 ? "+" : "") + card.deltaPercent.toFixed(2) + "%"
            },
            formatDeltaValue(card) {
                // Für Rank: delta = previous - current (positiv = Verbesserung)
                if (card.delta == null) return "—"
                if (card.key === "relevance_rank") {
                    // Zeige Verbesserungen als -ΔRang? oder +? Wir zeigen: ▲2 (wenn von 5 auf 3)
                    const abs = Math.abs(card.delta)
                    return abs
                }
                if (card.key === "sentiment") {
                    return this.formatSentimentDelta(card.delta)
                }
                // Standard numerisch
                const sign = card.delta > 0 ? "+" : card.delta < 0 ? "" : ""
                const decimals = card.decimals != null ? card.decimals : 2
                return sign + card.delta.toFixed(decimals) + (card.suffix || "")
            },
            formatSentimentValue(value) {
                const clamped = this.clampSentimentValue(value)
                if (clamped == null) return "—"
                const scaled = clamped * 100
                if (scaled === 0) return "0"
                return `${scaled > 0 ? "+" : ""}${scaled.toFixed(0)}`
            },
            formatSentimentDelta(delta) {
                const num = Number(delta)
                if (!Number.isFinite(num)) return "—"
                const scaled = num * 100
                if (scaled === 0) return "±0"
                return `${scaled > 0 ? "+" : ""}${scaled.toFixed(0)}`
            },
            sentimentMarkerLeft(value) {
                const clamped = this.clampSentimentValue(value)
                if (clamped == null) return "50%"
                const pct = ((clamped + 1) / 2) * 100
                return `calc(${pct.toFixed(2)}% - 6px)`
            },
            clampSentimentValue(value) {
                if (value == null && value !== 0) return null
                const num = Number(value)
                if (!Number.isFinite(num)) return null
                return Math.max(-1, Math.min(1, num))
            },

            async exportActiveTable() {
                let csv = ""
                const tableView = this.$refs.activeTableView
                if (tableView && typeof tableView.exportTableData === "function") {
                    csv = await tableView.exportTableData()
                }
                if (csv) {
                    const effectiveButton = this.sectionToActiveButton(this.currentProjectSection) || this.activeButton || "Table"
                    this.downloadCsvFile(csv, `${effectiveButton}_export.csv`)
                }
            },
            csvEscape(value) {
                if (value === null || value === undefined) return ""
                const str = String(value)
                if (/[",\n;]/.test(str)) {
                    return `"${str.replace(/"/g, '""')}"`
                }
                return str
            },
            downloadCsvFile(csv, filename) {
                if (!csv) return
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
                const link = document.createElement("a")
                link.href = URL.createObjectURL(blob)
                link.setAttribute("download", filename)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            },
            exportPerformanceMatrix() {
                if (this.matrixViewMode === "tag") {
                    this.exportTagPerformanceMatrix()
                    return
                }
                this.exportBrandPerformanceMatrix()
            },
            exportBrandPerformanceMatrix() {
                const rows = []
                ;(this.matrixPlatforms || []).forEach((platformKey) => {
                    ;(this.matrixBrandOrder || []).forEach((brandKey) => {
                        const cell = this.matrixCellData(platformKey, brandKey)
                        rows.push({
                            platform: this.getPlatformInfo(platformKey).label || platformKey,
                            brand: this.brandCanonical(brandKey),
                            visibility: cell?.visibility,
                            relevanceRank: cell?.relevanceRank,
                            sourceReferences: cell?.sourceReferences,
                            noData: Boolean(cell?.noData)
                        })
                    })
                })

                if (!rows.length) {
                    this.createNotification("No Brand Performance data to export.", "mdi-information", "primary")
                    return
                }

                const header = ["Platform", "Brand", "Visibility (%)", "Relevance Rank", "Source References", "No Data"]
                const csv = [header.join(",")]
                rows.forEach((r) => {
                    csv.push(
                        [
                            this.csvEscape(r.platform),
                            this.csvEscape(r.brand),
                            this.csvEscape(typeof r.visibility === "number" ? r.visibility.toFixed(2) : ""),
                            this.csvEscape(typeof r.relevanceRank === "number" ? r.relevanceRank : ""),
                            this.csvEscape(typeof r.sourceReferences === "number" ? r.sourceReferences : ""),
                            this.csvEscape(r.noData ? "true" : "false")
                        ].join(",")
                    )
                })
                this.downloadCsvFile(csv.join("\n"), "brand_performance_export.csv")
            },
            exportTagPerformanceMatrix() {
                const columns = this.tagPerformanceMatrixColumns || []
                const rows = []
                ;(this.matrixTagOrderVisible || []).forEach((tagKey) => {
                    columns.forEach((col) => {
                        const cell = this.getTagMatrixCell(tagKey, col)
                        rows.push({
                            tag: this.tagPerformanceRowLabel(tagKey),
                            platform: this.getPlatformInfo(col.platformKey).label || col.platformKey,
                            brand: col.brandName,
                            visibility: cell?.visibility,
                            relevanceRank: cell?.relevanceRank,
                            sourceReferences: cell?.sourceReferences,
                            noData: Boolean(cell?.noData)
                        })
                    })
                })

                if (!rows.length) {
                    this.createNotification("No Tag Performance data to export.", "mdi-information", "primary")
                    return
                }

                const header = ["Tag", "Platform", "Brand", "Visibility (%)", "Relevance Rank", "Source References", "No Data"]
                const csv = [header.join(",")]
                rows.forEach((r) => {
                    csv.push(
                        [
                            this.csvEscape(r.tag),
                            this.csvEscape(r.platform),
                            this.csvEscape(r.brand),
                            this.csvEscape(typeof r.visibility === "number" ? r.visibility.toFixed(2) : ""),
                            this.csvEscape(typeof r.relevanceRank === "number" ? r.relevanceRank : ""),
                            this.csvEscape(typeof r.sourceReferences === "number" ? r.sourceReferences : ""),
                            this.csvEscape(r.noData ? "true" : "false")
                        ].join(",")
                    )
                })
                this.downloadCsvFile(csv.join("\n"), "tag_performance_export.csv")
            },
            async fetchData(project_id, category_id = null) {
                // ...existing code (gekürzt)...
                try {
                    this.loading = true
                    this.filtersReady = false
                    this.project = await this.fetchProject(project_id)
                    this.fetchGooglePromptCount(project_id)
                    const firstEnabled = this.platformSelectItems.find((p) => !p.disabled)
                    this.selectedPlatform = firstEnabled || this.platformSelectItems[0]
                    this.updateAvailableDates()
                    this.selectedDates = this.getValidSelectedDates(this.availableDates, this.selectedDates)

                    const query = this._pendingQuery || this.$route?.query || {}
                    this._pendingQuery = null
                    const projectId = this.project?._id || project_id
                    const hasQuery = query && Object.keys(query).length > 0
                    const returning = this.isReturningFromPromptDetail(projectId)
                    if (hasQuery) {
                        this.applyQueryToState(query)
                    } else if (!returning) {
                        this.resetAiSuiteViewState()
                        this.syncQueryFromState()
                    }
                    // Beim Zurückkehren aus dem Prompt-Detail die zuletzt aktive Section wiederherstellen
                    if (returning) {
                        try {
                            const savedSection = sessionStorage.getItem(`ai_suite_project_return_section_${projectId}`)
                            if (savedSection) {
                                const normalized = this.normalizeProjectSection(savedSection)
                                if (this.projectSection !== normalized) this.projectSection = normalized
                                const nextButton = this.sectionToActiveButton(normalized)
                                if (nextButton && this.activeButton !== nextButton) this.activeButton = nextButton
                            }
                            sessionStorage.removeItem(`ai_suite_project_return_section_${projectId}`)
                        } catch (e) {
                            // ignore storage errors
                        }
                    }
                    this.clearReturnFromPromptDetail(projectId)

                    // Prompts/Mentions tables can already start fetching while brand/tag data load in parallel.
                    this.filtersReady = true
                    this.openPromptOverlayFromQuery()

                    await this.fetchBrandFavorites(this.project._id)
                    // Unabhaengig voneinander, deshalb parallel: die gepflegten Marken haengen nicht
                    // am Sichtbarkeits-Ergebnis. Tags bleiben dahinter, weil fetchAvailableTags am
                    // Ende die Tag-Matrix anstoesst und dafuer die Marken braucht.
                    await Promise.all([this.setBrandData(project_id, category_id), this.fetchManagedBrandsEdited(this.project._id)])
                    this.fetchAllKnownBrands(this.project._id)
                    this.fetchBrandMetrics(this.project.brand)
                    this.debouncedBuildMetricsMatrix()
                    await this.fetchAvailableTags() // Tags initial laden
                    this.ensureTagPerformanceTagSelection()
                    if (this.matrixViewMode === "tag" && this.selectedPlatform?.key === "all") {
                        this.triggerTagPerformanceRefresh(true)
                    }
                    this.restoreProjectScrollPosition(this.project?._id || project_id)
                } catch (e) {
                    this.loading = false
                    console.log(e)
                }
            },
            normalizeBrandsFromBrandData(bdata) {
                const brandsByDate = bdata?.brandsByDate || []
                const [brandsOld, brandsNew] = brandsByDate
                let merged = []
                const projectBrandName = (this.project?.brand?.brand_name || "").trim()
                const projectBrandNameLower = projectBrandName.toLowerCase()

                if (brandsOld?.length && brandsNew?.length) {
                    const updatedNew = brandsNew.map((newb) => {
                        const copy = { ...newb }
                        copy.mentions = (copy.mentions || []).filter((m) => !m.includes(". "))
                        const matchingOld = brandsOld.find((oldb) => (oldb.mentions || []).filter((m) => !m.includes(". ")).some((m) => (copy.mentions || []).includes(m)))
                        copy.visibilityPercentOld = matchingOld ? matchingOld.visibilityPercent : null
                        copy.responsesWithMentionOld = matchingOld ? matchingOld.responsesWithMention : null
                        copy.salienceSumOld = matchingOld ? matchingOld.salienceSum : null
                        copy.sentimentAvgOld = matchingOld ? matchingOld.sentimentAvg ?? null : null
                        return copy
                    })
                    merged = updatedNew
                } else {
                    merged = brandsOld?.length ? brandsOld : brandsNew || []
                }

                const normalized = (Array.isArray(merged) ? merged : []).map((brand) => {
                    const copy = { ...(brand || {}) }
                    const mentions = Array.isArray(copy.mentions) ? copy.mentions.map((m) => (m || "").toString().trim()).filter(Boolean) : []
                    const hasProjectBrandInMentions = Boolean(projectBrandNameLower && mentions.some((m) => m.toLowerCase() === projectBrandNameLower))
                    if (hasProjectBrandInMentions) {
                        copy.canonicalName = projectBrandName
                        copy.brand_name = projectBrandName
                        if (!mentions.some((m) => m.toLowerCase() === projectBrandNameLower)) {
                            copy.mentions = [projectBrandName, ...mentions]
                        } else {
                            copy.mentions = mentions
                        }
                    }
                    return copy
                })
                normalized.sort((a, b) => {
                    const aVisibility = a?.responsesWithMention || 0
                    const bVisibility = b?.responsesWithMention || 0
                    return bVisibility - aVisibility
                })
                return normalized
            },
            normalizeBrandsFromRangeAverages(data) {
                const list = Array.isArray(data?.brands) ? data.brands : []
                const projectBrandName = (this.project?.brand?.brand_name || "").trim()
                const projectBrandNameLower = projectBrandName.toLowerCase()
                const normalized = list.map((b) => {
                    const copy = { ...(b || {}) }
                    const mentions = Array.isArray(copy.mentions)
                        ? copy.mentions
                              .map((m) => (m || "").toString().trim())
                              .filter(Boolean)
                              .filter((m) => !m.includes(". "))
                        : []
                    copy.mentions = mentions
                    copy.brand_name = copy.brand_name || copy.canonicalName || (mentions.length ? mentions[0] : null)
                    const hasProjectBrandInMentions = Boolean(projectBrandNameLower && mentions.some((m) => m.toLowerCase() === projectBrandNameLower))
                    if (hasProjectBrandInMentions) {
                        copy.canonicalName = projectBrandName
                        copy.brand_name = projectBrandName
                        if (!mentions.some((m) => m.toLowerCase() === projectBrandNameLower)) {
                            copy.mentions = [projectBrandName, ...mentions]
                        }
                    }
                    return copy
                })
                normalized.sort((a, b) => (b?.visibilityPercent || 0) - (a?.visibilityPercent || 0))
                return normalized
            },
            async fetchBrandsRangeAverages(project_id) {
                const range = this.computeBrandMetricsRanges()
                if (!range) return { brands: [] }
                const baseUrl =
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/brandvisibility/range-averages"
                const post_data = {
                    company_id: localStorage.company_id,
                    project_id: project_id || this.$route.params.id,
                    platform: this.selectedPlatform?.key === "all" ? null : this.selectedPlatform?.key,
                    tags: this.selectedTags && this.selectedTags.length ? this.selectedTags : null,
                    tagsOperator: this.tagOperator,
                    currentRange: range.current,
                    previousRange: range.previous
                }
                try {
                    const resp = await this.$helpers.axiosPost(baseUrl, post_data)
                    return resp?.data || { brands: [] }
                } catch (e) {
                    console.error("fetchBrandsRangeAverages error", e)
                    return { brands: [] }
                }
            },
            async setBrandData(project_id, category_id = null) {
                // Neue Markendaten heissen: gemerkte Plattform-Reihen sind nicht mehr gueltig.
                this.clearSeriesMemo()
                try {
                    let normalized
                    if (this.dashboardMode === "average") {
                        const data = await this.fetchBrandsRangeAverages(project_id)
                        normalized = this.normalizeBrandsFromRangeAverages(data)
                        if (!normalized.length) {
                            // fallback to snapshot if no range data
                            const bdata = await this.fetchBrands(project_id, category_id, null)
                            normalized = this.normalizeBrandsFromBrandData(bdata)
                        }
                    } else {
                        const bdata = await this.fetchBrands(project_id, category_id, null)
                        normalized = this.normalizeBrandsFromBrandData(bdata)
                    }
                    this.brands = normalized
                    this.tableBrands = normalized
                    if (this.brands[0]?.sentimentAvg != null) {
                        this.sentimentAvailable = true
                    } else {
                        this.sentimentAvailable = false
                    }
                    // Top 5 Brands auswählen
                    const topBrands = []
                    const seen = new Set()
                    for (const b of this.brands) {
                        const key = (b._id && b._id.toString()) || (b.canonicalName && b.canonicalName.toLowerCase()) || (b.mentions && b.mentions[0] && b.mentions[0].toLowerCase()) || null
                        if (!key || seen.has(key)) continue
                        seen.add(key)
                        topBrands.push(b)
                        if (topBrands.length >= 5) break
                    }

                    const projectBrandName = (this.project?.brand?.brand_name || "").toLowerCase().trim()
                    // Brand aus project.brand.brand_name suchen (robust: canonicalName, brand_name, mentions)
                    let mainBrand = this.brands.find((b) => {
                        const canonical = (b?.canonicalName || "").toLowerCase().trim()
                        const brandName = (b?.brand_name || "").toLowerCase().trim()
                        const mentions = Array.isArray(b?.mentions) ? b.mentions.map((m) => (m || "").toLowerCase().trim()) : []
                        return projectBrandName && (canonical === projectBrandName || brandName === projectBrandName || mentions.includes(projectBrandName))
                    })

                    // Falls die Projekt-Brand noch nicht in den geladenen Brands vorkommt, trotzdem hinzufügen
                    if (!mainBrand && this.project?.brand?.brand_name) {
                        mainBrand = {
                            canonicalName: this.project.brand.brand_name,
                            brand_name: this.project.brand.brand_name,
                            brand_domain: this.project?.brand?.brand_domain || null,
                            mentions: [this.project.brand.brand_name]
                        }
                    }

                    if (mainBrand) {
                        const mainKey = this.brandKey(mainBrand) || (mainBrand?.canonicalName || "").toLowerCase()
                        const exists = topBrands.some((b) => {
                            const bKey = this.brandKey(b) || (b?.canonicalName || "").toLowerCase()
                            return bKey && bKey === mainKey
                        })
                        if (!exists) topBrands.push(mainBrand)
                    }

                    const nextBrandKeys = topBrands.map((b) => this.brandKey(b)).filter(Boolean)
                    const currentBrandKeys = (this.selectedBrands || []).map((b) => this.brandKey(b)).filter(Boolean)
                    const selectedBrandsChanged = nextBrandKeys.length !== currentBrandKeys.length || nextBrandKeys.some((key, index) => key !== currentBrandKeys[index])
                    if (selectedBrandsChanged) {
                        this.selectedBrands = topBrands
                    }

                    // Falls Favoriten-Modus aktiv ist und Favoriten hinterlegt sind, diese bevorzugt selektieren
                    if (this.brandDisplayMode === "favorites") {
                        const favObjects = this.getFavoriteBrandObjects()
                        if (favObjects.length) {
                            const favKeys = favObjects.map((b) => this.brandKey(b)).filter(Boolean)
                            const curKeys = (this.selectedBrands || []).map((b) => this.brandKey(b)).filter(Boolean)
                            const favChanged = favKeys.length !== curKeys.length || favKeys.some((key, index) => key !== curKeys[index])
                            if (favChanged) this.selectedBrands = favObjects
                        }
                    }
                    this.ensureTagPerformanceBrandSelection()
                    // this.buildMetricsMatrix()
                    console.log(this.project)

                    this.mentionBrand = mainBrand ? mainBrand : this.selectedBrands[0]
                    this.buildBrandMetricCards()

                    console.log("mainBrand", mainBrand)

                    if (this.activeButton === "Brands" && this.search) {
                        await this.setBrandTableData(project_id, category_id)
                    }

                    //TODO: sort by visiblity
                    // Duplikate entfernen (z.B. falls mainBrand schon in Top 5 war)
                    this.loading = false
                    console.log("this.brands:  ", this.brands)
                } catch (error) {
                    this.loading = false
                    console.error("Error fetching brands:", error)
                }
            },
            async setBrandTableData(project_id, category_id = null) {
                try {
                    if (this.dashboardMode === "average") {
                        const data = await this.fetchBrandsRangeAverages(project_id)
                        let normalized = this.normalizeBrandsFromRangeAverages(data)
                        const s = (this.search || "").trim().toLowerCase()
                        if (s) {
                            normalized = normalized.filter((b) => {
                                const cn = (b?.canonicalName || "").toLowerCase()
                                const bn = (b?.brand_name || "").toLowerCase()
                                if (cn.includes(s) || bn.includes(s)) return true
                                if (Array.isArray(b?.mentions) && b.mentions.some((m) => (m || "").toLowerCase().includes(s))) return true
                                return false
                            })
                        }
                        this.tableBrands = normalized
                        return
                    }
                    const bdata = await this.fetchBrands(project_id, category_id, this.search)
                    this.tableBrands = this.normalizeBrandsFromBrandData(bdata)
                } catch (error) {
                    console.error("Error fetching brand table data:", error)
                }
            },
            fetchProject(project_id) {
                return new Promise(async (resolve, reject) => {
                    try {
                        const url =
                            (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                                this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                            ) == -1
                                ? process.env.VUE_APP_MONITORING_API_URL
                                : process.env.VUE_APP_APIURL + "/monitoring-api") + "/project/one"
                        let post_data = {
                            company_id: localStorage.company_id,
                            project_id: project_id
                        }
                        let response_data = await this.$helpers.axiosPost(url, post_data)
                        resolve(response_data.data)
                    } catch (error) {
                        console.log(error)
                        reject(error)
                    }
                })
            },
            // ...existing code...
            fetchBrands(project_id, category_id = null, search = null) {
                return new Promise(async (resolve, reject) => {
                    try {
                        const url =
                            (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                                this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                            ) == -1
                                ? process.env.VUE_APP_MONITORING_API_URL
                                : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/brandvisibility/by/date"
                        let post_data = {
                            company_id: localStorage.company_id,
                            project_id: project_id || this.$route.params.id,
                            tags: this.selectedTags && this.selectedTags.length ? this.selectedTags : null,
                            tagsOperator: this.tagOperator,
                            search: search || null
                        }
                        if (this.selectedPlatform.key === "all") {
                            post_data.platform = null
                            const importDatesByPlatformObj = this.buildImportDatesByPlatform(2) || {}
                            post_data.importDatesByPlatform = Object.entries(importDatesByPlatformObj).map(([platform, importDates]) => ({ platform, importDates }))
                        } else {
                            post_data.platform = this.selectedPlatform.key
                            post_data.importDates = this.selectedDbDates
                        }
                        let response_data = await this.$helpers.axiosPost(url, post_data)
                        resolve(response_data.data)
                    } catch (error) {
                        console.log(error)
                        reject(error)
                    }
                })
            },
            allowedDates(val) {
                return this.availableDates.includes(val)
            },
            // Favicon-Domain aus officialWebsite oder aus erster Mention extrahieren
            getBrandFaviconDomain(item) {
                // 1. officialWebsite bevorzugen
                if (item.metadata && item.metadata.officialWebsite) {
                    return item.metadata.officialWebsite
                        .replace(/^https?:\/\//, "")
                        .replace(/^www\./, "")
                        .split("/")[0]
                }
                // 2. Domains aus mentions extrahieren, Duplikate vermeiden
                if (item.mentions && item.mentions.length) {
                    const domains = item.mentions
                        .map(
                            (m) =>
                                m
                                    .replace(/^https?:\/\//, "")
                                    .replace(/^www\./, "")
                                    .split("/")[0]
                        )
                        .filter((m) => /^[\w-]+\.[a-z]{2,}$/.test(m))
                    const uniqueDomains = [...new Set(domains)]
                    if (uniqueDomains.length) return uniqueDomains[0]
                    const firstMention = typeof item.mentions[0] === "string" ? item.mentions[0].trim() : ""
                    let fallbackMention = firstMention
                    if (firstMention.includes(" ") && item.mentions.length > 1) {
                        const nextMention = item.mentions.slice(1).find((m) => typeof m === "string" && m.trim())
                        if (nextMention) fallbackMention = nextMention.trim()
                    }
                    return fallbackMention + "." + this.project.langloc.country_code.toLowerCase()
                }
                return null
            },
            openManageBrandsDialog() {
                this.showManageBrandsDialog = true
            },
            normalizeManagedBrandEntry(entry) {
                const normalize = (v) => String(v || "").trim()
                const normalizeDomain = (v) =>
                    String(v || "")
                        .trim()
                        .toLowerCase()
                        .replace(/^https?:\/\//, "")
                        .replace(/^www\./, "")
                        .split(/[/?#]/)[0]
                const normalizeMentionToken = (v) =>
                    String(v || "")
                        .trim()
                        .toLowerCase()
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "")
                        .replace(/[’´`ʻʼ]/g, "'")
                        .replace(/\s+/g, " ")
                const canonicalName = normalize(entry?.canonicalName || entry?.brand_name || entry?.name)

                const mentionsSet = new Set()
                ;(Array.isArray(entry?.mentions) ? entry.mentions : []).forEach((m) => {
                    let raw = ""
                    if (typeof m === "string") raw = normalize(m)
                    else if (m?.text?.content) raw = normalize(m.text.content)
                    else if (m?.content) raw = normalize(m.content)
                    else if (m?.name) raw = normalize(m.name)
                    const norm = normalizeMentionToken(raw)
                    if (norm) mentionsSet.add(norm)
                })
                const mentions = Array.from(mentionsSet).sort((a, b) => a.localeCompare(b))

                const blacklistedSet = new Set()
                ;(Array.isArray(entry?.blacklisted) ? entry.blacklisted : []).forEach((m) => {
                    const norm = normalizeMentionToken(typeof m === "string" ? m : "")
                    if (norm) blacklistedSet.add(norm)
                })
                const blacklisted = Array.from(blacklistedSet).sort((a, b) => a.localeCompare(b))
                const brand_domain = normalizeDomain(entry?.brand_domain || entry?.metadata?.officialWebsite || entry?.metadata?.website || "")

                return {
                    canonicalName,
                    mentions,
                    blacklisted,
                    brand_domain
                }
            },
            buildManagedBrandsBaselineMap() {
                const map = new Map()
                const baseRows = Array.isArray(this.brands) ? this.brands : []
                const editedRows = this.managedBrandsEditedLoaded && Array.isArray(this.managedBrandsEdited) ? this.managedBrandsEdited : []

                // 1) Immer komplette aktuelle Brand-Basis aufnehmen
                baseRows.forEach((entry) => {
                    const normalized = this.normalizeManagedBrandEntry(entry)
                    if (!normalized.canonicalName) return
                    map.set(normalized.canonicalName.toLowerCase(), normalized)
                })

                // 2) Gespeicherte Edits darüberlegen (inkl. domain/mentions overrides)
                editedRows.forEach((entry) => {
                    const normalized = this.normalizeManagedBrandEntry(entry)
                    if (!normalized.canonicalName) return
                    map.set(normalized.canonicalName.toLowerCase(), normalized)
                })

                return map
            },
            computeManagedBrandsEditedDelta(payload) {
                const baselineMap = this.buildManagedBrandsBaselineMap()
                const rows = Array.isArray(payload) ? payload : []
                const result = []
                const currentKeys = new Set()
                const resultKeys = new Set()
                const pushUnique = (row) => {
                    if (!row?.canonicalName) return
                    const key = row.canonicalName.toLowerCase()
                    if (resultKeys.has(key)) return
                    resultKeys.add(key)
                    result.push(row)
                }

                rows.forEach((entry) => {
                    const current = this.normalizeManagedBrandEntry(entry)
                    if (!current.canonicalName) return
                    const key = current.canonicalName.toLowerCase()
                    currentKeys.add(key)

                    const baseline = baselineMap.get(key)
                    if (!baseline) {
                        pushUnique(current)
                        return
                    }

                    const mentionsEqual = JSON.stringify(current.mentions) === JSON.stringify(baseline.mentions)
                    const blacklistedEqual = JSON.stringify(current.blacklisted) === JSON.stringify(baseline.blacklisted)
                    const domainEqual = (current.brand_domain || "") === (baseline.brand_domain || "")

                    if (!mentionsEqual || !blacklistedEqual || !domainEqual) {
                        pushUnique(current)
                    }
                })

                const deletedKeys = []
                baselineMap.forEach((baseline, key) => {
                    if (currentKeys.has(key)) return
                    if (!baseline?.canonicalName) return
                    deletedKeys.push(key)
                    pushUnique({
                        canonicalName: baseline.canonicalName,
                        mentions: [],
                        blacklisted: [],
                        brand_domain: "",
                        deleted: true
                    })
                })

                // Wenn eine Source-Brand entfernt wurde (Merge), muss die Ziel-Brand
                // ebenfalls als edited gespeichert werden, auch wenn sie zur Baseline
                // identisch wirkt. Sonst kommt die Source nach Reload wieder.
                if (deletedKeys.length) {
                    const deletedSet = new Set(deletedKeys)
                    rows.forEach((entry) => {
                        const current = this.normalizeManagedBrandEntry(entry)
                        if (!current?.canonicalName) return
                        const mentionKeys = new Set((Array.isArray(current.mentions) ? current.mentions : []).map((m) => (m || "").toString().toLowerCase()).filter(Boolean))
                        const referencesDeleted = Array.from(deletedSet).some((k) => mentionKeys.has(k))
                        if (referencesDeleted) {
                            pushUnique(current)
                        }
                    })
                }

                return result
            },
            async fetchAllKnownBrands(project_id) {
                if (!project_id) {
                    this.allKnownBrands = []
                    return []
                }
                try {
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/brands/all/frontend/get"
                    const post_data = {
                        company_id: localStorage.company_id,
                        project_id
                    }
                    const resp = await this.$helpers.axiosPost(url, post_data)
                    this.allKnownBrands = Array.isArray(resp?.data?.brands) ? resp.data.brands : []
                    return this.allKnownBrands
                } catch (e) {
                    console.error("fetchAllKnownBrands error", e)
                    this.allKnownBrands = []
                    return []
                }
            },
            async fetchManagedBrandsEdited(project_id) {
                if (!project_id) {
                    this.managedBrandsEdited = []
                    this.managedBrandsEditedLoaded = true
                    return []
                }
                try {
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/brands/edited/frontend/get"

                    const post_data = {
                        company_id: localStorage.company_id,
                        project_id: project_id
                    }

                    const resp = await this.$helpers.axiosPost(url, post_data)
                    const rows = Array.isArray(resp?.data?.brands) ? resp.data.brands : []
                    this.managedBrandsEdited = rows.map((b) => ({ ...this.normalizeManagedBrandEntry(b), frontend_edited: true }))
                    this.managedBrandsEditedLoaded = true
                    return this.managedBrandsEdited
                } catch (e) {
                    console.error("fetchManagedBrandsEdited error", e)
                    this.managedBrandsEdited = []
                    this.managedBrandsEditedLoaded = true
                    return []
                }
            },
            async fetchBrandFavorites(project_id) {
                if (!project_id) {
                    this.favoriteBrands = []
                    this.brandColors = {}
                    return
                }
                try {
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/brands/favorites/get"
                    const post_data = {
                        company_id: localStorage.company_id,
                        project_id
                    }
                    const resp = await this.$helpers.axiosPost(url, post_data)
                    this.favoriteBrands = Array.isArray(resp?.data?.favorites)
                        ? resp.data.favorites
                              .map((v) =>
                                  String(v || "")
                                      .trim()
                                      .toLowerCase()
                              )
                              .filter(Boolean)
                        : []
                    this.brandColors = resp?.data?.colors && typeof resp.data.colors === "object" ? resp.data.colors : {}
                } catch (e) {
                    console.error("fetchBrandFavorites error", e)
                    this.favoriteBrands = []
                    this.brandColors = {}
                }
            },
            onBrandFavoritesUpdated(payload) {
                const safe = payload && typeof payload === "object" ? payload : {}
                this.favoriteBrands = Array.isArray(safe.favorites)
                    ? safe.favorites
                          .map((v) =>
                              String(v || "")
                                  .trim()
                                  .toLowerCase()
                          )
                          .filter(Boolean)
                    : []
                this.brandColors = safe.colors && typeof safe.colors === "object" ? { ...safe.colors } : {}
                this.persistBrandFavorites()
                // Im Favoriten-Modus die Auswahl sofort neu anwenden, damit eine neu
                // favorisierte Brand direkt im Chart erscheint (ohne Umschalten des Modus).
                if (this.brandDisplayMode === "favorites") {
                    this.$nextTick(() => this.applyBrandDisplaySelection())
                }
            },
            async persistBrandFavorites() {
                if (!this.project?._id || this._savingBrandFavorites) return
                this._savingBrandFavorites = true
                try {
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/brands/favorites/save"
                    const post_data = {
                        company_id: localStorage.company_id,
                        project_id: this.project._id,
                        favorites: this.favoriteBrands,
                        colors: this.brandColors
                    }
                    await this.$helpers.axiosPost(url, post_data)
                } catch (e) {
                    console.error("persistBrandFavorites error", e)
                } finally {
                    this._savingBrandFavorites = false
                }
            },
            isFavoriteBrand(brand) {
                if (!brand || !Array.isArray(this.favoriteBrands) || !this.favoriteBrands.length) return false
                // Favoriten werden ausschliesslich per canonicalName gespeichert (siehe ManageBrandsDialog.brandColorKey).
                // Deshalb NUR exakt darüber matchen – ein Abgleich über mentions führt zu Fehltreffern,
                // z.B. matcht die Marke "Mercedes" den Favoriten "Mercedes Benz", wenn deren mentions "mercedes benz" enthalten.
                const canonical = (brand?.canonicalName || brand?.brand_name || "").toString().trim().toLowerCase()
                return !!canonical && this.favoriteBrands.includes(canonical)
            },
            getFavoriteBrandObjects() {
                const source = Array.isArray(this.brandSelectorOptions) ? this.brandSelectorOptions : []
                const favs = source.filter((b) => this.isFavoriteBrand(b))
                const seen = new Set()
                const unique = []
                favs.forEach((b) => {
                    const key = this.brandKey(b) || (b?.canonicalName || "").toLowerCase()
                    if (!key || seen.has(key)) return
                    seen.add(key)
                    unique.push(b)
                })
                return unique
            },
            getTopBrandObjects() {
                const topBrands = []
                const seen = new Set()
                for (const b of Array.isArray(this.brands) ? this.brands : []) {
                    const key = (b._id && b._id.toString()) || (b.canonicalName && b.canonicalName.toLowerCase()) || (b.mentions && b.mentions[0] && b.mentions[0].toLowerCase()) || null
                    if (!key || seen.has(key)) continue
                    seen.add(key)
                    topBrands.push(b)
                    if (topBrands.length >= 5) break
                }
                const projectBrandName = (this.project?.brand?.brand_name || "").toLowerCase().trim()
                let mainBrand = (this.brands || []).find((b) => {
                    const canonical = (b?.canonicalName || "").toLowerCase().trim()
                    const brandName = (b?.brand_name || "").toLowerCase().trim()
                    const mentions = Array.isArray(b?.mentions) ? b.mentions.map((m) => (m || "").toLowerCase().trim()) : []
                    return projectBrandName && (canonical === projectBrandName || brandName === projectBrandName || mentions.includes(projectBrandName))
                })
                if (!mainBrand && this.project?.brand?.brand_name) {
                    mainBrand = {
                        canonicalName: this.project.brand.brand_name,
                        brand_name: this.project.brand.brand_name,
                        brand_domain: this.project?.brand?.brand_domain || null,
                        mentions: [this.project.brand.brand_name]
                    }
                }
                if (mainBrand) {
                    const mainKey = this.brandKey(mainBrand) || (mainBrand?.canonicalName || "").toLowerCase()
                    const exists = topBrands.some((b) => (this.brandKey(b) || (b?.canonicalName || "").toLowerCase()) === mainKey)
                    if (!exists) topBrands.push(mainBrand)
                }
                return topBrands
            },
            applyBrandDisplaySelection() {
                let next
                if (this.brandDisplayMode === "favorites") {
                    next = this.getFavoriteBrandObjects()
                    if (!next.length) {
                        // Keine Favoriten hinterlegt -> Hinweis, Auswahl unverändert lassen
                        this.createNotification('No favorite brands set yet. Mark favorites in "Manage Brands".', "mdi-star-outline", "#FCBA03")
                        return
                    }
                } else {
                    next = this.getTopBrandObjects()
                }
                const nextKeys = next.map((b) => this.brandKey(b)).filter(Boolean)
                const currentKeys = (this.selectedBrands || []).map((b) => this.brandKey(b)).filter(Boolean)
                const changed = nextKeys.length !== currentKeys.length || nextKeys.some((key, index) => key !== currentKeys[index])
                if (changed) this.selectedBrands = next
            },
            async onManagedBrandsSaved(payload) {
                this.showManageBrandsDialog = false

                this.managedBrandsEdited = this.computeManagedBrandsEditedDelta(payload)
                this.managedBrandsEdited = this.managedBrandsEdited.map((b) => ({ ...b, frontend_edited: true }))

                // Wenn keine echten Brand-Edits vorliegen (nur Favoriten/Farben geändert),
                // ist kein Backend-Save + History-Reconcile + Reload nötig.
                if (!this.managedBrandsEdited.length) {
                    this.createNotification("Favorites saved.", "mdi-check-circle", "primary")
                    return
                }

                this.createNotification("Saving brand edits. This may take a while.", "mdi-progress-clock", "primary")

                if (!this.project?._id) {
                    this.createNotification("Brand edits saved locally.", "mdi-check-circle", "primary")
                    return
                }

                try {
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/brands/edited/frontend/save"

                    const post_data = {
                        company_id: localStorage.company_id,
                        project_id: this.project._id,
                        brands: this.managedBrandsEdited
                    }
                    this.clearSeriesMemo()

                    const saveResp = await this.$helpers.axiosPost(url, post_data)
                    await this.fetchManagedBrandsEdited(this.project._id)
                    await this.fetchAllKnownBrands(this.project._id)
                    const reconciled = saveResp?.data?.historyReconciled || {}
                    const rewritten = Number(reconciled?.rewritten || 0)
                    const affected = Number(reconciled?.affected || 0)
                    const reason = reconciled?.reason ? ` (${reconciled.reason})` : ""
                    this.createNotification(`Brand edits saved.`, "mdi-check-circle", "primary")
                    setTimeout(() => {
                        this.$router.go()
                    }, 350)
                } catch (e) {
                    console.error("onManagedBrandsSaved error", e)
                    this.createNotification("Brand edits could not be saved to backend. They are kept locally.", "mdi-alert-circle-outline", "error")
                }
            },
            async onManagedBrandsReset() {
                if (!this.project?._id || this.managedBrandsResetLoading) return

                this.managedBrandsResetLoading = true
                this.createNotification("Resetting brands. This may take a while.", "mdi-progress-clock", "primary")

                try {
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/brands/reset"

                    const post_data = {
                        company_id: localStorage.company_id,
                        project_id: this.project._id,
                        clear_visibility: false,
                        only_frontend_edited: false
                    }

                    this.clearSeriesMemo()
                    await this.$helpers.axiosPost(url, post_data)
                    this.managedBrandsEdited = []
                    this.managedBrandsEditedLoaded = true
                    this.showManageBrandsDialog = false
                    this.createNotification("Brands reset. They will be rebuilt on next update.", "mdi-check-circle", "primary")

                    setTimeout(() => {
                        this.$router.go()
                    }, 350)
                } catch (e) {
                    console.error("onManagedBrandsReset error", e)
                    this.createNotification("Brand reset failed.", "mdi-alert-circle-outline", "error")
                } finally {
                    this.managedBrandsResetLoading = false
                }
            },
            openImportPromptTagsDialog() {
                this.importPromptTagsFile = null
                this.importPromptTagsRows = []
                this.importPromptTagsSummary = ""
                this.importPromptTagsError = ""
                this.importPromptTagsCurrentPlatformOnly = false
                this.showImportPromptTagsDialog = true
            },
            downloadImportPromptTagsTemplate() {
                const csv = [
                    "prompt,tags",
                    '"What are the best running shoes for beginners?","sports,beginner,shopping"',
                    '"Compare iPhone 16 and Samsung S26 features","tech;comparison"',
                    '"How to improve local SEO for a small business?","seo|marketing|local"'
                ].join("\n")

                const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
                const url = URL.createObjectURL(blob)
                const link = document.createElement("a")
                link.href = url
                link.setAttribute("download", "prompt-tags-import-example.csv")
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(url)
            },
            closeImportPromptTagsDialog() {
                if (this.importPromptTagsLoading) return
                this.showImportPromptTagsDialog = false
            },
            parseCsvLine(line, delimiter) {
                const out = []
                let cur = ""
                let inQuotes = false
                for (let i = 0; i < line.length; i++) {
                    const ch = line[i]
                    if (ch === '"') {
                        if (inQuotes && line[i + 1] === '"') {
                            cur += '"'
                            i++
                        } else {
                            inQuotes = !inQuotes
                        }
                        continue
                    }
                    if (ch === delimiter && !inQuotes) {
                        out.push(cur)
                        cur = ""
                        continue
                    }
                    cur += ch
                }
                out.push(cur)
                return out.map((v) => (v == null ? "" : String(v).trim()))
            },
            normalizePromptTagRowsFromCsvText(text) {
                const raw = String(text || "")
                    .replace(/^\uFEFF/, "")
                    .replace(/\r\n/g, "\n")
                    .replace(/\r/g, "\n")
                const lines = raw
                    .split("\n")
                    .map((l) => l.trim())
                    .filter(Boolean)
                if (lines.length < 2) {
                    return { rows: [], summary: "", error: "CSV is empty or contains no data rows." }
                }

                const headerLine = lines[0]
                const delimiters = [";", ",", "\t"]
                let delimiter = ","
                let maxCols = -1
                for (const d of delimiters) {
                    const cols = this.parseCsvLine(headerLine, d).length
                    if (cols > maxCols) {
                        maxCols = cols
                        delimiter = d
                    }
                }

                const header = this.parseCsvLine(headerLine, delimiter).map((h) => h.toLowerCase())
                const promptIdx = header.findIndex((h) => ["prompt", "keyword"].includes(h))
                const tagIdx = header.findIndex((h) => h === "tag" || h === "tags")
                if (promptIdx < 0 || tagIdx < 0) {
                    return { rows: [], summary: "", error: "Columns not found. Please use headers 'prompt' and 'tag' (or 'tags')." }
                }

                const grouped = new Map()
                let rawRows = 0
                for (let i = 1; i < lines.length; i++) {
                    const cols = this.parseCsvLine(lines[i], delimiter)
                    const prompt = (cols[promptIdx] || "").trim()
                    const tagCell = (cols[tagIdx] || "").trim()
                    if (!prompt || !tagCell) continue
                    rawRows++

                    const tags = tagCell
                        .split(/[;,|]/g)
                        .map((t) => t.trim())
                        .filter(Boolean)
                    if (!tags.length) continue

                    const curr = grouped.get(prompt) || []
                    grouped.set(prompt, Array.from(new Set(curr.concat(tags))))
                }

                const rows = Array.from(grouped.entries()).map(([prompt, tags]) => ({ prompt, tags }))
                const uniqueTags = new Set(rows.flatMap((r) => r.tags || []))
                const summary = `${rows.length} prompts, ${uniqueTags.size} unique tags (${rawRows} CSV rows processed).`
                if (!rows.length) {
                    return { rows: [], summary: "", error: "No valid prompt/tag rows found." }
                }
                return { rows, summary, error: "" }
            },
            async onImportPromptTagsFileChange(fileOrFiles) {
                try {
                    this.importPromptTagsError = ""
                    this.importPromptTagsSummary = ""
                    this.importPromptTagsRows = []

                    const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles
                    if (!file) return
                    const text = await file.text()
                    const parsed = this.normalizePromptTagRowsFromCsvText(text)
                    this.importPromptTagsRows = parsed.rows
                    this.importPromptTagsSummary = parsed.summary
                    this.importPromptTagsError = parsed.error
                } catch (e) {
                    console.error("onImportPromptTagsFileChange error", e)
                    this.importPromptTagsRows = []
                    this.importPromptTagsSummary = ""
                    this.importPromptTagsError = "Could not read CSV file."
                }
            },
            async submitImportPromptTags() {
                if (!this.importPromptTagsRows.length || !this.project?._id) return
                this.importPromptTagsLoading = true
                try {
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/modify/tags/by-prompt"

                    const payload = {
                        company_id: localStorage.company_id,
                        project_id: this.project._id,
                        rows: this.importPromptTagsRows,
                        mode: "add",
                        platform: this.importPromptTagsCurrentPlatformOnly && this.selectedPlatform && this.selectedPlatform.key !== "all" ? this.selectedPlatform.key : null
                    }

                    const resp = await this.$helpers.axiosPost(url, payload)
                    const processedPrompts = resp?.data?.processedPrompts || this.importPromptTagsRows.length
                    const modifiedResp = resp?.data?.results?.responses?.modified ?? 0
                    const modifiedKw = resp?.data?.results?.keywords?.modified ?? 0

                    this.createNotification(`${processedPrompts} prompts imported. Updates: ${modifiedResp} prompt responses, ${modifiedKw} project keywords.`, "mdi-check-circle", "primary", {
                        persistent: true,
                        actionLabel: "OK"
                    })

                    this.showImportPromptTagsDialog = false
                    await this.fetchData(this.project._id)
                } catch (e) {
                    console.error("submitImportPromptTags error", e)
                    this.importPromptTagsError = e?.response?.data?.error || "Import failed."
                } finally {
                    this.importPromptTagsLoading = false
                }
            },
            openAddPrompts() {
                this.resetPromptCollector()
                this.showAddPromptsDialog = true
            },
            closeAddPrompts() {
                if (!this.savingPrompts) this.showAddPromptsDialog = false
            },
            resetPromptCollector() {
                this.collectedPromptList = []
                this.mergedNewPrompts = []
            },
            onPromptsCollected(list) {
                this.collectedPromptList = Array.isArray(list) ? list : []
                this.updateMergedPrompts()
            },
            promptsAddedDetailed(objs) {
                this.promptObjects = Array.isArray(objs) ? objs : []
            },
            updateMergedPrompts() {
                this.mergedNewPrompts = [...new Set(this.collectedPromptList.map((p) => (p || "").trim()).filter(Boolean))]
            },
            async saveNewPrompts() {
                if (!this.mergedNewPrompts.length || !this.project?._id) return
                const keywordsToSave = this.promptObjects.length ? this.promptObjects.map((p) => p.keyword) : this.mergedNewPrompts
                const tooLongCount = keywordsToSave.filter((p) => (p || "").length > 700).length
                if (tooLongCount) {
                    this.createNotification(`${tooLongCount} prompt(s) exceed 700 characters. Please shorten them before saving.`, "mdi-alert-circle-outline", "#FCBA03", {
                        persistent: true,
                        actionLabel: "OK"
                    })
                    return
                }
                this.savingPrompts = true
                try {
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/project/add/keywords/later"
                    const post_data = {
                        company_id: localStorage.company_id,
                        project_id: this.project._id,
                        category: null,
                        keywords: keywordsToSave
                    }
                    if (this.promptObjects.length) {
                        post_data.keywordsWithTags = this.promptObjects
                    }
                    const resp = await this.$helpers.axiosPost(url, post_data)
                    if (resp.data?.updated) {
                        const addedCount = Array.isArray(post_data.keywords) ? post_data.keywords.length : 0
                        const label = addedCount === 1 ? "Prompt" : "Prompts"
                        // pausiert: gespeichert, aber erst nach dem Fortsetzen abgefragt und gebucht
                        const followUp = resp.data?.paused
                            ? "The project is paused - the new prompts are fetched and use credits once you resume it."
                            : "The new prompts will be visible and displayed in the next update."
                        this.createNotification(`${addedCount} ${label} added. ${followUp}`, "mdi-check-circle", "primary", {
                            persistent: true,
                            actionLabel: "OK"
                        })
                        await this.fetchData(this.project._id)
                        this.showAddPromptsDialog = false
                    } else if (resp.data?.error === "not_enough_credits") {
                        const remaining = Math.max(0, (Number(resp.data.credits) || 0) - (Number(resp.data.count) || 0))
                        this.createNotification(
                            `Not enough credits. You have ${remaining.toLocaleString("de-DE")} credits left, which is not enough for the selected prompts.`,
                            "mdi-alert-circle-outline",
                            "error",
                            { persistent: true, actionLabel: "OK" }
                        )
                    } else {
                        throw new Error()
                    }
                } catch {
                    this.createNotification("Error while saving data. Please try again or contact support!", "mdi-alert-circle-outline", "error", { persistent: true, actionLabel: "OK" })
                } finally {
                    this.savingPrompts = false
                }
            },
            createNotification(message, icon = "mdi-information", color = "primary", opts = {}) {
                if (!this.notification) this.notification = {}
                this.notification.show = true
                this.notification.color = color
                this.notification.icon = icon
                this.notification.message = message
                this.notification.timeout = opts.persistent ? -1 : Number.isFinite(opts.timeout) ? opts.timeout : 4000
                this.notification.actionLabel = opts.actionLabel || "OK"
            },
            getPlatformInfo(name) {
                const key = (name || "").toString().trim().toLowerCase()
                if (key === "all") {
                    return {
                        logo: null,
                        label: "all models",
                        status: null
                    }
                }
                const platforms = {
                    openai: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.639"><path fill="#fff" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.64 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill-rule="nonzero" d="M412.037 221.764a90.834 90.834 0 004.648-28.67 90.79 90.79 0 00-12.443-45.87c-16.37-28.496-46.738-46.089-79.605-46.089-6.466 0-12.943.683-19.264 2.04a90.765 90.765 0 00-67.881-30.515h-.576c-.059.002-.149.002-.216.002-39.807 0-75.108 25.686-87.346 63.554-25.626 5.239-47.748 21.31-60.682 44.03a91.873 91.873 0 00-12.407 46.077 91.833 91.833 0 0023.694 61.553 90.802 90.802 0 00-4.649 28.67 90.804 90.804 0 0012.442 45.87c16.369 28.504 46.74 46.087 79.61 46.087a91.81 91.81 0 0019.253-2.04 90.783 90.783 0 0067.887 30.516h.576l.234-.001c39.829 0 75.119-25.686 87.357-63.588 25.626-5.242 47.748-21.312 60.682-44.033a91.718 91.718 0 0012.383-46.035 91.83 91.83 0 00-23.693-61.553l-.004-.005zM275.102 413.161h-.094a68.146 68.146 0 01-43.611-15.8 56.936 56.936 0 002.155-1.221l72.54-41.901a11.799 11.799 0 005.962-10.251V241.651l30.661 17.704c.326.163.55.479.596.84v84.693c-.042 37.653-30.554 68.198-68.21 68.273h.001zm-146.689-62.649a68.128 68.128 0 01-9.152-34.085c0-3.904.341-7.817 1.005-11.663.539.323 1.48.897 2.155 1.285l72.54 41.901a11.832 11.832 0 0011.918-.002l88.563-51.137v35.408a1.1 1.1 0 01-.438.94l-73.33 42.339a68.43 68.43 0 01-34.11 9.12 68.359 68.359 0 01-59.15-34.11l-.001.004zm-19.083-158.36a68.044 68.044 0 0135.538-29.934c0 .625-.036 1.731-.036 2.5v83.801l-.001.07a11.79 11.79 0 005.954 10.242l88.564 51.13-30.661 17.704a1.096 1.096 0 01-1.034.093l-73.337-42.375a68.36 68.36 0 01-34.095-59.143 68.412 68.412 0 019.112-34.085l-.004-.003zm251.907 58.621l-88.563-51.137 30.661-17.697a1.097 1.097 0 011.034-.094l73.337 42.339c21.109 12.195 34.132 34.746 34.132 59.132 0 28.604-17.849 54.199-44.686 64.078v-86.308c.004-.032.004-.065.004-.096 0-4.219-2.261-8.119-5.919-10.217zm30.518-45.93c-.539-.331-1.48-.898-2.155-1.286l-72.54-41.901a11.842 11.842 0 00-5.958-1.611c-2.092 0-4.15.558-5.957 1.611l-88.564 51.137v-35.408l-.001-.061a1.1 1.1 0 01.44-.88l73.33-42.303a68.301 68.301 0 0134.108-9.129c37.704 0 68.281 30.577 68.281 68.281a68.69 68.69 0 01-.984 11.545v.005zm-191.843 63.109l-30.668-17.704a1.09 1.09 0 01-.596-.84v-84.692c.016-37.685 30.593-68.236 68.281-68.236a68.332 68.332 0 0143.689 15.804 63.09 63.09 0 00-2.155 1.222l-72.54 41.9a11.794 11.794 0 00-5.961 10.248v.068l-.05 102.23zm16.655-35.91l39.445-22.782 39.444 22.767v45.55l-39.444 22.767-39.445-22.767v-45.535z"/></svg>`,
                        label: "OpenAI",
                        status: this.project.platforms.includes("openai") ? "included" : "upgrade"
                    },
                    chatgpt: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.639"><path fill="#fff" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.64 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill-rule="nonzero" d="M412.037 221.764a90.834 90.834 0 004.648-28.67 90.79 90.79 0 00-12.443-45.87c-16.37-28.496-46.738-46.089-79.605-46.089-6.466 0-12.943.683-19.264 2.04a90.765 90.765 0 00-67.881-30.515h-.576c-.059.002-.149.002-.216.002-39.807 0-75.108 25.686-87.346 63.554-25.626 5.239-47.748 21.31-60.682 44.03a91.873 91.873 0 00-12.407 46.077 91.833 91.833 0 0023.694 61.553 90.802 90.802 0 00-4.649 28.67 90.804 90.804 0 0012.442 45.87c16.369 28.504 46.74 46.087 79.61 46.087a91.81 91.81 0 0019.253-2.04 90.783 90.783 0 0067.887 30.516h.576l.234-.001c39.829 0 75.119-25.686 87.357-63.588 25.626-5.242 47.748-21.312 60.682-44.033a91.718 91.718 0 0012.383-46.035 91.83 91.83 0 00-23.693-61.553l-.004-.005zM275.102 413.161h-.094a68.146 68.146 0 01-43.611-15.8 56.936 56.936 0 002.155-1.221l72.54-41.901a11.799 11.799 0 005.962-10.251V241.651l30.661 17.704c.326.163.55.479.596.84v84.693c-.042 37.653-30.554 68.198-68.21 68.273h.001zm-146.689-62.649a68.128 68.128 0 01-9.152-34.085c0-3.904.341-7.817 1.005-11.663.539.323 1.48.897 2.155 1.285l72.54 41.901a11.832 11.832 0 0011.918-.002l88.563-51.137v35.408a1.1 1.1 0 01-.438.94l-73.33 42.339a68.43 68.43 0 01-34.11 9.12 68.359 68.359 0 01-59.15-34.11l-.001.004zm-19.083-158.36a68.044 68.044 0 0135.538-29.934c0 .625-.036 1.731-.036 2.5v83.801l-.001.07a11.79 11.79 0 005.954 10.242l88.564 51.13-30.661 17.704a1.096 1.096 0 01-1.034.093l-73.337-42.375a68.36 68.36 0 01-34.095-59.143 68.412 68.412 0 019.112-34.085l-.004-.003zm251.907 58.621l-88.563-51.137 30.661-17.697a1.097 1.097 0 011.034-.094l73.337 42.339c21.109 12.195 34.132 34.746 34.132 59.132 0 28.604-17.849 54.199-44.686 64.078v-86.308c.004-.032.004-.065.004-.096 0-4.219-2.261-8.119-5.919-10.217zm30.518-45.93c-.539-.331-1.48-.898-2.155-1.286l-72.54-41.901a11.842 11.842 0 00-5.958-1.611c-2.092 0-4.15.558-5.957 1.611l-88.564 51.137v-35.408l-.001-.061a1.1 1.1 0 01.44-.88l73.33-42.303a68.301 68.301 0 0134.108-9.129c37.704 0 68.281 30.577 68.281 68.281a68.69 68.69 0 01-.984 11.545v.005zm-191.843 63.109l-30.668-17.704a1.09 1.09 0 01-.596-.84v-84.692c.016-37.685 30.593-68.236 68.281-68.236a68.332 68.332 0 0143.689 15.804 63.09 63.09 0 00-2.155 1.222l-72.54 41.9a11.794 11.794 0 00-5.961 10.248v.068l-.05 102.23zm16.655-35.91l39.445-22.782 39.444 22.767v45.55l-39.444 22.767-39.445-22.767v-45.535z"/></svg>`,
                        label: "ChatGPT",
                        status: this.project.platforms.includes("chatgpt") ? "included" : "upgrade"
                    },
                    google_ai_mode: {
                        label: "AI Mode",
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path></svg>`,
                        status: this.project.platforms.includes("google_ai_mode") ? "included" : "upgrade"
                    },
                    google: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path></svg>`,
                        label: "Google AIO",
                        status: this.project.google ? "included" : "upgrade"
                    },
                    gemini: {
                        logo: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 65 65"><mask id="maskme" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="65" height="65"><path d="M32.447 0c.68 0 1.273.465 1.439 1.125a38.904 38.904 0 001.999 5.905c2.152 5 5.105 9.376 8.854  13.125 3.751 3.75 8.126 6.703 13.125 8.855a38.98 38.98 0 005.906 1.999c.66.166 1.124.758 1.124 1.438 0 .68-.464 1.273-1.125 1.439a38.902 38.902 0 00-5.905 1.999c-5 2.152-9.375 5.105-13.125 8.854-3.749 3.751-6.702 8.126-8.854 13.125a38.973 38.973 0 00-2 5.906 1.485 1.485 0 01-1.438 1.124c-.68 0-1.272-.464-1.438-1.125a38.913 38.913 0 00-2-5.905c-2.151-5-5.103-9.375-8.854-13.125-3.75-3.749-8.125-6.702-13.125-8.854a38.973 38.973 0 00-5.905-2A1.485 1.485 0 010 32.447c0-.68.465-1.272 1.125-1.438a38.903 38.903 0 005.905-2c5-2.151 9.376-5.104 13.125-8.854 3.75-3.749 6.703-8.125 8.855-13.125a38.972 38.972 0 001.999-5.905A1.485 1.485 0 0132.447 0z" fill="#000"/><path d="M32.447 0c.68 0 1.273.465 1.439 1.125a38.904 38.904 0 001.999 5.905c2.152 5 5.105 9.376 8.854 13.125 3.751 3.75 8.126 6.703 13.125 8.855a38.98 38.98 0 005.906 1.999c.66.166 1.124.758 1.124 1.438 0 .68-.464 1.273-1.125 1.439a38.902 38.902 0 00-5.905 1.999c-5 2.152-9.375 5.105-13.125 8.854-3.749 3.751-6.702 8.126-8.854 13.125a38.973 38.973 0 00-2 5.906 1.485 1.485 0 01-1.438 1.124c-.68 0-1.272-.464-1.438-1.125a38.913 38.913 0 00-2-5.905c-2.151-5-5.103-9.375-8.854-13.125-3.75-3.749-8.125-6.702-13.125-8.854a38.973 38.973 0 00-5.905-2A1.485 1.485 0 010 32.447c0-.68.465-1.272 1.125-1.438a38.903 38.903 0 005.905-2c5-2.151 9.376-5.104 13.125-8.854 3.75-3.749 6.703-8.125 8.855-13.125a38.972 38.972 0 001.999-5.905A1.485 1.485 0 0132.447 0z" fill="url(#prefix__paint0_linear_2001_67)"/></mask><g mask="url(#maskme)"><g filter="url(#prefix__filter0_f_2001_67)"><path d="M-5.859 50.734c7.498 2.663 16.116-2.33 19.249-11.152 3.133-8.821-.406-18.131-7.904-20.794-7.498-2.663-16.116 2.33-19.25 11.151-3.132 8.822.407 18.132 7.905 20.795z" fill="#FFE432"/></g><g filter="url(#prefix__filter1_f_2001_67)"><path d="M27.433 21.649c10.3 0 18.651-8.535 18.651-19.062 0-10.528-8.35-19.062-18.651-19.062S8.78-7.94 8.78 2.587c0 10.527 8.35 19.062 18.652 19.062z" fill="#FC413D"/></g><g filter="url(#prefix__filter2_f_2001_67)"><path d="M20.184 82.608c10.753-.525 18.918-12.244 18.237-26.174-.68-13.93-9.95-24.797-20.703-24.271C6.965 32.689-1.2 44.407-.519 58.337c.681 13.93 9.95 24.797 20.703 24.271z" fill="#00B95C"/></g><g filter="url(#prefix__filter3_f_2001_67)"><path d="M20.184 82.608c10.753-.525 18.918-12.244 18.237-26.174-.68-13.93-9.95-24.797-20.703-24.271C6.965 32.689-1.2 44.407-.519 58.337c.681 13.93 9.95 24.797 20.703 24.271z" fill="#00B95C"/></g><g filter="url(#prefix__filter4_f_2001_67)"><path d="M30.954 74.181c9.014-5.485 11.427-17.976 5.389-27.9-6.038-9.925-18.241-13.524-27.256-8.04-9.015 5.486-11.428 17.977-5.39 27.902 6.04 9.924 18.242 13.523 27.257 8.038z" fill="#00B95C"/></g><g filter="url(#prefix__filter5_f_2001_67)"><path d="M67.391 42.993c10.132 0 18.346-7.91 18.346-17.666 0-9.757-8.214-17.667-18.346-17.667s-18.346 7.91-18.346 17.667c0 9.757 8.214 17.666 18.346 17.666z" fill="#3186FF"/></g><g filter="url(#prefix__filter6_f_2001_67)"><path d="M-13.065 40.944c9.33 7.094 22.959 4.869 30.442-4.972 7.483-9.84 5.987-23.569-3.343-30.663C4.704-1.786-8.924.439-16.408 10.28c-7.483 9.84-5.986 23.57 3.343 30.664z" fill="#FBBC04"/></g><g filter="url(#prefix__filter7_f_2001_67)"><path d="M34.74 51.43c11.135 7.656 25.896 5.524 32.968-4.764 7.073-10.287 3.779-24.832-7.357-32.488C49.215 6.52 34.455 8.654 27.382 18.94c-7.072 10.288-3.779 24.833 7.357 32.49z" fill="#3186FF"/></g><g filter="url(#prefix__filter8_f_2001_67)"><path d="M54.984-2.336c2.833 3.852-.808 11.34-8.131 16.727-7.324 5.387-15.557 6.631-18.39 2.78-2.833-3.853.807-11.342 8.13-16.728 7.324-5.387 15.558-6.631 18.39-2.78z" fill="#749BFF"/></g><g filter="url(#prefix__filter9_f_2001_67)"><path d="M31.727 16.104C43.053 5.598 46.94-8.626 40.41-15.666c-6.53-7.04-21.006-4.232-32.332 6.274s-15.214 24.73-8.683 31.77c6.53 7.04 21.006 4.232 32.332-6.274z" fill="#FC413D"/></g><g filter="url(#prefix__filter10_f_2001_67)"><path d="M8.51 53.838c6.732 4.818 14.46 5.55 17.262 1.636 2.802-3.915-.384-10.994-7.116-15.812-6.731-4.818-14.46-5.55-17.261-1.636-2.802 3.915.383 10.994 7.115 15.812z" fill="#FFEE48"/></g></g><defs><filter id="prefix__filter0_f_2001_67" x="-19.824" y="13.152" width="39.274" height="43.217" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="2.46" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter1_f_2001_67" x="-15.001" y="-40.257" width="84.868" height="85.688" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="11.891" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter2_f_2001_67" x="-20.776" y="11.927" width="79.454" height="90.916" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter3_f_2001_67" x="-20.776" y="11.927" width="79.454" height="90.916" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter4_f_2001_67" x="-19.845" y="15.459" width="79.731" height="81.505" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter5_f_2001_67" x="29.832" y="-11.552" width="75.117" height="73.758" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="9.606" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter6_f_2001_67" x="-38.583" y="-16.253" width="78.135" height="78.758" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="8.706" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter7_f_2001_67" x="8.107" y="-5.966" width="78.877" height="77.539" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="7.775" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter8_f_2001_67" x="13.587" y="-18.488" width="56.272" height="51.81" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="6.957" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter9_f_2001_67" x="-15.526" y="-31.297" width="70.856" height="69.306" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="5.876" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter10_f_2001_67" x="-14.168" y="20.964" width="55.501" height="51.571" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="7.273" result="effect1_foregroundBlur_2001_67"/></filter><linearGradient id="prefix__paint0_linear_2001_67" x1="18.447" y1="43.42" x2="52.153" y2="15.004" gradientUnits="userSpaceOnUse"><stop stop-color="#4893FC"/><stop offset=".27" stop-color="#4893FC"/><stop offset=".777" stop-color="#969DFF"/><stop offset="1" stop-color="#BD99FE"/></linearGradient></defs></svg>`,
                        label: "Gemini",
                        status: this.project.platforms.includes("gemini") ? "included" : "upgrade"
                    },
                    perplexity: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.64"><path fill="#1F1F1F" d="M115.613 0h280.774C459.974 0 512 52.025 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.613C52.026 509.64 0 457.614 0 394.027V115.612C0 52.025 52.026 0 115.613 0z"/><path fill="#fff" fill-rule="nonzero" d="M348.851 128.063l-68.946 58.302h68.946v-58.302zm-83.908 48.709l100.931-85.349v94.942h32.244v143.421h-38.731v90.004l-94.442-86.662v83.946h-17.023v-83.906l-96.596 86.246v-89.628h-37.445V186.365h38.732V90.768l95.309 84.958v-83.16h17.023l-.002 84.206zm-29.209 26.616c-34.955.02-69.893 0-104.83 0v109.375h20.415v-27.121l84.415-82.254zm41.445 0l82.208 82.324v27.051h21.708V203.388c-34.617 0-69.274.02-103.916 0zm-42.874-17.023l-64.669-57.646v57.646h64.669zm13.617 124.076v-95.2l-79.573 77.516v88.731l79.573-71.047zm17.252-95.022v94.863l77.19 70.83c0-29.485-.012-58.943-.012-88.425l-77.178-77.268z"/></svg>`,
                        label: "Perplexity",
                        status: this.project.platforms.includes("perplexity") ? "included" : "upgrade"
                    },
                    grok: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" ><path d="M18.542 30.532l15.956-11.776c.783-.576 1.902-.354 2.274.545 1.962 4.728 1.084 10.411-2.819 14.315-3.903 3.901-9.333 4.756-14.299 2.808l-5.423 2.511c7.778 5.315 17.224 4 23.125-1.903 4.682-4.679 6.131-11.058 4.775-16.812l.011.011c-1.966-8.452.482-11.829 5.501-18.735C47.759 1.332 47.88 1.166 48 1l-6.602 6.599V7.577l-22.86 22.958M15.248 33.392c-5.582-5.329-4.619-13.579.142-18.339 3.521-3.522 9.294-4.958 14.331-2.847l5.412-2.497c-.974-.704-2.224-1.46-3.659-1.994-6.478-2.666-14.238-1.34-19.505 3.922C6.904 16.701 5.31 24.488 8.045 31.133c2.044 4.965-1.307 8.48-4.682 12.023C2.164 44.411.967 45.67 0 47l15.241-13.608"/></svg>`,
                        label: "Grok",
                        status: this.project.platforms.includes("grok") ? "included" : "upgrade"
                    },
                    microsoft_copilot: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48"><path d="M34.142 7.325A4.63 4.63 0 0029.7 4H28.35a4.63 4.63 0 00-4.554 3.794L21.48 20.407l.575-1.965a4.63 4.63 0 014.444-3.33h7.853l3.294 1.282 3.175-1.283h-.926a4.63 4.63 0 01-4.443-3.325l-1.31-4.461z" fill="url(#prefix__paint0_radial_56201_15503)"/><path d="M14.33 40.656A4.63 4.63 0 0018.779 44h2.87a4.63 4.63 0 004.629-4.51l.312-12.163-.654 2.233a4.63 4.63 0 01-4.443 3.329h-7.919l-2.823-1.532-3.057 1.532h.912a4.63 4.63 0 014.447 3.344l1.279 4.423z" fill="url(#prefix__paint1_radial_56201_15503)"/><path d="M29.5 4H13.46c-4.583 0-7.332 6.057-9.165 12.113C2.123 23.29-.72 32.885 7.503 32.885h6.925a4.63 4.63 0 004.456-3.358 2078.617 2078.617 0 014.971-17.156c.843-2.843 1.544-5.284 2.621-6.805C27.08 4.714 28.086 4 29.5 4z" fill="url(#prefix__paint2_linear_56201_15503)"/><path d="M29.5 4H13.46c-4.583 0-7.332 6.057-9.165 12.113C2.123 23.29-.72 32.885 7.503 32.885h6.925a4.63 4.63 0 004.456-3.358 2078.617 2078.617 0 014.971-17.156c.843-2.843 1.544-5.284 2.621-6.805C27.08 4.714 28.086 4 29.5 4z" fill="url(#prefix__paint3_linear_56201_15503)"/><path d="M18.498 44h16.04c4.582 0 7.332-6.058 9.165-12.115 2.171-7.177 5.013-16.775-3.208-16.775h-6.926a4.63 4.63 0 00-4.455 3.358 2084.036 2084.036 0 01-4.972 17.16c-.842 2.843-1.544 5.285-2.62 6.806-.604.852-1.61 1.566-3.024 1.566z" fill="url(#prefix__paint4_radial_56201_15503)"/><path d="M18.498 44h16.04c4.582 0 7.332-6.058 9.165-12.115 2.171-7.177 5.013-16.775-3.208-16.775h-6.926a4.63 4.63 0 00-4.455 3.358 2084.036 2084.036 0 01-4.972 17.16c-.842 2.843-1.544 5.285-2.62 6.806-.604.852-1.61 1.566-3.024 1.566z" fill="url(#prefix__paint5_linear_56201_15503)"/><defs><radialGradient id="prefix__paint0_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(-10.96051 -13.38922 12.59013 -10.30637 38.005 20.514)"><stop offset=".096" stop-color="#00AEFF"/><stop offset=".773" stop-color="#2253CE"/><stop offset="1" stop-color="#0736C4"/></radialGradient><radialGradient id="prefix__paint1_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="rotate(51.84 -28.201 27.85) scale(15.9912 15.5119)"><stop stop-color="#FFB657"/><stop offset=".634" stop-color="#FF5F3D"/><stop offset=".923" stop-color="#C02B3C"/></radialGradient><radialGradient id="prefix__paint4_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="rotate(109.274 16.301 20.802) scale(38.3873 45.9867)"><stop offset=".066" stop-color="#8C48FF"/><stop offset=".5" stop-color="#F2598A"/><stop offset=".896" stop-color="#FFB152"/></radialGradient><linearGradient id="prefix__paint2_linear_56201_15503" x1="12.5" y1="7.5" x2="14.788" y2="33.975" gradientUnits="userSpaceOnUse"><stop offset=".156" stop-color="#0D91E1"/><stop offset=".487" stop-color="#52B471"/><stop offset=".652" stop-color="#98BD42"/><stop offset=".937" stop-color="#FFC800"/></linearGradient><linearGradient id="prefix__paint3_linear_56201_15503" x1="14.5" y1="4" x2="15.75" y2="32.885" gradientUnits="userSpaceOnUse"><stop stop-color="#3DCBFF"/><stop offset=".247" stop-color="#0588F7" stop-opacity="0"/></linearGradient><linearGradient id="prefix__paint5_linear_56201_15503" x1="42.586" y1="13.346" x2="42.569" y2="21.215" gradientUnits="userSpaceOnUse"><stop offset=".058" stop-color="#F8ADFA"/><stop offset=".708" stop-color="#A86EDD" stop-opacity="0"/></linearGradient></defs></svg>`,
                        label: "Microsoft Copilot",
                        status: this.project.platforms.includes("microsoft_copilot") ? "included" : "upgrade"
                    },
                    claude: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.64"><path fill="#D77655" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.612-115.613 115.612H115.612C52.026 509.639 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill="#FCF2EE" fill-rule="nonzero" d="M142.27 316.619l73.655-41.326 1.238-3.589-1.238-1.996-3.589-.001-12.31-.759-42.084-1.138-36.498-1.516-35.361-1.896-8.897-1.895-8.34-10.995.859-5.484 7.482-5.03 10.717.935 23.683 1.617 35.537 2.452 25.782 1.517 38.193 3.968h6.064l.86-2.451-2.073-1.517-1.618-1.517-36.776-24.922-39.81-26.338-20.852-15.166-11.273-7.683-5.687-7.204-2.451-15.721 10.237-11.273 13.75.935 3.513.936 13.928 10.716 29.749 23.027 38.848 28.612 5.687 4.727 2.275-1.617.278-1.138-2.553-4.271-21.13-38.193-22.546-38.848-10.035-16.101-2.654-9.655-1.617-7.304-1.617-11.374l11.652-15.823 6.445-2.073 15.545 2.073 6.547 5.687 9.655 22.092 15.646 34.78 24.265 47.291 7.103 14.028 3.791 12.992 1.416 3.968 2.449-.001v-2.275l1.997-26.641 3.69-32.707 3.589-42.084 1.239-11.854 5.863-14.206 11.652-7.683 9.099 4.348 7.482 10.716-1.036 6.926-4.449 28.915-8.72 45.294-5.687 30.331h3.313l3.792-3.791 15.342-20.372 25.782-32.227 11.374-12.789 13.27-14.129 8.517-6.724 16.1-.001 11.854 17.617-5.307 18.199-16.581 21.029-13.75 17.819-19.716 26.54-12.309 21.231 1.138 1.694 2.932-.278 44.536-9.479 24.062-4.347 28.714-4.928 12.992 6.066 1.416 6.167-5.106 12.613-30.71 7.583-36.018 7.204-53.636 12.689-.657.48.758.935 24.164 2.275 10.337.556h25.301l47.114 3.514 12.309 8.139 7.381 9.959-1.238 7.583-18.957 9.655-25.579-6.066-59.702-14.205-20.474-5.106-2.83-.001v1.694l17.061 16.682 31.266 28.233 39.152 36.397 1.997 8.999-5.03 7.102-5.307-.758-34.401-25.883-13.27-11.651-30.053-25.302-1.996-.001v2.654l6.926 10.136 36.574 54.975 1.895 16.859-2.653 5.485-9.479 3.311-10.414-1.895-21.408-30.054-22.092-33.844-17.819-30.331-2.173 1.238-10.515 113.261-4.929 5.788-11.374 4.348-9.478-7.204-5.03-11.652 5.03-23.027 6.066-30.052 4.928-23.886 4.449-29.674 2.654-9.858-.177-.657-2.173.278-22.37 30.71-34.021 45.977-26.919 28.815-6.445 2.553-11.173-5.789 1.037-10.337 6.243-9.2 37.257-47.392 22.47-29.371 14.508-16.961-.101-2.451h-.859l-98.954 64.251-17.618 2.275-7.583-7.103.936-11.652 3.589-3.791 29.749-20.474-.101.102.024.101z"/></svg>`,
                        label: "Claude",
                        status: "coming soon"
                    }
                }
                return platforms[key] || { logo: null, label: name, status: null }
            }
        },
        mounted() {
            this.$router.onReady(() => {
                this.fetchData(this.$route.params.id)
            })
        },
        activated() {
            const projectId = this.project?._id || this.$route?.params?.id
            const hasQuery = this.$route?.query && Object.keys(this.$route.query).length > 0
            const returning = this.isReturningFromPromptDetail(projectId)
            if (this.isForceReloadNeeded(projectId) && !returning) {
                this.clearForceReloadFlag(projectId)
                this.startFreshProjectLoad(projectId)
                return
            }
            if (!hasQuery && !returning) {
                this.resetAiSuiteViewState()
                this.syncQueryFromState()
            }
            this.restoreProjectScrollPosition(projectId)
        },
        deactivated() {
            this.saveProjectScrollPosition(this.project?._id || this.$route?.params?.id)
        },
        beforeDestroy() {
            // Falls die View mit offenem Overlay zerstört wird: Scroll-Lock nicht hängen lassen
            this.setPageScrollLock(false)
        },
        beforeRouteLeave(to, from, next) {
            const toPath = (to && typeof to.path === "string" ? to.path : "") || ""
            if (toPath.startsWith("/ai-suite/prompt-detail/")) {
                this.setReturnFromPromptDetail(this.project?._id || this.$route?.params?.id)
                this.saveProjectScrollPosition(this.project?._id || this.$route?.params?.id)
            } else {
                this.clearReturnFromPromptDetail(this.project?._id || this.$route?.params?.id)
                if (toPath === "/ai-suite") {
                    try {
                        sessionStorage.setItem("ai_suite_project_force_reload_all", "1")
                    } catch (e) {
                        // ignore storage errors
                    }
                }
            }
            if (this.shouldClearQueryOnLeave(to)) {
                try {
                    window.history.replaceState({}, "", this.$route.path)
                } catch (e) {
                    // ignore history errors
                }
            }
            next()
        }
    }
</script>
<style scoped>
    .v-text-field--outlined >>> fieldset {
        border-color: rgba(87, 87, 87, 0.986) !important;
    }
    .import-tags-card {
        border-radius: 14px !important;
        overflow: hidden;
    }
    .import-tags-header {
        border-bottom: 1px solid rgba(255, 255, 255, 0.18);
    }
    .import-tags-icon {
        background: rgba(255, 255, 255, 0.18);
    }
    .import-tags-hint {
        border-radius: 10px !important;
    }
    .import-tags-file-input >>> .v-input__slot {
        border-radius: 10px !important;
        transition: box-shadow 0.2s ease;
    }
    .import-tags-file-input:hover >>> .v-input__slot {
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
    }
</style>
<style>
    /* Prompt-Detail-Overlay: v-dialog als rechtes Panel über volle Höhe.
       Global (nicht scoped), weil Vuetify den Dialog an die App-Root teleportiert. */
    .v-dialog.prompt-overlay-panel {
        position: fixed;
        top: 0;
        right: 0;
        margin: 0;
        width: min(1500px, 94vw);
        max-width: none;
        height: 100vh;
        max-height: 100vh;
        border-radius: 0;
        /* Das Panel selbst ist ein fester Rahmen — gescrollt wird nur der Inhalt (__body) */
        overflow: hidden;
        display: flex;
        flex-direction: column;
        background: #f8f9fb;
        /* Eigene CSS-Animation statt Vue-Transition: die bleibt beim Laden nicht bei opacity 0 stehen */
        animation: prompt-overlay-slide-in 0.22s ease-out;
    }
    @keyframes prompt-overlay-slide-in {
        from {
            transform: translateX(28px);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }
    .prompt-overlay-panel__body {
        flex: 1 1 auto;
        min-height: 0;
        /* Fester Rahmen: hier wird nicht gescrollt — Kopfzeile, KPI-Karten und das
           Card-Chrome bleiben immer sichtbar. Gescrollt wird nur in der Response-Card. */
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }
    /* Flex-Kette bis zur Response-Card durchreichen, damit diese den Restplatz bekommt */
    .prompt-overlay-panel__body > #prompt-detail,
    .prompt-overlay-panel__body .prompt-detail-container {
        flex: 1 1 auto;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
    /* .standard-mode (App.vue) setzt margin: auto. Als Flex-Item schrumpft #prompt-detail
       damit auf Inhaltsbreite — bei kurzer Response wurde das ganze Overlay schmal. */
    .prompt-overlay-panel__body > #prompt-detail {
        margin: 0;
    }
    /* Kopfzeile, Fehlermeldung und KPI-Karten behalten ihre natürliche Höhe.
       Wichtig: Vuetifys .row bringt selbst flex: 1 1 auto mit — ohne das explizite 0 0 auto
       teilen sich KPI-Zeile und Response-Zeile den Platz nach Inhalt und springen beim Laden. */
    .prompt-overlay-panel__body .prompt-detail-header,
    .prompt-overlay-panel__body .prompt-detail-container > .v-alert,
    .prompt-overlay-panel__body .prompt-detail-container > .row:not(:last-of-type) {
        flex: 0 0 auto;
    }
    /* Die Zeile mit der Response-Card füllt den Rest */
    .prompt-overlay-panel__body .prompt-detail-container > .row:last-of-type {
        flex: 1 1 auto;
        min-height: 0;
    }
    /* Platz für die Prompt-Tags reservieren — die rendern erst, wenn die Daten da sind,
       und würden die Kachel-Zeile sonst nachträglich wachsen lassen. */
    .prompt-overlay-panel__body .brand-metrics-cards-wrapper .brand-metric-card {
        min-height: 205px;
    }
    .prompt-overlay-panel__body .prompt-response-card {
        width: 100%;
        height: 100%;
        max-height: none;
        /* Luft zwischen KPI-Kacheln und Hauptinhalt */
        margin-top: 20px !important;
        margin-bottom: 0 !important;
    }
    /* Sicherheitsnetz für sehr flache Fenster: dort darf das Panel doch scrollen,
       damit nichts unerreichbar abgeschnitten wird. */
    @media (max-height: 640px) {
        .prompt-overlay-panel__body {
            overflow-y: auto;
            overscroll-behavior: contain;
            display: block;
        }
        .prompt-overlay-panel__body .prompt-response-card {
            height: auto;
            max-height: 80vh;
        }
    }
    .theme--light.v-label {
        color: rgba(0, 0, 0, 0.8) !important;
    }
    .sr-only {
        position: absolute !important;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0 0 1px 1px);
        white-space: nowrap;
        border: 0;
    }
    .sr-only-focusable:focus {
        position: static !important;
        width: auto;
        height: auto;
        margin: 8px 0;
        padding: 6px 10px;
        background: #fff3cd;
        color: #000;
        border-radius: 6px;
        box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.35);
    }

    /* Optional: sichtbarer Fokusring für interaktive Elemente
    .v-btn:focus-visible,
    .v-chip:focus-visible,
    .v-text-field input:focus-visible,
    .v-select .v-input__slot:focus-within {
        outline: 3px solid #4c9ffe !important;
        outline-offset: 2px;
    } */
    #tagfilter .v-text-field .v-input__prepend-inne {
        margin-top: 6px !important;
    }
    .tag-settings-btn {
        color: var(--v-primary-base) !important;
        transition: transform 0.15s ease, opacity 0.15s ease;
    }
    .tag-settings-btn:hover {
        opacity: 1;
        transform: translateY(-1px);
    }

    /* Popover card */
    .tag-settings-popover {
        min-width: 300px;
        border-radius: 12px !important;
    }
    .tag-filter-menu-content {
        z-index: 2600 !important;
    }
    .tag-op-toggle .v-btn {
        border-radius: 10px !important;
    }
    /* Toggle emphasis */
    .tag-op-toggle .v-btn.is-active {
        background: color-mix(in srgb, var(--v-primary-base) 12%, transparent) !important;
        color: var(--v-primary-base) !important;
        border-radius: 10px !important;
    }
    .theme--dark .tag-op-toggle .v-btn.is-active {
        background: color-mix(in srgb, var(--v-primary-base) 28%, transparent) !important;
        color: #fff !important;
        border-radius: 10px !important;
    }
    .tag-dot {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 6px;
        vertical-align: -1px;
    }

    /* Chip bleibt weiß/outlined (bzw. dunkel im Dark Mode) */
    .tag-chip {
        background: #fff !important;
        border-color: rgba(0, 0, 0, 0.15) !important;
        color: inherit !important;
    }
    .theme--dark .tag-chip {
        background: #1f1f1f !important;
        border-color: rgba(255, 255, 255, 0.2) !important;
    }

    /* vorhandener Dot im Dropdown (größer, mit Ring) */
    .tag-color-dot {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-right: 8px;
        box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(0, 0, 0, 0.08);
        vertical-align: -1px;
    }
    .theme--dark .tag-color-dot {
        box-shadow: 0 0 0 2px #1f1f1f, 0 0 0 3px rgba(255, 255, 255, 0.08);
    }
    .mm-scroll {
        overflow-x: auto;
        padding: 4px 2px 2px;
    }
    .metrics-matrix-card .tp-control {
        margin-top: 6px;
        margin-bottom: 6px;
    }
    .metrics-matrix-card .tp-control .v-input__slot {
        min-height: 40px !important;
        border-radius: 10px !important;
        background: rgba(255, 255, 255, 0.88) !important;
        border-color: rgba(0, 0, 0, 0.14) !important;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        transition: border-color 0.18s ease, box-shadow 0.18s ease;
    }
    .metrics-matrix-card .tp-control .v-input__slot:hover {
        border-color: rgba(0, 0, 0, 0.25) !important;
    }
    .metrics-matrix-card .tp-control.v-input--is-focused .v-input__slot,
    .metrics-matrix-card .tp-control .v-input__slot:focus-within {
        border-color: var(--v-primary-base) !important;
        box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.14);
    }
    .metrics-matrix-card .tp-control .v-label {
        font-size: 12px;
        letter-spacing: 0.2px;
    }
    .metrics-matrix-card .tp-controls-row {
        padding: 8px 14px 2px;
    }
    .metrics-matrix-card .tp-brand-select {
        width: 100%;
    }
    .metrics-matrix-card .tp-platform-select {
        width: 100%;
    }
    .metrics-matrix-card .tp-combine-select {
        width: 100%;
    }
    .metrics-matrix-card .tp-tags-select {
        width: 100%;
    }
    .metrics-matrix-card .tp-tags-select .v-select__selections {
        max-height: 92px;
        overflow-y: auto;
        flex-wrap: wrap;
        scrollbar-width: thin;
        row-gap: 4px;
    }
    .metrics-matrix-card .tp-tags-select .v-chip {
        border-radius: 14px !important;
        max-width: 100%;
    }
    .metrics-matrix-card .tp-platform-select .v-select__selections {
        min-height: 22px;
        align-items: center;
    }
    @media (max-width: 1200px) {
        .metrics-matrix-card .tp-controls-row {
            padding: 8px 10px 2px;
        }
    }
    .theme--dark .metrics-matrix-card .tp-control .v-input__slot {
        background: rgba(31, 33, 37, 0.92) !important;
        border-color: rgba(255, 255, 255, 0.12) !important;
        box-shadow: none;
    }
    .theme--dark .metrics-matrix-card .tp-control .v-input__slot:hover {
        border-color: rgba(255, 255, 255, 0.22) !important;
    }
    .theme--dark .metrics-matrix-card .tp-control.v-input--is-focused .v-input__slot,
    .theme--dark .metrics-matrix-card .tp-control .v-input__slot:focus-within {
        border-color: color-mix(in srgb, var(--v-primary-base) 72%, white) !important;
        box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.22);
    }
    .mm-scroll.mm-scroll--tag {
        max-height: 640px;
        overflow-y: auto;
    }
    .tp-empty-state {
        width: 100%;
        border: 1px dashed rgba(25, 118, 210, 0.4);
        border-radius: 14px;
        background: linear-gradient(180deg, rgba(25, 118, 210, 0.06), rgba(25, 118, 210, 0.015));
        padding: 20px 18px 16px;
        text-align: center;
    }
    .tp-empty-state__icon-wrap {
        width: 48px;
        height: 48px;
        margin: 0 auto 8px;
        border-radius: 999px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(25, 118, 210, 0.14);
    }
    .tp-empty-state__icon {
        font-size: 22px !important;
    }
    .tp-empty-state__title {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        letter-spacing: 0.2px;
        color: #1f2a37;
    }
    .tp-empty-state__text {
        margin: 7px auto 0;
        max-width: 560px;
        font-size: 13px;
        line-height: 1.45;
        color: #556172;
    }
    .tp-empty-state__actions {
        margin-top: 12px;
    }
    .theme--dark .tp-empty-state {
        border-color: rgba(66, 165, 245, 0.45);
        background: linear-gradient(180deg, rgba(66, 165, 245, 0.18), rgba(66, 165, 245, 0.06));
    }
    .theme--dark .tp-empty-state__icon-wrap {
        background: rgba(66, 165, 245, 0.26);
    }
    .theme--dark .tp-empty-state__title {
        color: #f1f5f9;
    }
    .theme--dark .tp-empty-state__text {
        color: rgba(226, 232, 240, 0.9);
    }
    .mm-table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0 6px;
        min-width: 680px;
        font-size: 13px;
    }
    .mm-table thead th {
        text-align: left;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.6px;
        font-weight: 600;
        padding: 6px 10px;
        color: #4a5662;
    }
    .theme--dark .mm-table thead th {
        color: rgba(255, 255, 255, 0.7);
    }
    .mm-col-platform {
        min-width: 150px;
    }
    .mm-col-tag {
        min-width: 200px;
    }
    .mm-platform-cell {
        padding: 4px 10px;
        font-weight: 600;
        font-size: 13px;
        vertical-align: top;
    }
    .mm-tag-cell {
        padding: 4px 10px;
        font-weight: 600;
        font-size: 13px;
        vertical-align: top;
        min-width: 200px;
        white-space: nowrap;
    }
    .mm-platform-info {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .mm-platform-logo {
        width: 30px;
        height: 30px;
        border-radius: 10px;
        background: linear-gradient(135deg, #f0f4f8, #fff);
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid rgba(0, 0, 0, 0.06);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
        overflow: hidden;
    }
    .theme--dark .mm-platform-logo {
        background: linear-gradient(135deg, #2a2d31, #1d1f22);
        border: 1px solid rgba(255, 255, 255, 0.08);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.55), 0 0 0 5px rgba(0, 0, 0, 0.35);
    }
    .mm-platform-logo svg {
        width: 18px;
        height: 18px;
    }
    .mm-platform-logo--head {
        width: 22px;
        height: 22px;
        border-radius: 7px;
        box-shadow: none;
    }
    .mm-platform-logo--head svg {
        width: 14px;
        height: 14px;
    }
    .mm-metric-cell {
        padding: 4px 6px;
        vertical-align: top;
    }
    .mm-metric-box {
        background: #fcfcfc;
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 14px;
        padding: 12px 12px 5px;
        min-width: 170px;
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 0;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.45);
        transition: box-shadow 0.2s;
    }
    .mm-metric-box.mm-metric-box--with-rank-pill {
        padding-top: 35px;
    }
    .mm-rank-pill {
        position: absolute;
        top: 8px;
        left: 10px;
        width: calc(100% - 20px);
        display: inline-flex;
        align-items: center;
        gap: 4px;
        border-radius: 999px;
        padding: 3px 8px;
        font-size: 11px;
        font-weight: 700;
        line-height: 1;
        letter-spacing: 0.1px;
        color: #374151;
        background: rgba(107, 114, 128, 0.14);
        border: 1px solid rgba(107, 114, 128, 0.28);
    }
    .mm-rank-pill .v-icon {
        font-size: 12px !important;
    }
    .mm-rank-pill.mm-rank-pill--gold {
        color: #166534;
        background: rgba(34, 197, 94, 0.18);
        border-color: rgba(34, 197, 94, 0.4);
    }
    .mm-rank-pill.mm-rank-pill--silver {
        color: #854d0e;
        background: rgba(250, 204, 21, 0.2);
        border-color: rgba(250, 204, 21, 0.42);
    }
    .mm-rank-pill.mm-rank-pill--bronze {
        color: #9a3412;
        background: rgba(249, 115, 22, 0.18);
        border-color: rgba(249, 115, 22, 0.4);
    }
    .mm-rank-pill.mm-rank-pill--neutral {
        color: #4b5563;
        background: rgba(107, 114, 128, 0.14);
        border-color: rgba(107, 114, 128, 0.28);
    }
    .theme--dark .mm-metric-box {
        background: linear-gradient(180deg, #2a2f35, #252a30);
        border: 1px solid rgba(255, 255, 255, 0.08);
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
    }
    .theme--dark .mm-rank-pill {
        color: rgba(226, 232, 240, 0.95);
        background: rgba(148, 163, 184, 0.16);
        border-color: rgba(148, 163, 184, 0.3);
    }
    .theme--dark .mm-rank-pill.mm-rank-pill--gold {
        color: #86efac;
        background: rgba(34, 197, 94, 0.25);
        border-color: rgba(34, 197, 94, 0.42);
    }
    .theme--dark .mm-rank-pill.mm-rank-pill--silver {
        color: #fde047;
        background: rgba(250, 204, 21, 0.24);
        border-color: rgba(250, 204, 21, 0.42);
    }
    .theme--dark .mm-rank-pill.mm-rank-pill--bronze {
        color: #fdba74;
        background: rgba(249, 115, 22, 0.26);
        border-color: rgba(249, 115, 22, 0.44);
    }
    .mm-metric-box:hover {
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 2px 7px rgba(15, 23, 42, 0.08);
    }
    .theme--dark .mm-metric-box:hover {
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 4px 14px rgba(0, 0, 0, 0.36);
    }
    .mm-metric-box.mm-empty {
        justify-content: center;
        align-items: center;
        font-weight: 600;
        font-size: 18px;
        opacity: 0.35;
    }
    .mm-no-data-hint {
        min-height: 58px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-size: 11px;
        line-height: 1.25;
        font-weight: 600;
        color: rgba(0, 0, 0, 0.52);
        background: repeating-linear-gradient(135deg, rgba(0, 0, 0, 0.03), rgba(0, 0, 0, 0.03) 6px, transparent 6px, transparent 12px);
        border-radius: 8px;
        padding: 6px 8px;
    }
    .theme--dark .mm-no-data-hint {
        color: rgba(255, 255, 255, 0.7);
        background: repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.06) 6px, transparent 6px, transparent 12px);
    }
    .mm-brand-head {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        font-weight: 600;
    }
    .mm-fav {
        width: 16px;
        height: 16px;
        border-radius: 4px;
    }
    .mm-line {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-variant-numeric: tabular-nums;
        line-height: 1.2;
        min-height: 32px;
    }
    .mm-label {
        font-size: 14px;
        letter-spacing: 0.1px;
        opacity: 0.9;
        font-weight: 600;
        color: rgba(31, 41, 55, 0.86);
    }
    .mm-line:first-child .mm-label {
        font-size: 12px;
        letter-spacing: 0.8px;
        text-transform: uppercase;
        color: rgba(55, 65, 81, 0.92);
    }
    .mm-value {
        font-size: 16px;
        font-weight: 700;
        color: rgba(17, 24, 39, 0.9);
    }
    .mm-line:first-child .mm-value {
        font-size: 17px;
        letter-spacing: -0.2px;
    }
    .mm-value-wrap {
        display: inline-flex;
        align-items: center;
        justify-content: flex-end;
        gap: 4px;
        min-width: 126px;
    }
    .mm-change {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.08px;
        line-height: 1;
        font-variant-numeric: tabular-nums;
        color: rgba(75, 85, 99, 0.9);
        white-space: nowrap;
    }
    .mm-change .v-icon {
        font-size: 12px !important;
    }
    .mm-change.mm-change--compact {
        /* min-width: 46px; */
        justify-content: flex-end;
    }
    .mm-change.up {
        color: #1f9d45;
    }
    .mm-change.down {
        color: #d64545;
    }
    .mm-change.mm-change--na {
        opacity: 0.78;
    }
    .mm-divider {
        height: 1px;
        /* margin: 8px 0; */
        background: rgba(15, 23, 42, 0.08);
    }
    .theme--dark .mm-divider {
        background: rgba(255, 255, 255, 0.1);
    }
    .theme--dark .mm-label {
        color: rgba(226, 232, 240, 0.92);
    }
    .theme--dark .mm-line:first-child .mm-label {
        color: rgba(226, 232, 240, 0.86);
    }
    .theme--dark .mm-value {
        color: rgba(248, 250, 252, 0.96);
    }
    .theme--dark .mm-change {
        color: rgba(255, 255, 255, 0.45);
    }
    .theme--dark .mm-change.up {
        color: #4fd06e;
    }
    .theme--dark .mm-change.down {
        color: #ff7c7c;
    }
    .mm-value.mm-na {
        opacity: 0.35;
        font-weight: 500;
    }
    .mm-bar {
        position: relative;
        height: 7px;
        border-radius: 999px;
        background: linear-gradient(90deg, #e3e8ec, #dbe0e4);
        overflow: hidden;
        margin: 2px 0 6px;
    }
    .theme--dark .mm-bar {
        background: linear-gradient(90deg, #30363a, #272c30);
    }
    .mm-bar-fill {
        position: absolute;
        inset: 0;
        width: 0;
        background: linear-gradient(90deg, var(--v-primary-base) 0%, color-mix(in srgb, var(--v-primary-base) 70%, #ffffff) 60%, color-mix(in srgb, var(--v-primary-base) 40%, #ffffff) 100%);
        animation: mmGrow 0.8s cubic-bezier(0.55, 0.12, 0.35, 1) forwards;
    }
    @keyframes mmGrow {
        from {
            width: 0;
        }
        to {
            width: var(--target-width);
        }
    }
    .mm-secondary .mm-label {
        opacity: 0.8;
    }
    .mm-tertiary .mm-label {
        opacity: 0.8;
    }

    /* === Brand Metric Cards (benötigt) === */
    .brand-metrics-cards-wrapper {
        margin-top: 12px;
    }
    .data-basis-pill {
        display: inline-flex;
        align-items: center;
        align-self: center;
        flex-shrink: 0;
        gap: 5px;
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;
        line-height: 1;
        color: var(--v-primary-base, #1976d2);
        background: rgba(25, 118, 210, 0.08);
        border: 1px solid rgba(25, 118, 210, 0.18);
        white-space: nowrap;
    }
    .data-basis-pill__icon {
        color: var(--v-primary-base, #1976d2) !important;
    }
    .data-basis-pill__text {
        letter-spacing: 0.2px;
    }
    .theme--dark .data-basis-pill {
        color: #90caf9;
        background: rgba(144, 202, 249, 0.12);
        border-color: rgba(144, 202, 249, 0.25);
    }
    .theme--dark .data-basis-pill__icon {
        color: #90caf9 !important;
    }
    .brand-metrics-cards-wrapper.is-mode-loading {
        position: relative;
    }
    .brand-metrics-cards-wrapper.is-mode-loading > .row {
        opacity: 0.45;
        filter: blur(0.5px);
        transition: opacity 0.15s ease;
        pointer-events: none;
    }
    .dashboard-mode-loading-overlay {
        position: absolute;
        inset: 0;
        z-index: 5;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
    }
    .dashboard-mode-loading-overlay__text {
        font-size: 13px;
        font-weight: 600;
        color: var(--v-primary-base, #1976d2);
        text-transform: capitalize;
    }
    .tag-empty-state-wrap {
        display: flex;
        justify-content: center;
    }
    .tag-empty-state {
        width: 100%;
        max-width: 760px;
        border-radius: 16px !important;
        padding: 22px 22px 18px;
        text-align: center;
        border-color: rgba(0, 0, 0, 0.08) !important;
        background: linear-gradient(180deg, rgba(25, 118, 210, 0.04), rgba(25, 118, 210, 0.01));
    }
    .theme--dark .tag-empty-state {
        border-color: rgba(255, 255, 255, 0.14) !important;
        background: linear-gradient(180deg, rgba(66, 165, 245, 0.12), rgba(66, 165, 245, 0.04));
    }
    .tag-empty-state__icon-wrap {
        width: 52px;
        height: 52px;
        margin: 0 auto 10px;
        border-radius: 999px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(25, 118, 210, 0.12);
    }
    .theme--dark .tag-empty-state__icon-wrap {
        background: rgba(66, 165, 245, 0.2);
    }
    .tag-empty-state__icon {
        font-size: 24px !important;
    }
    .tag-empty-state__title {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        letter-spacing: 0.2px;
    }
    .tag-empty-state__text {
        margin: 8px auto 0;
        max-width: 620px;
        font-size: 13px;
        opacity: 0.86;
    }
    .tag-empty-state__chips {
        margin-top: 12px;
    }
    .tag-empty-state__actions {
        margin-top: 14px;
    }
    .brand-metrics-cards-wrapper .brand-metric-card {
        position: relative;
        padding: 12px 14px 10px;
        min-height: 124px;
        border-radius: 14px !important;
        border: 1px solid rgba(15, 23, 42, 0.1) !important;
        background: #ffffff;
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 8px 24px -18px rgba(15, 23, 42, 0.18);
        overflow: hidden;
        transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
    }
    .brand-metrics-cards-wrapper .brand-metric-card:hover {
        transform: translateY(-1px);
        border-color: rgba(15, 23, 42, 0.16) !important;
        box-shadow: 0 3px 8px rgba(15, 23, 42, 0.08), 0 14px 26px -20px rgba(15, 23, 42, 0.22);
    }
    .theme--dark .brand-metrics-cards-wrapper .brand-metric-card {
        background: #1f2328;
        border-color: rgba(255, 255, 255, 0.12) !important;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
    }

    .pc-top-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 8px;
    }
    .pc-head-left {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
    }
    .pc-favicon {
        width: 24px;
        height: 24px;
        min-width: 18px;
        border-radius: 999px;
        overflow: hidden;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 9px;
        font-weight: 700;
        color: rgba(15, 23, 42, 0.65);
        background: rgba(15, 23, 42, 0.06);
        border: 1px solid rgba(15, 23, 42, 0.12);
    }
    .pc-favicon img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
    .theme--dark .pc-favicon {
        color: rgba(255, 255, 255, 0.75);
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.14);
    }
    .brand-metrics-cards-wrapper .brand-metric-card .pc-title {
        margin: 0;
        font-size: 0.75rem !important;
        font-weight: 600 !important;
        color: rgba(15, 23, 42, 0.72);
        line-height: 1.2;
        text-transform: uppercase;
    }
    .theme--dark .brand-metrics-cards-wrapper .brand-metric-card .pc-title {
        color: rgba(255, 255, 255, 0.78);
    }

    .pc-main-row {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
    }
    .pc-value-wrap {
        min-width: 0;
    }
    .pc-value {
        font-size: 31px;
        font-weight: 700;
        line-height: 1.05;
        letter-spacing: -0.6px;
        font-variant-numeric: tabular-nums;
        color: rgba(15, 23, 42, 0.96);
        display: flex;
        align-items: baseline;
        gap: 2px;
    }
    .theme--dark .pc-value {
        color: rgba(255, 255, 255, 0.96);
    }
    .pc-subtitle {
        margin-top: 4px;
        font-size: 12px;
        color: rgba(15, 23, 42, 0.56);
        line-height: 1.2;
    }
    .theme--dark .pc-subtitle {
        color: rgba(255, 255, 255, 0.7);
    }

    .pc-change {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        font-size: 14px;
        font-weight: 600;
        line-height: 1;
        font-variant-numeric: tabular-nums;
        color: rgba(15, 23, 42, 0.48);
        white-space: nowrap;
    }
    .pc-change.up {
        color: #1f9d45;
    }
    .pc-change.down {
        color: #d64545;
    }
    .theme--dark .pc-change {
        color: rgba(255, 255, 255, 0.45);
    }
    .theme--dark .pc-change.up {
        color: #4fd06e;
    }
    .theme--dark .pc-change.down {
        color: #ff7c7c;
    }
    .pc-change--na {
        opacity: 0.62;
    }

    .pc-trend {
        margin-top: 5px;
        height: 28px;
        /* border-top: 1px solid rgba(15, 23, 42, 0.08); */
        padding-top: 6px;
    }
    .theme--dark .pc-trend {
        border-top-color: rgba(255, 255, 255, 0.08);
    }
    .pc-trend svg {
        width: 100%;
        height: 100%;
        display: block;
    }
    .pc-trend-line {
        fill: none;
        stroke: #9ca3af;
        stroke-width: 3;
        stroke-linecap: round;
    }
    .pc-trend.up .pc-trend-line {
        stroke: rgba(31, 157, 69, 0.55);
    }
    .pc-trend.down .pc-trend-line {
        stroke: rgba(214, 69, 69, 0.55);
    }
    .theme--dark .pc-trend-line {
        stroke: rgba(229, 231, 235, 0.38);
    }
    .theme--dark .pc-trend.up .pc-trend-line {
        stroke: rgba(79, 208, 110, 0.68);
    }
    .theme--dark .pc-trend.down .pc-trend-line {
        stroke: rgba(255, 124, 124, 0.68);
    }

    /* Brand Auswahl */
    .brand-color-dot {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-right: 8px;
        box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(0, 0, 0, 0.08);
        position: relative;
        top: 1px;
    }
    .theme--dark .brand-color-dot {
        box-shadow: 0 0 0 2px #1f1f1f, 0 0 0 3px rgba(255, 255, 255, 0.08);
    }
    .brand-select-center {
        display: flex;
        justify-content: center;
        width: 100%;
    }
    .brand-multi.compact {
        width: auto !important;
        max-width: 850px;
        display: inline-flex;
    }

    /* Gradient Toolbar */
    .gradient-toolbar {
        --gt-border: rgba(0, 0, 0, 0.08);
        --gt-glow: rgba(255, 255, 255, 0.65);
        background: linear-gradient(155deg, #f5f7fa 0%, #f5f7fa 45%, #eef2f6 100%) !important;
        position: relative;
        padding: 0 14px;
        min-height: 54px;
        border-bottom: 1px solid var(--gt-border);
        backdrop-filter: saturate(140%) blur(4px);
        box-shadow: 0 1px 2px -1px rgba(0, 0, 0, 0.12), 0 4px 14px -6px rgba(0, 0, 0, 0.1) !important;
        border-top: 1px solid var(--gt-glow);
        border-radius: 14px 14px 0 0;
    }
    .theme--dark .gradient-toolbar {
        --gt-border: rgba(255, 255, 255, 0.1);
        --gt-glow: rgba(255, 255, 255, 0.08);
        background: linear-gradient(155deg, #262a2e 0%, #1f2225 55%, #1a1c1e 100%) !important;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.7), 0 6px 22px -10px rgba(0, 0, 0, 0.55) !important;
        backdrop-filter: saturate(150%) blur(6px);
    }
    .gradient-toolbar:before {
        content: "";
        position: absolute;
        inset: 0;
        pointer-events: none;
        background: radial-gradient(circle at 86% 24%, rgba(255, 255, 255, 0.35), transparent 60%), radial-gradient(circle at 8% 90%, rgba(255, 255, 255, 0.2), transparent 55%);
        opacity: 0.55;
        mix-blend-mode: soft-light;
    }
    .theme--dark .gradient-toolbar:before {
        background: radial-gradient(circle at 82% 20%, rgba(255, 255, 255, 0.14), transparent 60%), radial-gradient(circle at 10% 88%, rgba(255, 255, 255, 0.1), transparent 55%);
        opacity: 0.7;
    }
    .gradient-toolbar .v-btn {
        transition: background 0.25s, color 0.25s;
        border-radius: 10px !important;
        margin: 4px 2px;
    }
    .gradient-toolbar .v-btn.active-button2 {
        background: rgba(0, 0, 0, 0.06) !important;
    }
    .theme--dark .gradient-toolbar .v-btn.active-button2 {
        background: rgba(255, 255, 255, 0.1) !important;
    }
    .gradient-toolbar .v-btn:not(.active-button2):hover {
        background: rgba(0, 0, 0, 0.05) !important;
    }
    .theme--dark .gradient-toolbar .v-btn:not(.active-button2):hover {
        background: rgba(255, 255, 255, 0.08) !important;
    }
    /* Inaktive Toolbar-Buttons nutzen die Farbe "grey darken-4" (fast schwarz),
       die im Dark Mode auf dem dunklen Toolbar-Hintergrund unlesbar ist. */
    .theme--dark .gradient-toolbar .v-btn:not(.active-button2),
    .theme--dark .gradient-toolbar .v-btn:not(.active-button2) .v-btn__content {
        color: rgba(255, 255, 255, 0.82) !important;
    }
    .theme--dark .gradient-toolbar .v-btn.active-button2,
    .theme--dark .gradient-toolbar .v-btn.active-button2 .v-btn__content {
        color: #fff !important;
    }
    .gradient-toolbar .v-toolbar__content {
        padding: 0 !important;
    }
    .project-section-tabs {
        width: 100%;
    }
    .project-section-tabs .v-tab,
    .gradient-toolbar .v-tab {
        min-width: 96px;
        text-transform: none;
        letter-spacing: 0;
        font-weight: 600;
        opacity: 1;
    }
    .project-section-tabs .v-tab.active-button2 {
        background: rgba(0, 0, 0, 0.06) !important;
        border-radius: 10px;
    }
    .theme--dark .project-section-tabs .v-tab.active-button2 {
        background: rgba(255, 255, 255, 0.1) !important;
    }
    .project-section-tabs__label {
        font-size: 0.85rem;
    }
    .project-sections {
        padding-top: 2px;
        padding-bottom: 2px;
    }
    .project-data-heading__title {
        margin: 0;
        font-size: 0.85rem;
        font-weight: 600;
        letter-spacing: 0.2px;
    }
    .aiproject .v-toolbar__items > .v-btn {
        height: 80% !important;
    }
    .project-top-row {
        align-items: center;
        gap: 10px;
        padding: 8px 12px;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        background: #f9fafb;
        box-shadow: none;
    }
    .theme--dark .project-top-row {
        border-color: rgba(255, 255, 255, 0.14);
        background: #1f2328;
    }
    .project-header {
        min-height: 54px;
    }
    .project-header__left {
        display: flex;
        align-items: center;
        min-width: 0;
        flex: 1;
        padding-left: 6px;
    }
    .project-title-favicon {
        width: 32px;
        height: 32px;
        border-radius: 444px;
        margin-right: 8px;
        flex: 0 0 auto;
        background: #fff;
        border: 1px solid rgba(15, 23, 42, 0.1);
    }
    .theme--dark .project-title-favicon {
        border-color: rgba(255, 255, 255, 0.18);
        background: #111827;
    }
    .project-title {
        margin: 0;
        font-size: 1.08rem;
        font-weight: 600;
        letter-spacing: 0;
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: #1f2937;
    }
    .theme--dark .project-title {
        color: #e5e7eb;
    }
    .project-share-btn {
        margin-left: 2px;
        margin-right: 2px;
        border: 1px solid rgba(15, 23, 42, 0.15) !important;
        background: #ffffff !important;
        color: #0f766e !important;
    }
    .theme--dark .project-share-btn {
        border-color: rgba(255, 255, 255, 0.18) !important;
        background: #111827 !important;
        color: #5eead4 !important;
    }
    .project-header__actions {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
        justify-content: flex-end;
    }
    .header-action-btn {
        margin: 0 !important;
    }
    .project-filters {
        gap: 8px;
        min-height: 50px;
        background: #f9fafb;
    }
    .theme--dark .project-filters {
        background: #1a1f25;
    }
    .project-filters__label-wrap {
        display: flex;
        align-items: center;
        min-height: 40px;
        padding: 0 4px;
        margin-right: 2px;
    }
    .project-filters__label {
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: #6b7280;
    }
    .theme--dark .project-filters__label {
        color: #9ca3af;
    }
    .project-filter-control {
        margin: 0 !important;
    }
    .project-filters .project-filter-control .v-input__slot {
        min-height: 38px !important;
        border-radius: 12px !important;
        background: #ffffff !important;
        border-color: #d1d5db !important;
    }

    .theme--dark .project-filters .project-filter-control .v-input__slot {
        background: #111827 !important;
        border-color: #374151 !important;
    }
    .theme--dark .project-filters .project-filter-control .v-label {
        color: #9ca3af !important;
    }
    .date-range-picker-wrapper {
        display: flex;
        align-items: stretch;
        background: #ffffff;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 10px 30px -12px rgba(15, 23, 42, 0.18), 0 2px 6px -2px rgba(15, 23, 42, 0.08);
        border: 1px solid rgba(15, 23, 42, 0.06);
    }
    .date-range-picker-wrapper .v-picker {
        border-radius: 0 !important;
        box-shadow: none !important;
    }
    .date-range-presets {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 14px 10px;
        border-right: 1px solid #eef0f3;
        min-width: 168px;
        background: #fafbfc;
    }
    .date-range-presets__title {
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.8px;
        text-transform: uppercase;
        color: #94a3b8;
        padding: 4px 10px 8px;
    }
    .date-range-preset-btn {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 7px 10px;
        border-radius: 8px;
        border: 0;
        background: transparent;
        font-size: 13px;
        font-weight: 500;
        color: #334155;
        cursor: pointer;
        text-align: left;
        transition: background-color 0.15s ease, color 0.15s ease;
    }
    .date-range-preset-btn:hover,
    .date-range-preset-btn:focus-visible {
        background: rgba(15, 23, 42, 0.05);
        color: #0f172a;
        outline: none;
    }
    .date-range-preset-btn.is-active {
        background: var(--v-primary-base, #1976d2);
        color: #ffffff;
    }
    .date-range-preset-btn.is-active:hover,
    .date-range-preset-btn.is-active:focus-visible {
        background: var(--v-primary-darken1, #1565c0);
        color: #ffffff;
    }
    .date-range-preset-btn__icon {
        color: inherit !important;
        opacity: 0.9;
    }
    .theme--dark .date-range-picker-wrapper {
        background: #1e1e1e;
        border-color: rgba(255, 255, 255, 0.08);
        box-shadow: 0 10px 30px -12px rgba(0, 0, 0, 0.55), 0 2px 6px -2px rgba(0, 0, 0, 0.35);
    }
    .theme--dark .date-range-presets {
        background: #161b22;
        border-right-color: #2a313c;
    }
    .theme--dark .date-range-presets__title {
        color: #64748b;
    }
    .theme--dark .date-range-preset-btn {
        color: #cbd5e1;
    }
    .theme--dark .date-range-preset-btn:hover,
    .theme--dark .date-range-preset-btn:focus-visible {
        background: rgba(255, 255, 255, 0.06);
        color: #f1f5f9;
    }
    .dashboard-mode-toggle {
        display: inline-flex;
        align-items: center;
        margin-left: 4px;
    }
    .dashboard-mode-toggle__group.v-btn-toggle {
        border-radius: 999px !important;
        background: #ffffff !important;
        border: 1px solid #d1d5db !important;
        padding: 3px !important;
        box-shadow: none !important;
        min-height: 38px !important;
    }
    .dashboard-mode-toggle__group .v-btn.dashboard-mode-toggle__btn {
        border-radius: 999px !important;
        min-width: 0 !important;
        height: 30px !important;
        padding: 0 12px !important;
        margin: 0 2px !important;
        text-transform: none !important;
        font-size: 12px !important;
        font-weight: 600 !important;
        letter-spacing: 0 !important;
        color: #475569 !important;
        background: transparent !important;
        border: 0 !important;
        opacity: 1 !important;
        transition: background 120ms ease, color 120ms ease;
    }
    .dashboard-mode-toggle__group .v-btn.dashboard-mode-toggle__btn::before {
        background: transparent !important;
    }
    .dashboard-mode-toggle__group .v-btn.dashboard-mode-toggle__btn.is-active {
        background: var(--v-primary-base) !important;
        color: #ffffff !important;
    }
    .dashboard-mode-toggle__group .v-btn.dashboard-mode-toggle__btn .v-icon {
        margin-right: 4px;
    }
    .theme--dark .dashboard-mode-toggle__group.v-btn-toggle {
        background: #111827 !important;
        border-color: #374151 !important;
    }
    .theme--dark .dashboard-mode-toggle__group .v-btn.dashboard-mode-toggle__btn {
        color: #cbd5e1 !important;
    }
    .theme--dark .dashboard-mode-toggle__group .v-btn.dashboard-mode-toggle__btn.is-active {
        color: #ffffff !important;
    }
    @media (max-width: 600px) {
        .date-range-picker-wrapper {
            flex-direction: column;
        }
        .date-range-presets {
            flex-direction: row;
            flex-wrap: wrap;
            min-width: 0;
            border-right: 0;
            border-bottom: 1px solid #eef0f3;
            gap: 6px;
            padding: 10px;
        }
        .date-range-presets__title {
            width: 100%;
            padding: 0 4px;
        }
        .date-range-preset-btn {
            flex: 1 1 auto;
            justify-content: center;
        }
        .theme--dark .date-range-presets {
            border-bottom-color: #2a313c;
        }
    }
    @media (max-width: 1300px) {
        .project-header {
            padding-bottom: 10px;
        }
        .project-header__actions {
            width: 100%;
            justify-content: flex-start;
            padding-left: 6px;
        }
        .project-filters {
            row-gap: 10px;
        }
        .project-filters__label-wrap {
            width: 100%;
            min-height: auto;
            padding: 0 0 2px;
        }
    }

    /* Misc */
    .v-text-field--outlined > .v-input__control > .v-input__slot {
        background: transparent !important;
    }
    .add-prompts-btn {
        text-transform: none;
        font-weight: 600;
        letter-spacing: 0.2px;
        border-radius: 10px !important;
        padding: 0 12px !important;
        background: #ffffff;
        border-color: #0f766e !important;
        color: #0f766e !important;
        height: 34px !important;
        font-size: 12px !important;
    }
    .theme--dark .add-prompts-btn {
        background: #111827;
        border-color: #115e59 !important;
        color: #5eead4 !important;
    }
    .add-prompts-btn:hover {
        background: #f0fdfa !important;
    }
    .theme--dark .add-prompts-btn:hover {
        background: #0f172a !important;
    }

    /* ===== AI Project Hero Header ===== */
    .aiproject-hero {
        border-radius: 18px;
        overflow: hidden;
        border: 1px solid #e5e7eb;
        background: #ffffff;
        margin-top: 8px;
        margin-bottom: 18px;
    }
    .aiproject-hero__top {
        position: relative;
        background: linear-gradient(135deg, var(--v-hero_gradient1-base) 0%, var(--v-hero_gradient2-base) 100%);
        padding: 22px 24px;
        display: flex;
        align-items: center;
        gap: 16px;
        overflow: hidden;
        flex-wrap: wrap;
        row-gap: 14px;
    }
    .aiproject-hero__top::before {
        content: "";
        position: absolute;
        width: 360px;
        height: 360px;
        right: -120px;
        top: -180px;
        border-radius: 50%;
        background: radial-gradient(circle, color-mix(in srgb, var(--v-hero_glow-base) 28%, transparent), transparent 62%);
        pointer-events: none;
    }
    .aiproject-hero__brandav {
        position: relative;
        z-index: 1;
        flex: 0 0 auto;
        width: 46px;
        height: 46px;
        border-radius: 13px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.16);
        overflow: hidden;
    }
    .aiproject-hero__brandav img {
        width: 26px;
        height: 26px;
        object-fit: contain;
    }
    .aiproject-hero__title-wrap {
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        gap: 7px;
        min-width: 200px;
        flex: 1 1 auto;
    }
    .aiproject-hero__row1 {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
    }
    .aiproject-hero__title {
        font-size: 22px;
        font-weight: 700;
        letter-spacing: -0.02em;
        margin: 0;
        color: #ffffff;
        line-height: 1.15;
    }
    .aiproject-hero__badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        white-space: nowrap;
        padding: 4px 11px;
        border-radius: 999px;
        background: rgba(28, 237, 179, 0.16);
        color: #1cedb3;
        text-transform: uppercase;
    }
    /* Pausiert: gleiche Form wie das AI-Visibility-Badge, nur entfärbt - keine eigene Farbe nötig */
    .aiproject-hero__badge--paused {
        filter: grayscale(1);
        gap: 0;
    }
    .aiproject-hero__badge--paused .v-icon {
        color: inherit;
    }
    .aiproject-hero__badge-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #1cedb3;
    }
    .aiproject-hero__meta {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        font-size: 12.5px;
        color: #9cc7bf;
    }
    .aiproject-hero__meta-dot {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: #5e8c83;
        flex: 0 0 auto;
    }
    .aiproject-hero__meta-chip {
        display: inline-flex;
        align-items: center;
    }
    .aiproject-hero__actions {
        position: relative;
        z-index: 1;
        margin-left: auto;
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 9px;
        flex-wrap: wrap;
    }

    /* Hero buttons */
    .aiproject-hero__btn.v-btn {
        text-transform: none !important;
        letter-spacing: 0 !important;
        font-weight: 600 !important;
        font-size: 13px !important;
        height: 36px !important;
        padding: 0 14px !important;
        margin: 0 !important;
        box-shadow: none !important;
        transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
    }
    .aiproject-hero__btn--ghost.v-btn {
        background: rgba(255, 255, 255, 0.08) !important;
        border: 1px solid rgba(255, 255, 255, 0.18) !important;
        color: #dff3ee !important;
    }
    .aiproject-hero__btn--ghost.v-btn:hover {
        background: rgba(28, 237, 179, 0.14) !important;
        border-color: #1cedb3 !important;
        color: #ffffff !important;
    }
    .aiproject-hero__btn--icon.v-btn {
        width: 36px !important;
        height: 36px !important;
        min-width: 36px !important;
        padding: 0 !important;
        border-radius: 50% !important;
        background: rgba(255, 255, 255, 0.08) !important;
        border: 1px solid rgba(255, 255, 255, 0.18) !important;
        color: #dff3ee !important;
    }
    .aiproject-hero__btn--icon.v-btn:hover {
        background: rgba(28, 237, 179, 0.14) !important;
        border-color: #1cedb3 !important;
        color: #ffffff !important;
    }
    .aiproject-hero__btn--icon.v-btn.v-btn--disabled {
        opacity: 0.45 !important;
        background: rgba(255, 255, 255, 0.04) !important;
        color: rgba(255, 255, 255, 0.4) !important;
    }
    .aiproject-hero__btn--primary.v-btn {
        background: #1cedb3 !important;
        color: #06231d !important;
        box-shadow: 0 2px 12px rgba(28, 237, 179, 0.3) !important;
    }
    .aiproject-hero__btn--primary.v-btn:hover {
        background: #0fd6a0 !important;
        transform: translateY(-1px);
    }

    /* Filter bar */
    .aiproject-hero__bar {
        background: #ffffff;
        display: flex;
        align-items: flex-end;
        gap: 12px;
        padding: 16px 24px;
        flex-wrap: wrap;
        row-gap: 12px;
    }
    .aiproject-hero__ctrl {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
    }
    .aiproject-hero__ctrl-hint {
        position: absolute;
        top: 100%;
        left: 4px;
        margin-top: 3px;
        display: inline-flex;
        align-items: center;
        gap: 3px;
        font-size: 10px;
        font-weight: 600;
        line-height: 1;
        white-space: nowrap;
        cursor: default;
    }
    .aiproject-hero__ctrl-hint {
        position: absolute;
        top: 100%;
        left: 4px;
        margin-top: 3px;
        display: inline-flex;
        align-items: center;
        gap: 3px;
        font-size: 10px;
        font-weight: 600;
        line-height: 1;
        white-space: nowrap;
        cursor: default;
    }
    .aiproject-hero__ctrl--grow {
        flex: 1 1 220px;
        min-width: 220px;
    }
    .aiproject-hero__ctrl-label {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #8fbdb8;
        padding-left: 4px;
    }
    /* Hide floating Vuetify labels inside the bar — external labels handle naming */
    .aiproject-hero__bar .v-text-field .v-label {
        display: none !important;
    }
    .aiproject-hero__bar .v-text-field--outlined fieldset legend {
        width: 0 !important;
    }
    .aiproject-hero__bar .v-text-field--outlined .v-input__slot {
        margin-bottom: 0 !important;
        min-height: 42px !important;
    }
    .aiproject-hero__bar .v-text-field__details {
        display: none !important;
    }
    .aiproject-hero__bar .dashboard-mode-toggle {
        margin-left: 0 !important;
    }

    /* Tabs */
    .aiproject-hero__tabs-wrap {
        background: linear-gradient(180deg, #ffffff, #f2faf8);
        border-top: 1px solid #eaf5f3;
        padding: 10px 12px;
    }
    .aiproject-hero__tabs.v-tabs {
        background: transparent !important;
    }
    .aiproject-hero__tabs .v-slide-group__wrapper {
        background: transparent !important;
    }

    /* Dark theme */
    .theme--dark .aiproject-hero {
        border-color: rgba(255, 255, 255, 0.12);
        background: #14181d;
    }
    .theme--dark .aiproject-hero__bar {
        background: #14181d;
    }
    .theme--dark .aiproject-hero__tabs-wrap {
        background: linear-gradient(180deg, #14181d, #0f1318);
        border-top-color: rgba(255, 255, 255, 0.08);
    }
    .theme--dark .aiproject-hero__ctrl-label {
        color: #6b8a85;
    }

    /* Responsive */
    @media (max-width: 900px) {
        .aiproject-hero__actions {
            margin-left: 0;
            width: 100%;
            justify-content: flex-start;
        }
        .aiproject-hero__title {
            font-size: 19px;
        }
        .aiproject-hero__top,
        .aiproject-hero__bar {
            padding: 18px 16px;
        }
    }
</style>
